import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Lenis from "lenis";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Link, Navigate, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowDownRight,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Bookmark,
  BookmarkCheck,
  Clipboard,
  Download,
  ExternalLink,
  Eye,
  FileText,
  Instagram,
  Mail,
  Menu,
  ShoppingBag,
  CheckCircle2,
  LoaderCircle,
  LogIn,
  LogOut,
  CalendarDays,
  Send,
  Search,
  Share2,
  Truck,
  Plus,
  Minus,
  X,
  Trash2,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { getPrintEdition, PRINT_FILTER, PRINT_SHIPPING } from "./data/printEditions";
import { buildCvDownloadHtml } from "./data/cv";
import { buildPressSheetDownloadHtml } from "./data/pressSheet";
import { EXHIBITIONS } from "./data/exhibitions";
import "@/App.css";
import { SiteMetadata } from "./components/SiteMetadata";
import { ShopMenu } from "./components/ShopMenu";
import { SearchMenu } from "./components/SearchMenu";
import { SearchResultsPage } from "./components/SearchResultsPage";
import { StudioContactLeads } from "./components/StudioContactLeads";

const STUDIO_EMAIL = "love.quetee@gmail.com";
const API = process.env.REACT_APP_BACKEND_URL;
let pageLenis = null;
const OUTREACH_STATUSES = [
  { value: "pending", label: "Follow-up pending" },
  { value: "contacted", label: "Contacted" },
  { value: "complete", label: "Complete" },
];
const DELIVERY_STATUSES = [
  { value: "preparing", label: "Preparing" },
  { value: "in_transit", label: "In transit" },
  { value: "delivered", label: "Delivered" },
  { value: "exception", label: "Exception" },
];
const DELIVERY_STATUS_LABELS = Object.fromEntries(DELIVERY_STATUSES.map((status) => [status.value, status.label]));
const BUYER_EMAIL_PREVIEW_TEMPLATES = [
  { value: "purchase_receipt", label: "Purchase receipt" },
  { value: "tracking_update", label: "Tracking update" },
  { value: "delivered", label: "Delivered email" },
];
const EMAIL_HISTORY_FILTERS = [
  { value: "all", label: "All email events" },
  { value: "shipment_delivery", label: "Delivered" },
  { value: "shipment_update", label: "Tracking" },
  { value: "dispute_acknowledgement", label: "Dispute acknowledgement" },
  { value: "dispute_owner_notification", label: "Owner dispute notification" },
];
function getCarrierTrackingUrl(carrier, trackingNumber) {
  const encoded = encodeURIComponent((trackingNumber || "").trim());
  const carrierKey = (carrier || "").trim().toLowerCase();
  if (!encoded) return "";
  if (carrierKey === "usps") return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encoded}`;
  if (carrierKey === "ups") return `https://www.ups.com/track?tracknum=${encoded}`;
  if (carrierKey === "fedex") return `https://www.fedex.com/fedextrack/?trknbr=${encoded}`;
  if (carrierKey === "dhl") return `https://www.dhl.com/global-en/home/tracking.html?tracking-id=${encoded}`;
  return "";
}
const OUTREACH_NOTE_PHRASES = [
  "Tracking details shared with collector.",
  "Followed up after delivery.",
  "Checking in about installation and care.",
];
const DATE_PRESETS = [
  { value: "month", label: "This month" },
  { value: "last30", label: "Last 30 days" },
];
const PAID_ORDER_COUNTRY_CODES = [
  "AE", "AG", "AL", "AM", "AO", "AR", "AT", "AU", "AZ", "BA", "BD", "BE", "BG", "BH", "BJ", "BN", "BO", "BR", "BS", "BT", "BW", "CA", "CH", "CI", "CL", "CO", "CR", "CY", "CZ", "DE", "DK", "DO", "DZ", "EC", "EE", "EG", "ES", "ET", "FI", "FR", "GA", "GB", "GH", "GI", "GM", "GR", "GT", "GY", "HK", "HR", "HU", "ID", "IE", "IL", "IN", "IS", "IT", "JM", "JO", "JP", "KE", "KH", "KR", "KW", "KZ", "LA", "LC", "LI", "LK", "LT", "LU", "LV", "MA", "MC", "MD", "MG", "MK", "MN", "MO", "MT", "MU", "MX", "MY", "MZ", "NA", "NE", "NG", "NL", "NO", "NZ", "OM", "PA", "PE", "PH", "PK", "PL", "PT", "PY", "QA", "RO", "RS", "RW", "SA", "SE", "SG", "SI", "SK", "SM", "SN", "SV", "TH", "TN", "TR", "TT", "TW", "TZ", "US", "UY", "UZ", "VN", "ZA",
];
const countryDisplayNames = typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function"
  ? new Intl.DisplayNames(["en"], { type: "region" })
  : null;
const PAID_ORDER_COUNTRIES = [
  { value: "", label: "All countries" },
  ...PAID_ORDER_COUNTRY_CODES.map((code) => ({ value: code, label: countryDisplayNames?.of(code) || code })),
];
function formatLocalDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function getDatePresetRange(value) {
  const end = new Date();
  const start = new Date(end);
  if (value === "month") start.setDate(1);
  if (value === "last30") start.setDate(start.getDate() - 29);
  return { from: formatLocalDateInput(start), to: formatLocalDateInput(end) };
}
const PRESS_URL = "https://www.mankatofreepress.com/news/lifestyles/love-in-war-artist-who-found-inspiration-in-iraq-has-gallery-show-at-the-410/article_4aeccd94-8401-11ee-a7ba-e3d77c8ce47f.html";
const CANVAS_REBEL_URL = "https://canvasrebel.com/meet-love-quetee";
const COLOR_THEORY_FLYER_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/toozvkno_Screenshot_20261005_072452_Instagram.jpg";
const INSTAGRAM_URL = "https://www.instagram.com/loves_artpage";
const ARTIST_PORTRAIT_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/jd9vjuvr_Screenshot_20260902_014924_Instagram.jpg";
const PRESS_PORTRAIT_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/qkfahstq_Screenshot_20260708_170736_Gallery.jpg";

const series = ["All Works", "Descendants of the Sun", "Photosynthesis", "Still Life"];

const works = [
  {
    id: "work-01",
    roman: "I",
    title: "Crowns of Moonlit Skin",
    year: "2026",
    medium: "Acrylic on canvas board",
    dimensions: "18 × 24",
    series: "",
    status: "Available",
    note: "A layered portrait archive in blue and violet, holding Black identity, heritage, and community inside newspaper texture.",
    description: `Crowns of Moonlit Skin celebrates the unique crowns we, as people of color, wear, showcasing our beauty regardless of our origins. It emphasizes our shared connections while honoring our individuality, with each crown representing our personal narratives through the diverse hairstyles and garments we wear on our heads.

The term “moonlit skin,” inspired by a quote from the film Moonlight, evokes a sense of depth and luminosity, reminding viewers that our beauty shines in its own light.

In the background, fragments of newspaper articles from the African diaspora and Black America add layers of history and context, highlighting stories that shape our collective experience. This piece invites contemplation on the connections between culture, community, and personal history, celebrating the fragile yet resilient architecture of togetherness.`,
    tone: "tone-rose",
    imageUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/isnw9de7_Screenshot_20260819_235757_Instagram.jpg",
    imageAlt: "Love Quetee acrylic painting of multiple blue and violet Black portraits layered over newspaper clippings",
  },
  {
    id: "work-02",
    roman: "II",
    title: "Dust we shall return",
    year: "2024",
    medium: "Acrylic on canvas",
    dimensions: "20 × 20",
    series: "Photosynthesis",
    status: "Available",
    note: "Two figures settle into a botanical hush, their faces held close to leaves and quiet weather.",
    description: `In Dust we shall return, I reflect on the simple connection we all share. Beneath our different cultures, identities, and stories, I see us as part of the same earth, eventually returning to nature.

I made this piece as a reminder to be present and cherish the people beside us. We are here for a moment, growing together and leaving something beautiful behind. While we are here, we get to live, love, and grow.`,
    tone: "tone-steel",
    imageUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/fd9dllmc_Screenshot_20260819_235915_Instagram.jpg",
    imageAlt: "Love Quetee acrylic painting of two figures embraced among green leaves and berries",
  },
  {
    id: "work-03",
    roman: "III",
    title: "Liminal",
    year: "2025",
    medium: "Oil on canvas",
    dimensions: "18 × 24",
    series: "Photosynthesis",
    status: "Sold",
    note: "A figure meets a butterfly inside a field of leaves, where looking becomes a form of care.",
    description: `Liminal is about the in-between moments in life. It is about the quiet beauty of transformation, reminding us that these moments, while often uncertain, can also be full of hope. The butterfly resting on the figure’s finger represents change and the gentle encouragement that helps us move forward. It speaks to the idea that growth does not always happen alone.

Nature plays an important role in this piece because I do not see it as something separate from us. I believe we are deeply connected to the world around us, and that the land, the plants, and the spaces we move through shape us just as much as we shape them.

The figure is surrounded by nature, showing the path is not always clear. This serves as a reminder that growth rarely follows a straight path. We are always changing, growing, or finding our way, even if we cannot quite see where we are headed. It is often in those in-between spaces that we discover who we are becoming.`,
    tone: "tone-earth",
    imageUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/sa7oqr5w_Screenshot_20260819_235813_Instagram.jpg",
    imageAlt: "Love Quetee oil painting of a figure holding a butterfly among leaves",
  },
  {
    id: "work-04",
    roman: "IV",
    title: "Homegrown",
    year: "2025",
    medium: "Oil on canvas",
    dimensions: "24 × 24",
    series: "Photosynthesis",
    status: "Sold",
    note: "A gathered field of dried flowers, fragments, and hand-built memory, assembled from the inside out.",
    description: `With Homegrown, I reflect on the parts of us that remain connected to where we come from, no matter how far we travel or how much life changes around us. I created this piece as a reminder that home is not only a place, it is something we carry in our roots, memories, culture, and experiences.

As the figure becomes intertwined with the leaves, I think about how we grow from the people and stories that shape us. Homegrown honors those roots while reminding us that we can keep growing wherever we go.`,
    tone: "tone-wine",
    imageUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/0r4cqnfz_Screenshot_20260902_014743_Photos.jpg",
    imageAlt: "Love Quetee painting of a child surrounded by blue-green leaves",
  },
  {
    id: "work-05",
    roman: "V",
    title: "From",
    year: "2026",
    medium: "Acrylic on canvas",
    dimensions: "24 × 24",
    series: "Descendants of the Sun",
    status: "Sold",
    note: "A market still life of avocados, bananas, tomatoes, and limes, held as a study in abundance and origin.",
    description: `From is a dedication to my mother and the community that shaped her, and, in turn, shaped me. Born in Africa as the oldest sibling, she spent her days in the market, buying and selling to support her family. That labor was not just survival; it was participation in a living network of care, exchange, and responsibility. The market was her first community. This painting depicts that community through the image of a woman seated in the market, selling various fruits, embodying both resilience and connection.

When we immigrated to the United States, that sense of community did not disappear; it transformed. She carried those same values into a new environment, finding different ways to ensure that I grew up surrounded by love and stability, sustaining us while quietly rebuilding community in unfamiliar spaces.

My mother’s community is the foundation of my own. Through her, I learned that community is not just where you come from; it is what you carry, what you build, and what you give to others.`,
    tone: "tone-steel",
    imageUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/6kbge38b_Screenshot_20260819_235732_Instagram.jpg",
    imageAlt: "Love Quetee acrylic painting of baskets filled with avocados, bananas, tomatoes, and limes",
  },
  {
    id: "work-06",
    roman: "VI",
    title: "Rooted",
    year: "2026",
    medium: "Oil on canvas",
    dimensions: "26 × 38",
    series: "Descendants of the Sun",
    status: "Unavailable",
    note: "A young figure rests inside citrus foliage, held by the garden, the earth, and the quiet labor of becoming.",
    description: `In Rooted, I reflect on childhood, culture, and the places that shape us. The child beneath the lemon bush, playing with a handmade palm-tree doll, connects me to the abundance of Liberia and to the stories my mother has shared about growing up there.

I did not experience her childhood myself, so this painting is my way of reaching toward the stories I may never fully know. Through heritage, memory, and the land, I find pieces of home that I can carry forward.`,
    tone: "tone-earth",
    imageUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/0y0rqfq4_Screenshot_20260902_014412_Instagram.jpg",
    imageAlt: "Love Quetee oil painting of a young figure seated among citrus trees and fruit",
  },
  {
    id: "work-08",
    roman: "VIII",
    title: "The Giving Tree",
    year: "2026",
    medium: "Acrylic on canvas",
    dimensions: "36 × 36",
    series: "Descendants of the Sun",
    status: "Available",
    note: "",
    description: "",
    tone: "tone-earth",
    imageUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/tqif1pi1_20260920_115038.webp",
    imageAlt: "Love Quetee painting of a seated figure peeling an orange among lush green foliage",
  },
  {
    id: "work-07",
    roman: "VII",
    title: "Gbanganla",
    year: "2026",
    medium: "Oil on canvas",
    dimensions: "30 × 40",
    series: "Descendants of the Sun",
    status: "Sold",
    note: "",
    description: `With Gbanganla, I wanted to make a visual love letter to Liberia, celebrating its culture, land, and the beauty I see in its people. The greenery, traditional garments, jewelry, and the figure’s bold presence reflect the richness of a heritage that continues to shape me.

I admire the way clothing and adornment tell stories without words. Through patterns, colors, fabrics, and handmade details, I honor pride, belonging, and a culture that continues to inspire me, even from afar.`,
    tone: "tone-blue",
    imageUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/4vjglkda_2837.webp",
    imageAlt: "Love Quetee oil painting of a woman in a red headwrap holding white flowers in a lush garden",
    awards: [
      "Weisman Art Museum Award",
      "North Hennepin Community College Award of Excellence in Memory of Lance Kiland",
      "Minnesota Museum of American Art Purchase Award",
    ],
  },
];


const stillLifeWorks = [
  {
    id: "still-life-01",
    roman: "I",
    title: "Untitled",
    year: "2025",
    medium: "Acrylic on canvas",
    dimensions: "18 × 24",
    series: "Still Life",
    status: "Sold",
    note: "A perfume bottle, glass vessels, and jewel-toned blocks settle into a study of reflection and quiet arrangement.",
    tone: "tone-steel",
    imageUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/ywbxnbur_Screenshot_20251130_143447_Instagram.jpg",
    imageAlt: "Love Quetee acrylic still-life painting of a perfume bottle, glass vessels, blue and green blocks, and a pink bowl against dark fabric",
  },
  {
    id: "still-life-02",
    roman: "II",
    title: "Untitled",
    year: "2025",
    medium: "Acrylic on canvas",
    dimensions: "18 × 24",
    series: "Still Life",
    status: "Available",
    note: "A fallen vessel and small red forms held against a dark cloth, quiet and deliberately cinematic.",
    tone: "tone-earth",
    imageUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/r1vl99kx_Screenshot_20251130_143521_Instagram.jpg",
    imageAlt: "Love Quetee still life painting of a glass, a terracotta vessel, and red objects on white cloth",
  },
  {
    id: "still-life-03",
    roman: "III",
    title: "Untitled",
    year: "2025",
    medium: "Acrylic on canvas",
    dimensions: "18 × 24",
    series: "Still Life",
    status: "Available",
    note: "Blue glass, citrus, and folded fabric become a study in reflection, weight, and morning light.",
    tone: "tone-blue",
    imageUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/6t8hu4b1_Screenshot_20251216_002241_Instagram.jpg",
    imageAlt: "Love Quetee still life painting of blue glass bottles, citrus fruit, and folded white cloth",
  },
  {
    id: "still-life-04",
    roman: "IV",
    title: "Confections",
    year: "2025",
    medium: "Acrylic on canvas",
    dimensions: "18 × 24",
    series: "Still Life",
    status: "Sold",
    note: "Perfume, a glass, colored boxes, and a pink dish arranged as an intimate inventory of the everyday.",
    tone: "tone-wine",
    imageUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_dark-canvas-47/artifacts/7bsibts6_Screenshot_20251130_143550_Instagram.jpg",
    imageAlt: "Love Quetee still life painting of perfume, glassware, colored boxes, and a pink dish",
  },
];

const archiveWorks = [...stillLifeWorks, ...works];
const DESCENDANTS_CATALOG_WORK_IDS = ["work-05", "work-06", "work-07", "work-08"];
const descendantsCatalogWorks = DESCENDANTS_CATALOG_WORK_IDS.map((id) => works.find((work) => work.id === id)).filter(Boolean);
const artworkNavigation = [...archiveWorks].reverse();
const globalSearchItems = [
  {
    type: "page",
    group: "Pages",
    label: "Home",
    detail: "Artist studio",
    searchText: "Home Love Quetee fine artist painter studio",
    path: "/",
  },
  {
    type: "page",
    group: "Pages",
    label: "Works archive",
    detail: "Paintings / archive",
    searchText: "Works archive paintings artworks archive",
    path: "/works",
  },
  {
    type: "page",
    group: "Pages",
    label: "About Love Quetee",
    detail: "Artist / practice",
    searchText: "About Love Quetee artist practice biography statement CV press",
    path: "/about",
  },
  {
    type: "page",
    group: "Pages",
    label: "Exhibitions",
    detail: "Current / upcoming / archive",
    searchText: "Exhibitions current upcoming archive gallery",
    path: "/exhibitions",
  },
  {
    type: "page",
    group: "Pages",
    label: "News & Events",
    detail: "Upcoming show / studio news",
    searchText: "News events upcoming show calendar Descendants of the Sun",
    path: "/news-events",
  },
  {
    type: "page",
    group: "Pages",
    label: "Contact",
    detail: "Studio dispatch",
    searchText: "Contact studio dispatch inquiry newsletter email",
    path: "/contact",
  },
  ...archiveWorks.map((work) => ({
    type: "work",
    group: "Works",
    label: work.title,
    detail: [work.series, work.medium].filter(Boolean).join(" / "),
    searchText: [work.title, work.series, work.medium, work.status, work.note].join(" "),
    facets: [work.series, work.medium, work.status],
    path: `/works?search=${encodeURIComponent(work.title)}#search-results`,
  })),
  ...EXHIBITIONS.map((exhibition) => ({
    type: "exhibition",
    group: "Exhibitions",
    label: `${exhibition.title}${exhibition.subtitle ? ` / ${exhibition.subtitle}` : ""}`,
    detail: `${exhibition.date} / ${exhibition.venue}`,
    searchText: [exhibition.title, exhibition.subtitle, exhibition.label, exhibition.date, exhibition.venue, exhibition.status, exhibition.description].filter(Boolean).join(" "),
    path: `/exhibitions/${exhibition.slug}`,
  })),
  {
    type: "print",
    group: "Prints",
    label: "Print editions",
    detail: "Shop / print archive",
    searchText: "Prints print editions shop purchase archive",
    path: "/works#prints",
  },
  ...archiveWorks.filter((work) => getPrintEdition(work.id)).map((work) => ({
    type: "print",
    group: "Prints",
    label: `${work.title} / print edition`,
    detail: [work.series, work.medium].filter(Boolean).join(" / "),
    searchText: [work.title, "print", "prints", "print edition", work.series, work.medium, work.status].join(" "),
    path: `/works#prints-${work.id}`,
  })),
];

const SHORTLIST_STORAGE_KEY = "love_quetee_collector_shortlist_v1";

function readStoredShortlist() {
  try {
    const stored = window.localStorage.getItem(SHORTLIST_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    if (!Array.isArray(parsed)) return [];
    return [...new Set(parsed)].filter((workId) => (
      typeof workId === "string" && archiveWorks.some((work) => work.id === workId)
    ));
  } catch {
    return [];
  }
}

function useCollectorShortlist() {
  const [shortlistIds, setShortlistIds] = useState(readStoredShortlist);

  useEffect(() => {
    window.localStorage.setItem(SHORTLIST_STORAGE_KEY, JSON.stringify(shortlistIds));
  }, [shortlistIds]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === SHORTLIST_STORAGE_KEY) setShortlistIds(readStoredShortlist());
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const toggleItem = (workId) => {
    setShortlistIds((current) => current.includes(workId)
      ? current.filter((id) => id !== workId)
      : [...current, workId]);
  };

  return {
    shortlistIds,
    toggleItem,
    clearAll: () => setShortlistIds([]),
    isShortlisted: (workId) => shortlistIds.includes(workId),
  };
}

function getArtworkShareUrl(workId) {
  const url = new URL("/works", window.location.origin);
  url.searchParams.set("artwork", workId);
  return url.toString();
}

function getExhibitionShareUrl(slug) {
  const url = new URL(`/exhibitions/${slug}`, window.location.origin);
  url.searchParams.set("source", "share");
  return url.toString();
}

async function copyToClipboard(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const input = document.createElement("textarea");
  input.value = value;
  input.setAttribute("readonly", "true");
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  const copied = document.execCommand("copy");
  input.remove();
  if (!copied) throw new Error("Copy is unavailable");
}

function slugify(value) {
  return value.toLowerCase().replace(/\s+/g, "-");
}

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
}

function Reveal({ children, className = "", ...props }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} {...props} className={`reveal ${visible ? "is-visible" : ""} ${className}`}>
      {children}
    </div>
  );
}

function Header({ mobileOpen, setMobileOpen, onCatalogue, cartCount, onCartOpen, shortlistCount, onShortlistOpen, searchItems }) {
  const navItems = [
    ["Home", "/", "nav-link-home"],
    ["Works", "/works", "nav-link-works"],
    ["About", "/about", "nav-link-about"],
    ["Exhibitions", "/exhibitions", "nav-link-exhibitions"],
    ["News/Events", "/news-events", "nav-link-news-events"],
    ["Contact", "/contact", "nav-link-contact"],
  ];

  const closeMenu = () => setMobileOpen(false);

  return (
    <header className="site-header">
      <div className="site-header-inner">
        <NavLink className="brand-lockup" to="/" end data-testid="brand-link">
          <span className="brand-mark">LQ</span>
          <span className="brand-name">Love Quetee</span>
        </NavLink>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {navItems.map(([label, target, testId]) => (
            <NavLink key={target} to={target} end className={({ isActive }) => isActive ? "is-active" : ""} data-testid={testId}>
              {label}
            </NavLink>
          ))}
          <SearchMenu searchItems={searchItems} />
          <ShopMenu />
        </nav>
        <div className="header-actions">
          <button className="header-cart" type="button" onClick={onCartOpen} data-testid="header-cart-button">
            <ShoppingBag size={14} strokeWidth={1.5} />
            <span>Shopping cart</span>
            <span className="header-cart-count" data-testid="header-cart-count">({cartCount})</span>
          </button>
          <button className="header-shortlist" type="button" onClick={onShortlistOpen} aria-label={`Open collector shortlist (${shortlistCount} ${shortlistCount === 1 ? "work" : "works"})`} data-testid="header-shortlist-button">
            {shortlistCount ? <BookmarkCheck size={14} strokeWidth={1.5} /> : <Bookmark size={14} strokeWidth={1.5} />}
            <span>Shortlist</span>
            <span className="header-shortlist-count" data-testid="header-shortlist-count">({shortlistCount})</span>
          </button>
          <button
            className="header-catalogue"
            type="button"
            onClick={onCatalogue}
            data-testid="nav-catalogue-button"
          >
            <BookOpen size={14} strokeWidth={1.5} />
            Catalogue
          </button>
          <button
            className="mobile-cart-toggle"
            type="button"
            aria-label={`Open shopping cart (${cartCount} ${cartCount === 1 ? "item" : "items"})`}
            onClick={() => {
              closeMenu();
              onCartOpen();
            }}
            data-testid="mobile-cart-icon-button"
          >
            <ShoppingBag size={18} strokeWidth={1.4} />
            <span className="mobile-cart-icon-count" data-testid="mobile-cart-icon-count">{cartCount}</span>
          </button>
          <button
            className="menu-toggle"
            type="button"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((current) => !current)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <nav className="mobile-nav" aria-label="Mobile navigation">
          <SearchMenu mobile searchItems={searchItems} onNavigate={closeMenu} />
          <ShopMenu mobile onNavigate={closeMenu} />
          {navItems.map(([label, target]) => (
            <NavLink
              key={target}
              to={target}
              end
              onClick={closeMenu}
              data-testid={`mobile-nav-link-${target === "/" ? "home" : target.slice(1).replaceAll("/", "-")}`}
            >
              <span>{label}</span>
              <ArrowUpRight size={16} strokeWidth={1.4} />
            </NavLink>
          ))}
          <button
            type="button"
            className="mobile-catalogue"
            onClick={() => {
              closeMenu();
              onCatalogue();
            }}
            data-testid="mobile-nav-catalogue-button"
          >
            Request catalogue <BookOpen size={15} strokeWidth={1.4} />
          </button>
        </nav>
      )}
    </header>
  );
}

function Hero({ onCatalogue }) {
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const featuredWork = works.find((work) => work.id === "work-07");

  const handlePointerMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 22;
    const y = ((event.clientY - bounds.top) / bounds.height - 0.5) * 18;
    setParallax({ x, y });
  };

  return (
    <section
      id="home"
      className="hero-section"
      data-testid="home-section"
      onPointerMove={handlePointerMove}
      onPointerLeave={() => setParallax({ x: 0, y: 0 })}
    >
      <div className="hero-inner">
        <div
          className="hero-backdrop"
          style={{ transform: `translate3d(${parallax.x * 0.3}px, ${parallax.y * 0.3}px, 0)` }}
          aria-hidden="true"
        />
        <div className="hero-copy">
          <div className="eyebrow hero-eyebrow">
            <span className="eyebrow-line" /> Fine artist / contemporary painter
          </div>
          <h1 className="hero-title" data-testid="hero-title" aria-label="Love Quetee">
            <span className="hero-line"><span>Love</span></span>
            <span className="hero-line hero-line-italic"><span>Quetee</span></span>
          </h1>
          <div className="hero-underbar" aria-hidden="true" />
          <Link className="hero-art-rail" to="/works" aria-label="Open the full artwork archive" data-testid="hero-featured-artwork">
            <div className="hero-canvas-shell" data-testid="hero-artwork-placeholder">
              <div
                className="hero-canvas-art hero-canvas-art-image"
                style={{ transform: `translate3d(${parallax.x}px, ${parallax.y}px, 0)` }}
              >
                <img
                  className="hero-art-image"
                  src={featuredWork.imageUrl}
                  alt={featuredWork.imageAlt}
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  data-testid="hero-artwork-image"
                />
              </div>
            </div>
          </Link>
          <Link className="latest-update" to="/exhibitions/descendants-of-the-sun" aria-label="View Descendants of the Sun exhibition information" data-testid="home-upcoming-exhibition">
            <div className="latest-update-label">Upcoming exhibition</div>
            <div className="latest-update-copy">
              <span>Descendants of the Sun</span>
              <span className="latest-update-plain">November 7, 2026 / African Museum, Arts and Culture Center / Saint Paul, Minnesota.</span>
            </div>
          </Link>
          <div className="hero-news-events-action">
            <Link className="button button-text" to="/news-events" data-testid="home-news-events-link">News/Events <ArrowRight size={16} strokeWidth={1.4} /></Link>
          </div>
          <div className="hero-actions">
            <button className="button button-text" type="button" onClick={onCatalogue} data-testid="hero-catalogue-cta">
              <BookOpen size={16} strokeWidth={1.5} /> Request catalogue
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ArtworkCard({ work, onOpen, onInquire, isShortlisted, onToggleShortlist, onShare }) {
  const [spotlight, setSpotlight] = useState({ x: "50%", y: "50%" });

  const handleMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    setSpotlight({
      x: `${((event.clientX - bounds.left) / bounds.width) * 100}%`,
      y: `${((event.clientY - bounds.top) / bounds.height) * 100}%`,
    });
  };

  return (
    <article className="artwork-card" data-testid={`artwork-card-${work.id}`}>
      <div
        className="artwork-frame"
        style={{ "--spot-x": spotlight.x, "--spot-y": spotlight.y }}
        onPointerMove={handleMove}
        onPointerLeave={() => setSpotlight({ x: "50%", y: "50%" })}
        onClick={() => onOpen(work)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpen(work);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Open details for ${work.title}`}
        data-testid={`artwork-frame-${work.id}`}
      >
        <div className={`artwork-placeholder ${work.tone} ${work.imageUrl ? "has-image" : ""}`}>
          {work.imageUrl && (
            <img
              className="artwork-image"
              src={work.imageUrl}
              alt={work.imageAlt || `${work.title} by Love Quetee`}
              loading="lazy"
              data-testid={`artwork-image-${work.id}`}
            />
          )}
          <div className="artwork-image-wash" aria-hidden="true" />
          <div className="placeholder-topline">
            {work.accession && <span>{work.accession}</span>}
            <span>{work.year}</span>
          </div>
          {!work.imageUrl && <span className="placeholder-message">Artwork image<br />to be added</span>}
          {!work.imageUrl && <span className="placeholder-stamp">Archival placeholder</span>}
          <button
            type="button"
            className="placeholder-open"
            onClick={(event) => {
              event.stopPropagation();
              onOpen(work);
            }}
            data-testid={`artwork-detail-button-${work.id}`}
          >
            Open details <ArrowUpRight size={15} strokeWidth={1.4} />
          </button>
          {!work.imageUrl && (
            <div className="placeholder-bottomline">
              <span>Oil / linen study</span>
            </div>
          )}
        </div>
      </div>
      <div className="artwork-meta">
        {work.series && (
          <div className="artwork-heading-row">
            <p className="artwork-series">{work.series}</p>
          </div>
        )}
        <h3>{work.title}</h3>
        <p className="artwork-medium">{work.medium}</p>
        <div className="artwork-detail-row">
          <span>{work.dimensions}</span>
          <span data-testid={`work-status-${work.id}`}>{work.status}</span>
        </div>
        <div className="artwork-card-actions">
          <button
            className={`artwork-shortlist-toggle${isShortlisted ? " is-active" : ""}`}
            type="button"
            aria-pressed={isShortlisted}
            aria-label={`${isShortlisted ? "Remove" : "Add"} ${work.title} ${isShortlisted ? "from" : "to"} collector shortlist`}
            onClick={() => onToggleShortlist(work)}
            data-testid={`artwork-shortlist-toggle-${work.id}`}
          >
            {isShortlisted ? <BookmarkCheck size={14} strokeWidth={1.4} /> : <Bookmark size={14} strokeWidth={1.4} />}
            {isShortlisted ? "Shortlisted" : "Shortlist"}
          </button>
          <button className="artwork-share-button" type="button" onClick={() => onShare(work)} aria-label={`Share ${work.title}`} data-testid={`artwork-share-button-${work.id}`}>
            Share <ArrowUpRight size={14} strokeWidth={1.3} />
          </button>
        </div>
        <button
          className="artwork-inquire"
          type="button"
          onClick={() => onInquire(work)}
          data-testid={`artwork-inquire-button-${work.id}`}
        >
          Inquire for acquisition <ArrowRight size={15} strokeWidth={1.3} />
        </button>
      </div>
    </article>
  );
}

function PrintEditionCard({ work, onAddToCart, cartQuantity, isShortlisted, onToggleShortlist, onShare }) {
  const printEdition = getPrintEdition(work.id);

  return (
    <article id={`prints-${work.id}`} className="print-edition-card" data-testid={`print-edition-card-${work.id}`}>
      <div className={`print-edition-image ${work.tone}`}>
        <img
          src={work.imageUrl}
          alt={`${work.title} archival print preview by Love Quetee`}
          loading="lazy"
          data-testid={`print-edition-image-${work.id}`}
        />
      </div>
      <div className="print-edition-copy">
        <div className="print-edition-topline">
          {work.series && <span>{work.series}</span>}
          <span>{work.year}</span>
        </div>
        <h3>{work.title}</h3>
        <div className="print-edition-specs">
          <div>
            <span>Format</span>
            <strong data-testid={`print-edition-dimensions-${work.id}`}>{printEdition.dimensions}</strong>
          </div>
          <div>
            <span>Price</span>
            <strong data-testid={`print-edition-price-${work.id}`}>{printEdition.priceLabel}</strong>
          </div>
        </div>
        <div className="artwork-card-actions print-edition-actions">
          <button
            className={`artwork-shortlist-toggle${isShortlisted ? " is-active" : ""}`}
            type="button"
            aria-pressed={isShortlisted}
            aria-label={`${isShortlisted ? "Remove" : "Add"} ${work.title} ${isShortlisted ? "from" : "to"} collector shortlist`}
            onClick={() => onToggleShortlist(work)}
            data-testid={`artwork-shortlist-toggle-${work.id}`}
          >
            {isShortlisted ? <BookmarkCheck size={14} strokeWidth={1.4} /> : <Bookmark size={14} strokeWidth={1.4} />}
            {isShortlisted ? "Shortlisted" : "Shortlist"}
          </button>
          <button className="artwork-share-button" type="button" onClick={() => onShare(work)} aria-label={`Share ${work.title}`} data-testid={`print-artwork-share-button-${work.id}`}>
            Share <ArrowUpRight size={14} strokeWidth={1.3} />
          </button>
        </div>
        <button
          className="button button-primary print-edition-acquire"
          type="button"
          onClick={() => onAddToCart(work)}
          aria-label={`Add ${work.title} print to cart`}
          data-testid={`print-edition-add-button-${work.id}`}
        >
          {cartQuantity ? `In cart (${cartQuantity}) · Add another` : "Add print to cart"} <ShoppingBag size={15} strokeWidth={1.3} />
        </button>
      </div>
    </article>
  );
}

const CART_STORAGE_KEY = "love_quetee_print_cart_v1";
const MAX_CART_QUANTITY = 50;

const formatUSD = (amount) => `$${amount.toFixed(2)} USD`;

const readStoredCart = () => {
  try {
    const stored = window.localStorage.getItem(CART_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && typeof item.workId === "string" && getPrintEdition(item.workId) && Number.isInteger(item.quantity) && item.quantity > 0)
      .map((item) => ({ workId: item.workId, quantity: Math.min(item.quantity, MAX_CART_QUANTITY) }));
  } catch {
    return [];
  }
};

function usePrintCart() {
  const [cartItems, setCartItems] = useState(readStoredCart);

  useEffect(() => {
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === CART_STORAGE_KEY) setCartItems(readStoredCart());
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const addItem = (workId) => {
    setCartItems((current) => {
      const existing = current.find((item) => item.workId === workId);
      if (existing) {
        return current.map((item) => item.workId === workId
          ? { ...item, quantity: Math.min(item.quantity + 1, MAX_CART_QUANTITY) }
          : item);
      }
      return [...current, { workId, quantity: 1 }];
    });
  };

  const updateQuantity = (workId, quantity) => {
    setCartItems((current) => current.map((item) => item.workId === workId
      ? { ...item, quantity: Math.max(1, Math.min(quantity, MAX_CART_QUANTITY)) }
      : item));
  };

  const removeItem = (workId) => setCartItems((current) => current.filter((item) => item.workId !== workId));
  const clearCart = () => setCartItems([]);
  const quantityFor = (workId) => cartItems.find((item) => item.workId === workId)?.quantity || 0;

  return { cartItems, addItem, updateQuantity, removeItem, clearCart, quantityFor };
}

function CartQuantityControls({ line, prefix, onDecrease, onIncrease }) {
  return (
    <div className="cart-quantity-controls">
      <button
        className="cart-quantity-button"
        type="button"
        onClick={onDecrease}
        disabled={line.quantity <= 1}
        aria-label={`Decrease ${line.work.title} quantity`}
        data-testid={`${prefix}-quantity-decrease-${line.work.id}`}
      >
        <Minus size={12} strokeWidth={1.4} />
      </button>
      <span data-testid={`${prefix}-quantity-${line.work.id}`}>{line.quantity}</span>
      <button
        className="cart-quantity-button"
        type="button"
        onClick={onIncrease}
        disabled={line.quantity >= MAX_CART_QUANTITY}
        aria-label={`Increase ${line.work.title} quantity`}
        data-testid={`${prefix}-quantity-increase-${line.work.id}`}
      >
        <Plus size={12} strokeWidth={1.4} />
      </button>
    </div>
  );
}

function CartItemRow({ line, cart }) {
  const prefix = "cart-item";
  return (
    <article className="cart-item" data-testid={`${prefix}-${line.work.id}`}>
      <img src={line.work.imageUrl} alt={`${line.work.title} print`} data-testid={`${prefix}-image-${line.work.id}`} />
      <div className="cart-item-copy">
        <div className="cart-item-heading">
          <h3 data-testid={`${prefix}-title-${line.work.id}`}>{line.work.title}</h3>
          <button className="cart-item-remove" type="button" onClick={() => cart.removeItem(line.work.id)} data-testid={`${prefix}-remove-${line.work.id}`}>
            Remove <X size={13} strokeWidth={1.3} />
          </button>
        </div>
        <p data-testid={`${prefix}-dimensions-${line.work.id}`}>{line.edition.dimensions} / {formatUSD(line.edition.price)} each</p>
        <div className="cart-item-footer">
          <CartQuantityControls
            line={line}
            prefix={prefix}
            onDecrease={() => cart.updateQuantity(line.work.id, line.quantity - 1)}
            onIncrease={() => cart.updateQuantity(line.work.id, line.quantity + 1)}
          />
          <strong data-testid={`${prefix}-line-total-${line.work.id}`}>{formatUSD(line.lineTotal)}</strong>
        </div>
      </div>
    </article>
  );
}

function CartLedger({ cart, prefix }) {
  const shipping = cart.lines.length ? PRINT_SHIPPING : 0;
  const total = cart.subtotal + shipping;
  return (
    <div className="cart-ledger">
      <div><span>Subtotal</span><strong data-testid={`${prefix}-subtotal`}>{formatUSD(cart.subtotal)}</strong></div>
      <div><span>Shipping</span><strong data-testid={`${prefix}-shipping`}>{shipping ? `+$${PRINT_SHIPPING.toFixed(2)} USD` : "$0.00 USD"}</strong></div>
      <div className="cart-ledger-total"><span>Total</span><strong data-testid={`${prefix}-total`}>{formatUSD(total)}</strong></div>
    </div>
  );
}

function CartSummaryPanel({ cart }) {
  const cartPrefix = "print-editions-cart";
  return (
    <section className="print-editions-cart-panel" data-testid="print-editions-cart-panel">
      <div className="print-cart-panel-heading">
        <div>
          <p className="section-kicker">Shopping cart / live order</p>
          <h3>Your print selections.</h3>
        </div>
        <span data-testid="print-editions-cart-count">{cart.count} {cart.count === 1 ? "print" : "prints"}</span>
      </div>
      {cart.lines.length ? (
        <div className="print-cart-panel-items" data-testid="print-editions-cart-items-list">
          {cart.lines.map((line) => (
            <div className="print-cart-panel-item" key={line.work.id} data-testid={`${cartPrefix}-item-${line.work.id}`}>
              <div>
                <strong>{line.work.title}</strong>
                <span>{line.edition.dimensions} / {formatUSD(line.edition.price)}</span>
              </div>
              <CartQuantityControls
                line={line}
                prefix={`${cartPrefix}-item`}
                onDecrease={() => cart.updateQuantity(line.work.id, line.quantity - 1)}
                onIncrease={() => cart.updateQuantity(line.work.id, line.quantity + 1)}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="print-cart-panel-empty" data-testid="print-editions-cart-empty-state">
          <p>Your cart is empty.</p>
          <span>Select a print below to begin your collection.</span>
          <button className="button button-text" type="button" onClick={cart.onBrowsePrints} data-testid="print-editions-cart-browse-button">
            Browse print editions <ArrowRight size={15} strokeWidth={1.3} />
          </button>
        </div>
      )}
      <CartLedger cart={cart} prefix={cartPrefix} />
      <div className="print-cart-panel-actions">
        <button className="button button-text" type="button" onClick={cart.onCartOpen} data-testid="print-editions-cart-open-drawer-button">
          View full cart <ArrowRight size={15} strokeWidth={1.3} />
        </button>
        <button
          className="button button-primary"
          type="button"
          onClick={cart.onCheckout}
          disabled={!cart.lines.length || cart.checkoutLoading}
          aria-busy={cart.checkoutLoading}
          data-testid="print-editions-cart-checkout-button"
        >
          {cart.checkoutLoading ? "Connecting to Stripe..." : "Proceed to checkout"} <ArrowUpRight size={16} strokeWidth={1.4} />
        </button>
      </div>
    </section>
  );
}

function CartDrawer({ cart, onClose }) {
  return (
    <ModalShell testId="cart-drawer" onClose={onClose} drawer labelledBy="cart-drawer-title">
      <div className="modal-topline">
        <span id="cart-drawer-title">Shopping cart / {cart.count} {cart.count === 1 ? "print" : "prints"}</span>
        <button className="modal-close" type="button" onClick={onClose} data-testid="cart-drawer-close-button">
          Close <X size={17} strokeWidth={1.3} />
        </button>
      </div>
      {cart.lines.length ? (
        <div className="cart-drawer-content">
          <div className="cart-items-list" data-testid="cart-items-list">
            {cart.lines.map((line) => <CartItemRow key={line.work.id} line={line} cart={cart} />)}
          </div>
          <CartLedger cart={cart} prefix="cart" />
          <div className="cart-drawer-actions">
            <button className="button button-text" type="button" onClick={cart.clearCart} data-testid="cart-clear-button">Clear cart</button>
            <button
              className="button button-primary"
              type="button"
              onClick={cart.onCheckout}
              disabled={cart.checkoutLoading}
              aria-busy={cart.checkoutLoading}
              data-testid="cart-checkout-button"
            >
              {cart.checkoutLoading ? <><LoaderCircle className="checkout-spinner" size={15} strokeWidth={1.3} /><span data-testid="cart-checkout-loading">Connecting to Stripe...</span></> : <>Proceed to secure checkout <ArrowUpRight size={16} strokeWidth={1.4} /></>}
            </button>
          </div>
        </div>
      ) : (
        <div className="cart-empty-state" data-testid="cart-empty-state">
          <ShoppingBag size={32} strokeWidth={1} />
          <h2>Your shopping cart is empty.</h2>
          <p>Select print editions from the studio release below.</p>
          <Link className="button button-primary" to="/works" onClick={onClose} data-testid="cart-empty-browse-button"><span>Browse Print Editions</span> <ArrowRight size={16} strokeWidth={1.4} /></Link>
        </div>
      )}
    </ModalShell>
  );
}

function CollectorShortlistDrawer({ shortlistWorks, onClose, onOpen, onRemove, onClear, onInquire }) {
  const openWork = (work) => {
    onClose();
    onOpen(work);
  };

  return (
    <ModalShell testId="collector-shortlist-drawer" onClose={onClose} drawer surfaceClassName="shortlist-drawer-surface" labelledBy="collector-shortlist-title">
      <div className="modal-topline">
        <span id="collector-shortlist-title">Collector shortlist</span>
        <button className="modal-close" type="button" onClick={onClose} data-testid="collector-shortlist-close-button">
          Close <X size={17} strokeWidth={1.3} />
        </button>
      </div>
      {shortlistWorks.length ? (
        <>
          <div className="shortlist-drawer-intro">
            <h2>Saved for<br /><em>another looking.</em></h2>
          </div>
          <div className="collector-shortlist-list" data-testid="collector-shortlist-list">
            {shortlistWorks.map((work) => (
              <article className="collector-shortlist-item" key={work.id} data-testid={`collector-shortlist-item-${work.id}`}>
                <button className="collector-shortlist-item-image-button" type="button" onClick={() => openWork(work)} aria-label={`Open details for ${work.title}`} data-testid={`collector-shortlist-item-image-button-${work.id}`}>
                  <img src={work.imageUrl} alt={work.imageAlt || `${work.title} by Love Quetee`} data-testid={`collector-shortlist-item-image-${work.id}`} />
                </button>
                <div className="collector-shortlist-item-copy">
                  <h3 data-testid={`collector-shortlist-item-title-${work.id}`}>{work.title}</h3>
                  <p>{work.year} / {work.medium}</p>
                  <span>{work.dimensions} / {work.status}</span>
                  <button className="collector-shortlist-open" type="button" onClick={() => openWork(work)} data-testid={`collector-shortlist-item-open-modal-${work.id}`}>
                    Open details <ArrowRight size={14} strokeWidth={1.3} />
                  </button>
                </div>
                <button className="collector-shortlist-remove" type="button" onClick={() => onRemove(work)} aria-label={`Remove ${work.title} from collector shortlist`} data-testid={`collector-shortlist-item-remove-${work.id}`}>
                  <Trash2 size={15} strokeWidth={1.3} />
                </button>
              </article>
            ))}
          </div>
          <div className="collector-shortlist-actions">
            <button className="button button-text" type="button" onClick={() => onInquire(shortlistWorks)} data-testid="collector-shortlist-inquire-button">
              Inquire about selections <ArrowUpRight size={15} strokeWidth={1.3} />
            </button>
            <button className="button button-text" type="button" onClick={onClear} data-testid="collector-shortlist-clear-button">
              Clear shortlist <X size={14} strokeWidth={1.3} />
            </button>
          </div>
        </>
      ) : (
        <div className="collector-shortlist-empty" data-testid="collector-shortlist-empty-state">
          <Bookmark size={28} strokeWidth={1.1} />
          <h2>Your collector shortlist is empty.</h2>
          <p>Tap Shortlist on any painting to save pieces for contemplation.</p>
          <Link className="button button-primary" to="/works" onClick={onClose} data-testid="collector-shortlist-empty-browse-button">
            Browse selected paintings <ArrowRight size={15} strokeWidth={1.3} />
          </Link>
        </div>
      )}
    </ModalShell>
  );
}


function CollectorShortlistBar({ shortlistWorks, onOpenShortlist, onOpen }) {
  return (
    <section className="works-shortlist-bar" role="region" aria-label="Collector shortlist" data-testid="works-shortlist-bar">
      <div className="works-shortlist-copy">
        <p className="section-kicker">Collector shortlist</p>
        <strong data-testid="works-shortlist-count">{shortlistWorks.length} {shortlistWorks.length === 1 ? "work" : "works"} saved</strong>
      </div>
      {shortlistWorks.length ? (
        <div className="works-shortlist-previews">
          {shortlistWorks.slice(0, 4).map((work) => (
            <button type="button" key={work.id} onClick={() => onOpen(work)} aria-label={`Open ${work.title} from collector shortlist`} data-testid={`works-shortlist-preview-${work.id}`}>
              <img src={work.imageUrl} alt="" />
            </button>
          ))}
          {shortlistWorks.length > 4 && <span data-testid="works-shortlist-more">+{shortlistWorks.length - 4} more</span>}
        </div>
      ) : (
        <p className="works-shortlist-empty" data-testid="works-shortlist-empty">Save paintings here as you move through the archive.</p>
      )}
      <button className="works-shortlist-open" type="button" onClick={onOpenShortlist} data-testid="works-shortlist-open-button">
        {shortlistWorks.length ? "View shortlist" : "Open shortlist"} <ArrowRight size={15} strokeWidth={1.3} />
      </button>
    </section>
  );
}

function Works({ onOpen, onInquire, cart, onAddToCart, shortlistWorks, onOpenShortlist, onToggleShortlist, onShare, isShortlisted }) {
  const { hash, search } = useLocation();
  const printHashTarget = hash.startsWith("#prints") ? hash.slice(1) : "";
  const searchQuery = new URLSearchParams(search).get("search")?.trim() || "";
  const normalizedSearchQuery = searchQuery.toLowerCase();
  const [activeSeries, setActiveSeries] = useState(() => printHashTarget ? PRINT_FILTER : "All Works");
  const filteredWorks = useMemo(() => {
    const seriesWorks = activeSeries === "All Works"
      ? archiveWorks
      : activeSeries === PRINT_FILTER
        ? works.filter((work) => getPrintEdition(work.id))
        : archiveWorks.filter((work) => work.series === activeSeries);
    if (!normalizedSearchQuery) return seriesWorks;
    return seriesWorks.filter((work) => [work.title, work.series, work.medium, work.status].some((value) => value.toLowerCase().includes(normalizedSearchQuery)));
  }, [activeSeries, normalizedSearchQuery]);
  const visibleWorks = filteredWorks;
  const galleryWorks = ["All Works", PRINT_FILTER, "Descendants of the Sun", "Photosynthesis"].includes(activeSeries)
    ? [...visibleWorks].reverse()
    : visibleWorks;
  const printView = activeSeries === PRINT_FILTER;

  useEffect(() => {
    if (!printHashTarget) return undefined;
    setActiveSeries(PRINT_FILTER);
    let secondFrame;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        document.getElementById(printHashTarget)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [printHashTarget]);

  useEffect(() => {
    if (!normalizedSearchQuery) return undefined;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById("search-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [normalizedSearchQuery]);

  return (
    <section id="works" className="works-section" data-testid="works-section">
      <div className="section-shell">
        <Reveal className="section-heading works-heading">
          <div>
            <h2>The works<br /><em>on view.</em></h2>
          </div>
        </Reveal>
        <CollectorShortlistBar shortlistWorks={shortlistWorks} onOpenShortlist={onOpenShortlist} onOpen={onOpen} />
        <Reveal id="search-results" className="works-controls">
          <div className="filter-label">Filter the archive</div>
          {searchQuery && <span className="works-search-query" data-testid="works-search-query">Search / “{searchQuery}”</span>}
          <div className="series-filters" role="group" aria-label="Filter paintings and print editions by series">
            {["All Works", PRINT_FILTER, ...series.slice(1)].map((item) => (
              <button
                key={item}
                className={`filter-button ${activeSeries === item ? "is-active" : ""}`}
                type="button"
                aria-pressed={activeSeries === item}
                onClick={() => setActiveSeries(item)}
                data-testid={`filter-series-${slugify(item)}`}
              >
                {item}
              </button>
            ))}
          </div>
          <span className="works-count" data-testid="works-count">{String(visibleWorks.length).padStart(2, "0")} {printView ? "print editions" : "artworks"}</span>
        </Reveal>
        {printView && (
          <Reveal id="prints" className="print-editions-banner" data-testid="print-editions-banner">
            <div>
              <h3 data-testid="print-editions-heading">Bring a piece of the archive home.</h3>
            </div>
          </Reveal>
        )}
        {printView && <CartSummaryPanel cart={cart} />}
        {!visibleWorks.length && (
          <div className="works-search-empty" data-testid="works-search-empty">
            <p>{searchQuery ? `No works matched “${searchQuery}”.` : "No works are available in this view yet."}</p>
            {searchQuery && <Link className="button button-text" to="/works" data-testid="works-search-clear">Clear search <ArrowRight size={15} strokeWidth={1.3} /></Link>}
          </div>
        )}
        {visibleWorks.length > 0 && (printView ? (
          <div className="print-editions-grid" data-testid="print-editions-grid">
            {galleryWorks.map((work) => (
              <Reveal key={work.id} className="print-edition-reveal">
                <PrintEditionCard work={work} onAddToCart={onAddToCart} cartQuantity={cart.quantityFor(work.id)} isShortlisted={isShortlisted(work.id)} onToggleShortlist={onToggleShortlist} onShare={onShare} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="works-grid">
            {galleryWorks.map((work, index) => (
              <Reveal key={work.id} className={`work-reveal work-reveal-${index + 1}`}>
                <ArtworkCard work={work} onOpen={onOpen} onInquire={onInquire} isShortlisted={isShortlisted(work.id)} onToggleShortlist={onToggleShortlist} onShare={onShare} />
              </Reveal>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function HomeArchiveCallout() {
  return (
    <section className="home-archive-callout" data-testid="home-archive-callout">
      <div className="section-shell">
        <div className="home-archive-callout-copy">
          <p className="section-kicker">Artworks</p>
          <h2>Enter the<br /><em>archive.</em></h2>
          <div className="home-archive-actions">
            <Link className="button button-primary" to="/works" data-testid="home-archive-callout-link">View the full archive <ArrowRight size={16} strokeWidth={1.4} /></Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function HomeAboutPreview() {
  return (
    <section className="home-about-preview" data-testid="home-about-preview">
      <div className="section-shell">
        <div className="home-preview-copy">
          <p className="section-kicker">Practice / and process</p>
          <h2>Where memory<br /><em>takes root.</em></h2>
          <Link className="button button-text" to="/about" data-testid="home-about-view-all">Read about the practice <ArrowRight size={15} strokeWidth={1.4} /></Link>
        </div>
      </div>
    </section>
  );
}

function HomeContactPreview() {
  return (
    <section className="home-contact-preview" data-testid="home-contact-preview">
      <div className="section-shell">
        <div className="home-preview-copy">
          <p className="section-kicker">Collector dispatch / contact</p>
          <h2>Keep a place<br /><em>for the work.</em></h2>
          <Link className="button button-primary" to="/contact" data-testid="home-contact-view-all">Start a conversation <ArrowUpRight size={15} strokeWidth={1.4} /></Link>
        </div>
      </div>
    </section>
  );
}

function HomePage({ onCatalogue }) {
  return (
    <>
      <Hero onCatalogue={onCatalogue} />
      <HomeArchiveCallout />
      <HomeAboutPreview />
      <HomeContactPreview />
    </>
  );
}

async function createCvPdfDownload() {
  const parsed = new DOMParser().parseFromString(buildCvDownloadHtml(PRESS_URL, CANVAS_REBEL_URL), "text/html");
  const wrapper = document.createElement("div");
  const styles = document.createElement("style");
  styles.textContent = [...parsed.head.querySelectorAll("style")].map((style) => style.textContent).join("\n");
  wrapper.append(styles);
  wrapper.insertAdjacentHTML("beforeend", parsed.body.innerHTML);
  Object.assign(wrapper.style, {
    position: "absolute",
    top: "0",
    left: "0",
    display: "block",
    width: "816px",
    minHeight: "1px",
    background: "#fff",
    zIndex: "100000",
    pointerEvents: "none",
  });
  document.body.appendChild(wrapper);
  try {
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const wrapperTop = wrapper.getBoundingClientRect().top;
    const contentHeight = Math.ceil(Math.max(wrapper.scrollHeight, wrapper.getBoundingClientRect().height));
    if (!contentHeight) throw new Error("CV content has no renderable height");
    wrapper.style.height = `${contentHeight}px`;

    const scale = 2;
    const dpi = 96;
    const pageMarginTop = 0.45;
    const pageMarginRight = 0.55;
    const pageMarginBottom = 0.45;
    const pageWidthIn = 8.5;
    const pageHeightIn = 11;
    const contentWidthIn = pageWidthIn - (pageMarginRight * 2);
    const printablePageHeightCss = (pageHeightIn - pageMarginTop - pageMarginBottom) * dpi;
    const entries = [...wrapper.querySelectorAll(".cv-entry, .cv-section h2")]
      .map((element) => {
        const rect = element.getBoundingClientRect();
        return { top: rect.top - wrapperTop, bottom: rect.bottom - wrapperTop };
      })
      .filter(({ top, bottom }) => bottom > top);
    const pageBreaks = [0];
    let startCss = 0;
    while (startCss < contentHeight) {
      const targetCss = startCss + printablePageHeightCss;
      if (targetCss >= contentHeight) {
        pageBreaks.push(contentHeight);
        break;
      }
      const crossingEntry = entries.find(({ top, bottom }) => top > startCss + 1 && top < targetCss && bottom > targetCss);
      const nextBreakCss = crossingEntry ? crossingEntry.top : targetCss;
      if (nextBreakCss <= startCss + 1) {
        pageBreaks.push(targetCss);
        startCss = targetCss;
      } else {
        pageBreaks.push(nextBreakCss);
        startCss = nextBreakCss;
      }
    }

    const fullCanvas = await html2canvas(wrapper, {
      scale,
      backgroundColor: "#ffffff",
      useCORS: true,
      width: 816,
      height: contentHeight,
      windowWidth: 816,
      windowHeight: contentHeight,
      scrollX: 0,
      scrollY: 0,
    });
    const pdf = new jsPDF({ unit: "in", format: "letter", orientation: "portrait" });
    pageBreaks.slice(0, -1).forEach((start, index) => {
      const end = pageBreaks[index + 1];
      const sourceY = Math.round(start * scale);
      const sourceHeight = Math.min(fullCanvas.height - sourceY, Math.max(1, Math.round((end - start) * scale)));
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = fullCanvas.width;
      pageCanvas.height = sourceHeight;
      pageCanvas.getContext("2d").drawImage(fullCanvas, 0, sourceY, fullCanvas.width, sourceHeight, 0, 0, pageCanvas.width, sourceHeight);
      if (index) pdf.addPage();
      pdf.addImage(pageCanvas.toDataURL("image/jpeg", 0.98), "JPEG", pageMarginRight, pageMarginTop, contentWidthIn, sourceHeight / scale / dpi, undefined, "FAST");
    });
    pdf.save("love-quetee-artist-cv.pdf");
  } finally {
    wrapper.remove();
  }
}

async function createPressSheetPdfDownload() {
  const parsed = new DOMParser().parseFromString(buildPressSheetDownloadHtml(PRESS_URL, PRESS_PORTRAIT_URL, CANVAS_REBEL_URL), "text/html");
  const wrapper = document.createElement("div");
  const styles = document.createElement("style");
  styles.textContent = [...parsed.head.querySelectorAll("style")].map((style) => style.textContent).join("\n");
  wrapper.append(styles);
  wrapper.insertAdjacentHTML("beforeend", parsed.body.innerHTML);
  Object.assign(wrapper.style, {
    position: "absolute",
    top: "0",
    left: "0",
    display: "block",
    width: "816px",
    minHeight: "1px",
    background: "#fff",
    zIndex: "100000",
    pointerEvents: "none",
  });
  document.body.appendChild(wrapper);
  try {
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const contentHeight = Math.ceil(Math.max(wrapper.scrollHeight, wrapper.getBoundingClientRect().height));
    if (!contentHeight) throw new Error("Press sheet has no renderable height");
    wrapper.style.height = `${contentHeight}px`;
    const scale = 2;
    const marginTop = 0.45;
    const marginRight = 0.55;
    const printableWidth = 8.5 - (marginRight * 2);
    const printableHeight = 11 - (marginTop * 2);
    const canvas = await html2canvas(wrapper, { scale, backgroundColor: "#ffffff", useCORS: true, width: 816, height: contentHeight, windowWidth: 816, windowHeight: contentHeight, scrollX: 0, scrollY: 0 });
    const naturalWidth = canvas.width / scale / 96;
    const naturalHeight = canvas.height / scale / 96;
    const fit = Math.min(printableWidth / naturalWidth, printableHeight / naturalHeight);
    const imageWidth = naturalWidth * fit;
    const imageHeight = naturalHeight * fit;
    const pdf = new jsPDF({ unit: "in", format: "letter", orientation: "portrait" });
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.98), "JPEG", marginRight + ((printableWidth - imageWidth) / 2), marginTop + ((printableHeight - imageHeight) / 2), imageWidth, imageHeight, undefined, "FAST");
    pdf.save("love-quetee-press-sheet.pdf");
  } finally {
    wrapper.remove();
  }
}

function About() {
  const [portraitFailed, setPortraitFailed] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pressSheetPdfExporting, setPressSheetPdfExporting] = useState(false);

  const handleCvPdfDownload = async () => {
    setPdfExporting(true);
    try {
      await createCvPdfDownload();
      toast.success("PDF CV downloaded", { description: "The formatted artist CV is ready to share." });
    } catch {
      toast.error("PDF CV could not be created", { description: "Use the formatted CV download and print it as a PDF instead." });
    } finally {
      setPdfExporting(false);
    }
  };

  const handlePressSheetPdfDownload = async () => {
    setPressSheetPdfExporting(true);
    try {
      await createPressSheetPdfDownload();
      toast.success("Press sheet PDF downloaded", { description: "The one-page artist press sheet is ready to share." });
    } catch {
      toast.error("Press sheet PDF could not be created", { description: "Use the formatted press sheet download and print it as a PDF instead." });
    } finally {
      setPressSheetPdfExporting(false);
    }
  };

  return (
    <section id="about" className="about-section" data-testid="about-section">
      <div className="section-shell">
        <Reveal className="about-heading">
          <div>
            <h2>Practice<br /><em>and process.</em></h2>
          </div>
        </Reveal>
        <div className="about-introduction">
          <Reveal className="about-lede about-statement-card">
            <p className="section-kicker" data-testid="artist-statement-heading">Artist statement /</p>
            <div className="statement-copy" data-testid="artist-statement-copy">
              <p>I’m a Minnesota-based artist from Liberia, West Africa. My artistic practice is rooted in storytelling and explores the multifaceted nature of Black and African identity, personal experiences, cultural memory, and our connection to the natural world.</p>
              <p>I primarily work in paint and pencil, with portraiture at the center of my practice. I create pieces that go beyond simply capturing someone’s appearance. I’m interested in the stories people carry, their expressions, body language, relationships, environments, and the experiences that shape who they are. I often bring nature into my work because I see a strong connection between the human body, our surroundings, and our sense of identity.</p>
              <p>A lot of my inspiration comes from my Liberian heritage and the stories I grew up hearing from my mother about her experiences in Liberia. As I’ve gotten older, I’ve realized there are parts of my culture and my family’s history that I didn’t personally get to experience. Creating art has become one way for me to reconnect with those stories, preserving them in my own way.</p>
              <p>I want my work to celebrate Black joy, challenge stereotypes, and create a sense of belonging. Representation is important to me because I know what it feels like to look for yourself in the world around you. I want people to see my work and feel recognized, not only in the way they look, but in the emotions, histories, and experiences being represented.</p>
            </div>
          </Reveal>
          <Reveal className="portrait-placeholder has-portrait" data-testid="artist-portrait-placeholder">
            {!portraitFailed && (
              <img
                className="portrait-image"
                src={ARTIST_PORTRAIT_URL}
                alt="Love Quetee in the studio surrounded by paintings in progress"
                loading="lazy"
                decoding="async"
                onError={() => setPortraitFailed(true)}
                data-testid="artist-portrait-image"
              />
            )}
            <div className="portrait-image-wash" aria-hidden="true" />
            <span className="portrait-caption" data-testid="artist-portrait-caption">
              Love Quetee in the studio / Maple Grove, Minnesota
            </span>
          </Reveal>
        </div>
        <div className="about-professional-grid">
          <Reveal className="bio-panel" data-testid="artist-bio-panel">
            <p className="section-kicker" data-testid="artist-bio-heading">Artist bio /</p>
            <h3>A visual journey<br /><em>toward belonging.</em></h3>
            <p data-testid="artist-bio-copy">Devoted to exploring the multifaceted nature of Black/African identity and the deep connections between the human body and the natural world, Love Quetee’s creative practice is shaped by personal experiences and broader cultural narratives. Her portfolio serves as a visual journey, celebrating Black joy, challenging stereotypes, and fostering a sense of belonging for those who need to see themselves reflected.</p>
            <p>Through storytelling in paint and pencil, Love reflects on her heritage and sparks nuanced conversations about community and resilience. Inspired by the harmony between people and the environment, her art invites viewers to consider the layered beauty of ordinary life and the stories that shape us.</p>
            <div className="bio-facts" data-testid="artist-bio-facts">
              <div><span>Born</span><strong>Liberia</strong></div>
              <div><span>Based</span><strong>Maple Grove, Minnesota</strong></div>
              <div><span>Practice</span><strong>Oil &amp; acrylic portraiture, still life</strong></div>
            </div>
          </Reveal>
          <Reveal className="cv-panel" data-testid="cv-panel">
            <div className="panel-header">
              <span>CV / resume index</span>
              <FileText size={15} strokeWidth={1.3} />
            </div>
            <div className="cv-list">
              <div><span>Press</span><a href={PRESS_URL} target="_blank" rel="noreferrer" data-testid="cv-press-link">Mankato Free Press / Love in War</a></div>
              <div><span>Press</span><a href={CANVAS_REBEL_URL} target="_blank" rel="noreferrer" data-testid="cv-canvas-rebel-link">Canvas Rebel / Meet Love Quetee</a></div>
              <div><span>Exhibition</span><strong>Ghetto Visionary</strong><em>The 410 Project / Mankato / November 10, 2023</em></div>
              <div><span>Education</span><strong>Fine arts degree / Minnesota State University—Mankato</strong><em>2023</em></div>
              <div><span>Postgraduate</span><strong>Maryland Institute College of Art / Master of Fine Arts in Studio Art</strong><em>Summer Low-Residency</em></div>
              <div><span>Public art</span><strong>Chapel mural / U.S. Army deployment in Iraq</strong><em>2021 / commission practice</em></div>
              <div><span>Award</span><strong>Weisman Art Museum Award</strong><em>Gbanganla</em></div>
              <div><span>Award</span><strong>North Hennepin Community College Award of Excellence</strong><em>In memory of Lance Kiland</em></div>
              <div><span>Purchase award</span><strong>Minnesota Museum of American Art Purchase Award</strong><em>Gbanganla</em></div>
            </div>
            <div className="cv-panel-downloads" data-testid="cv-panel-downloads">
              <div className="cv-download-group" data-testid="cv-download-group">
                <span className="download-group-label" data-testid="cv-download-group-label">Curriculum Vitae /</span>
                <div className="cv-download-actions">
                  <a className="cv-download" href={`data:text/html;charset=utf-8,${encodeURIComponent(buildCvDownloadHtml(PRESS_URL, CANVAS_REBEL_URL))}`} download="lovequetee-artist-cv.html" data-testid="cv-download-link">
                    Download formatted CV <Download size={15} strokeWidth={1.3} />
                  </a>
                  <button className="cv-download cv-download-pdf" type="button" onClick={handleCvPdfDownload} disabled={pdfExporting} aria-busy={pdfExporting} data-testid="cv-pdf-download-button">
                    {pdfExporting ? <><LoaderCircle className="checkout-spinner" size={15} /> Preparing PDF…</> : <><FileText size={15} strokeWidth={1.3} /> Download PDF</>}
                  </button>
                </div>
              </div>
              <div className="press-sheet-download-group" data-testid="press-sheet-download-panel">
                <span className="download-group-label" data-testid="press-sheet-download-label">Press Kit / One-Page Sheet /</span>
                <div className="cv-download-actions">
                  <a className="cv-download" href={`data:text/html;charset=utf-8,${encodeURIComponent(buildPressSheetDownloadHtml(PRESS_URL, PRESS_PORTRAIT_URL, CANVAS_REBEL_URL))}`} download="lovequetee-press-sheet.html" data-testid="press-sheet-download-link">
                    Download press sheet <Download size={15} strokeWidth={1.3} />
                  </a>
                  <button className="cv-download cv-download-pdf" type="button" onClick={handlePressSheetPdfDownload} disabled={pressSheetPdfExporting} aria-busy={pressSheetPdfExporting} data-testid="press-sheet-pdf-download-button">
                    {pressSheetPdfExporting ? <><LoaderCircle className="checkout-spinner" size={15} /> Preparing PDF…</> : <><FileText size={15} strokeWidth={1.3} /> Press sheet PDF</>}
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ExhibitionImageLightbox({ images, activeIndex, onClose, onNavigate }) {
  const closeButtonRef = useRef(null);
  const image = images[activeIndex];

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onNavigate(-1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        onNavigate(1);
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeIndex, onClose, onNavigate]);

  return (
    <div className="exhibition-lightbox-backdrop" role="dialog" aria-modal="true" aria-label={`Expanded ${image.alt}`} data-testid="exhibition-image-lightbox" onClick={onClose}>
      <div className="exhibition-lightbox-dialog" onClick={(event) => event.stopPropagation()}>
        <div className="exhibition-lightbox-topline">
          <span data-testid="exhibition-lightbox-index">{activeIndex + 1} / {images.length}</span>
          <button className="exhibition-lightbox-close" type="button" onClick={onClose} aria-label="Close expanded exhibition image" ref={closeButtonRef} data-testid="exhibition-lightbox-close-button"><X size={20} strokeWidth={1.2} /></button>
        </div>
        <div className="exhibition-lightbox-stage">
          <button className="exhibition-lightbox-control exhibition-lightbox-previous" type="button" onClick={() => onNavigate(-1)} aria-label="Show previous exhibition image" data-testid="exhibition-lightbox-previous-button"><ArrowLeft size={24} strokeWidth={1.2} /></button>
          <figure className="exhibition-lightbox-figure">
            <img className="exhibition-lightbox-image" src={image.url} alt={image.alt} loading="eager" decoding="async" data-testid="exhibition-lightbox-image" />
          </figure>
          <button className="exhibition-lightbox-control exhibition-lightbox-next" type="button" onClick={() => onNavigate(1)} aria-label="Show next exhibition image" data-testid="exhibition-lightbox-next-button"><ArrowRight size={24} strokeWidth={1.2} /></button>
        </div>
      </div>
    </div>
  );
}

function ExhibitionDetailPage({ exhibition, onShare }) {
  const currentIndex = EXHIBITIONS.findIndex((item) => item.slug === exhibition.slug);
  const prevExhibition = EXHIBITIONS[(currentIndex - 1 + EXHIBITIONS.length) % EXHIBITIONS.length];
  const nextExhibition = EXHIBITIONS[(currentIndex + 1) % EXHIBITIONS.length];
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isSharedExhibition = searchParams.get("source") === "share" || searchParams.get("ref") === "share";
  const [cueDismissed, setCueDismissed] = useState(false);
  const targetWorksUrl = exhibition.slug === "minnesota-state-fair" ? "/works?artwork=work-07" : "/works";
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const navigateLightbox = (direction) => {
    setLightboxIndex((currentIndex) => {
      if (currentIndex === null) return 0;
      return (currentIndex + direction + exhibition.gallery.length) % exhibition.gallery.length;
    });
  };
  const activeLightboxImage = lightboxIndex === null ? null : exhibition.gallery[lightboxIndex];

  return (
    <section className="exhibition-detail-page" data-testid={`exhibition-detail-${exhibition.slug}`}>
      <div className="section-shell">
        <Link className="exhibition-back-link" to="/exhibitions" data-testid="exhibition-detail-back-link"><ArrowRight size={15} strokeWidth={1.3} /> Back to Exhibitions</Link>
        <div className="exhibition-detail-header">
          <div>
            <p className="section-kicker">{exhibition.label}</p>
            {isSharedExhibition && !cueDismissed && (
              <div className="collector-welcome-cue" role="region" aria-label="Collector welcome navigation" data-testid="collector-welcome-cue">
                <span className="collector-welcome-tag" data-testid="collector-welcome-tag">Collector invitation</span>
                <span className="collector-welcome-separator" aria-hidden="true">/</span>
                <Link className="collector-welcome-link" to={targetWorksUrl} data-testid="collector-welcome-link">
                  <span>Explore selected works</span>
                  <ArrowUpRight size={13} strokeWidth={1.4} />
                </Link>
                <button className="collector-welcome-dismiss" type="button" onClick={() => setCueDismissed(true)} aria-label="Dismiss welcome note" data-testid="collector-welcome-dismiss">
                  <X size={12} strokeWidth={1.4} />
                </button>
              </div>
            )}
            <h1>{exhibition.title}{exhibition.subtitle && <><span aria-hidden="true"> </span><br /><em>{exhibition.subtitle}</em></>}</h1>
          </div>
          <div className="exhibition-detail-meta">
            <span>{exhibition.date}</span>
            {exhibition.time && <span data-testid="exhibition-detail-time">{exhibition.time}</span>}
            <span>{exhibition.venue}</span>
            <button className="exhibition-share-button" type="button" onClick={() => onShare(exhibition)} aria-label={`Share ${exhibition.title} exhibition details`} data-testid="exhibition-share-button">
              <Share2 size={14} strokeWidth={1.3} />
              <span>Share exhibition</span>
            </button>
          </div>
        </div>
        {exhibition.description && (
          <section className={`exhibition-detail-statement${exhibition.description.includes("\n\n") ? " exhibition-detail-statement-long" : ""}`} aria-labelledby="exhibition-statement-heading" data-testid="exhibition-detail-statement">
            <div id="exhibition-statement-heading" data-testid="exhibition-detail-statement-copy">
              {exhibition.description.split(/\n\n+/).map((paragraph, index) => <p key={`${exhibition.slug}-statement-${index}`}>{paragraph}</p>)}
            </div>
          </section>
        )}
        {exhibition.slug === "ghetto-visionary" && (
          <section className="exhibition-detail-press" data-testid="ghetto-visionary-press">
            <p className="section-kicker">Press</p>
            <h2>Love in War: Artist who found inspiration in Iraq has gallery show at the 410</h2>
            <p data-testid="ghetto-visionary-press-intro">A Mankato Free Press profile on Love Quetee’s path from military service in Iraq to the “Ghetto Visionary” exhibition, and the way personal history enters the work.</p>
            <a className="press-link" href={PRESS_URL} target="_blank" rel="noreferrer" data-testid="ghetto-visionary-press-link">
              Read the Mankato Free Press feature <ExternalLink size={16} strokeWidth={1.3} />
            </a>
          </section>
        )}
        {exhibition.gallery.length > 0 && (
          <div className="exhibition-gallery" data-testid="exhibition-gallery">
            {exhibition.gallery.map((image, index) => (
              <figure className={`exhibition-gallery-item exhibition-gallery-${image.type.toLowerCase().replaceAll(" ", "-")}`} key={image.url}>
                <button className="exhibition-gallery-trigger" type="button" onClick={() => openLightbox(index)} aria-label={`Expand ${image.alt}`} data-testid={`exhibition-gallery-open-${index + 1}`}>
                  <img src={image.url} alt={image.alt} loading={index < 2 ? "eager" : "lazy"} decoding="async" data-testid={`exhibition-gallery-image-${index + 1}`} />
                </button>
              </figure>
            ))}
          </div>
        )}
        {activeLightboxImage && <ExhibitionImageLightbox images={exhibition.gallery} activeIndex={lightboxIndex} onClose={closeLightbox} onNavigate={navigateLightbox} />}
        <nav className="exhibition-detail-nav" aria-label="Exhibition navigation" data-testid="exhibition-detail-nav">
          <Link className="exhibition-nav-link exhibition-nav-prev" to={`/exhibitions/${prevExhibition.slug}`} aria-label={`Previous exhibition: ${prevExhibition.title} ${prevExhibition.subtitle || ""}`.trim()} data-testid="exhibition-nav-prev-link">
            <ArrowLeft size={22} strokeWidth={1.4} />
            <div className="exhibition-nav-copy">
              <span className="exhibition-nav-kicker">Previous</span>
              <strong className="exhibition-nav-title" data-testid="exhibition-nav-prev-title">
                {prevExhibition.title}{prevExhibition.subtitle ? ` ${prevExhibition.subtitle}` : ""}
              </strong>
            </div>
          </Link>
          <Link className="exhibition-nav-link exhibition-nav-next" to={`/exhibitions/${nextExhibition.slug}`} aria-label={`Next exhibition: ${nextExhibition.title} ${nextExhibition.subtitle || ""}`.trim()} data-testid="exhibition-nav-next-link">
            <div className="exhibition-nav-copy">
              <span className="exhibition-nav-kicker">Next</span>
              <strong className="exhibition-nav-title" data-testid="exhibition-nav-next-title">
                {nextExhibition.title}{nextExhibition.subtitle ? ` ${nextExhibition.subtitle}` : ""}
              </strong>
            </div>
            <ArrowRight size={22} strokeWidth={1.4} />
          </Link>
        </nav>
        <Link className="button button-text exhibition-detail-return" to="/exhibitions" data-testid="exhibition-detail-return-link">Return to all exhibitions <ArrowRight size={15} strokeWidth={1.4} /></Link>
      </div>
    </section>
  );
}

function NewsEventsPage() {
  const upcomingExhibition = EXHIBITIONS.find((exhibition) => exhibition.slug === "descendants-of-the-sun");

  return (
    <section className="news-events-section" data-testid="news-events-section">
      <div className="section-shell">
        <Reveal className="news-events-heading">
          <div>
            <p className="section-kicker">News &amp; events</p>
            <h2 data-testid="news-events-title">Next on<br /><em>the calendar.</em></h2>
          </div>
        </Reveal>
        <Reveal className="news-event-card-reveal">
          <article className="news-event-flyer-card" data-testid="news-event-descendants-of-the-sun">
            <img className="news-event-flyer-image" src={upcomingExhibition.flyerUrl} alt="Descendants of the Sun solo exhibition flyer" loading="eager" decoding="async" data-testid="news-event-flyer-image" />
            <p className="news-event-flyer-location" data-testid="news-event-location">{upcomingExhibition.venue}</p>
          </article>
        </Reveal>
        <Reveal className="news-class-reveal">
          <article className="news-class-card" data-testid="news-event-color-theory">
            <img className="news-class-flyer" src={COLOR_THEORY_FLYER_URL} alt="Free paint, free canvases, free lessons color theory class flyer" loading="lazy" decoding="async" data-testid="news-event-color-theory-flyer" />
            <div className="news-class-copy">
              <p className="news-class-title" data-testid="news-event-color-theory-title">Teaching a class on color theory — October 10, 5:30 to 8:30 pm</p>
              <p className="news-class-location" data-testid="news-event-color-theory-location">Art Shelf / 1415 W 35th St, Minneapolis, MN</p>
            </div>
          </article>
        </Reveal>
        <Reveal className="news-story-reveal">
          <article className="news-story-card" data-testid="news-item-gbanganla-permanent-collection">
            <span className="news-story-label" data-testid="news-item-label">News</span>
            <div className="news-story-copy">
              <h3 data-testid="news-item-title">Gbanganla enters a permanent collection.</h3>
              <p data-testid="news-item-description">Gbanganla will be a part of the Minnesota Museum of American Art’s permanent collection.</p>
            </div>
          </article>
        </Reveal>
      </div>
    </section>
  );
}

function ExhibitionsNews() {
  const [archiveFilter, setArchiveFilter] = useState("all");
  const archiveFilterOptions = [
    { value: "all", label: "All" },
    { value: "upcoming", label: "Upcoming" },
    { value: "past", label: "Past" },
  ];
  const filteredExhibitions = archiveFilter === "all"
    ? EXHIBITIONS
    : EXHIBITIONS.filter((exhibition) => archiveFilter === "upcoming"
      ? exhibition.status === "Upcoming exhibition"
      : exhibition.status === "Past exhibition");
  const cardTestIds = {
    "minnesota-state-fair": "current-state-fair-exhibition",
    "descendants-of-the-sun": "upcoming-descendants-exhibition",
    "ghetto-visionary": "past-exhibitions",
  };

  return (
    <section id="exhibitions" className="exhibitions-section" data-testid="exhibitions-section">
      <div className="section-shell">
        <Reveal className="section-heading exhibitions-heading">
          <div>
            <h2 data-testid="exhibition-archive-title">Where the work<br /><em>has been.</em></h2>
          </div>
        </Reveal>
        <Reveal className="exhibition-archive-controls" data-testid="exhibition-archive-controls">
          <div className="exhibition-archive-filters" role="group" aria-label="Filter exhibitions by upcoming or past status">
            {archiveFilterOptions.map((option) => (
              <button
                key={option.value}
                className={`exhibition-archive-filter${archiveFilter === option.value ? " is-active" : ""}`}
                type="button"
                aria-pressed={archiveFilter === option.value}
                onClick={() => setArchiveFilter(option.value)}
                data-testid={`exhibition-filter-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="exhibitions-grid" data-testid="exhibition-archive-grid">
          {filteredExhibitions.map((exhibition, index) => {
            const statusClass = exhibition.status === "Upcoming exhibition" ? "upcoming" : "past";
            return (
              <Reveal className={`event-card-reveal event-card-reveal-${index + 1}`} key={exhibition.slug}>
                <NavLink className={`event-card event-card-link event-card-${statusClass}`} to={`/exhibitions/${exhibition.slug}`} data-testid={cardTestIds[exhibition.slug]}>
                  <p className="section-kicker">{exhibition.label}</p>
                  <h3>{exhibition.title}{exhibition.subtitle && <><br /><em>{exhibition.subtitle}</em></>}</h3>
                  <p>{exhibition.date}</p>
                  <p className="event-source">{exhibition.venue}</p>
                  {exhibition.status && <span className="event-status">{exhibition.status} <ArrowRight size={15} strokeWidth={1.3} /></span>}
                </NavLink>
              </Reveal>
            );
          })}
        </div>
        <Reveal className="press-journal" data-testid="press-journal">
          <div className="press-journal-label">
            <p className="section-kicker">Press / journal</p>
          </div>
          <div className="press-journal-copy">
            <div className="press-links-row" data-testid="press-links-row">
              <div className="press-journal-entry" data-testid="press-mankato-entry">
                <a className="press-link" href={PRESS_URL} target="_blank" rel="noreferrer" data-testid="press-article-link">
                  Read the Mankato Free Press feature <ExternalLink size={16} strokeWidth={1.3} />
                </a>
                <p>A Mankato Free Press profile on Love Quetee’s path from military service in Iraq to the “Ghetto Visionary” exhibition, and the way personal history enters the work.</p>
              </div>
              <div className="press-journal-entry" data-testid="press-canvas-rebel-entry">
                <a className="press-link" href={CANVAS_REBEL_URL} target="_blank" rel="noreferrer" data-testid="canvas-rebel-article-link">
                  Meet Love Quetee / Canvas Rebel <ExternalLink size={16} strokeWidth={1.3} />
                </a>
                <p>A Canvas Rebel profile on Love Quetee’s artistic practice, heritage, and the stories held in her paintings.</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Contact({ onCatalogue }) {
  const [inquirySending, setInquirySending] = useState(false);
  const [newsletterSending, setNewsletterSending] = useState(false);

  const submitInquiry = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setInquirySending(true);
    try {
      const response = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(Object.fromEntries(new FormData(form).entries())),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Inquiry delivery failed");
      if (payload.status !== "sent") {
        if (payload.confirmation_status === "sent") {
          toast.success("Your confirmation is on its way", { description: "We received your email address. The studio inbox is temporarily unavailable, but your automatic response has been sent." });
          form.reset();
          return;
        }
        throw new Error(payload.message || "The studio is temporarily unable to receive inquiries.");
      }
      toast.success("Your studio dispatch was sent", {
        description: payload.confirmation_status === "sent"
          ? "Your inquiry reached the Love Quetee studio inbox. A receipt is on its way to your email."
          : "Your inquiry reached the Love Quetee studio inbox. The confirmation email could not be delivered yet.",
      });
      form.reset();
    } catch {
      toast.error("Studio dispatch could not be sent", {
        description: "Please try again or use the studio email directly.",
      });
    } finally {
      setInquirySending(false);
    }
  };

  const subscribe = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const { email } = Object.fromEntries(new FormData(form).entries());
    setNewsletterSending(true);
    try {
      const response = await fetch(`${API}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Newsletter delivery failed");
      if (payload.status !== "sent") {
        if (payload.confirmation_status === "sent") {
          toast.success("Your welcome note is on its way", { description: "We received your email address. The studio inbox is temporarily unavailable, but your automatic response has been sent." });
          form.reset();
          return;
        }
        throw new Error(payload.message || "The studio is temporarily unable to receive signups.");
      }
      toast.success("With, Love signup received", {
        description: payload.confirmation_status === "sent"
          ? "The signup reached the Love Quetee studio inbox. A welcome note is on its way to your email."
          : "The signup reached the Love Quetee studio inbox. The welcome note could not be delivered yet.",
      });
      form.reset();
    } catch {
      toast.error("With, Love signup could not be sent", {
        description: "Please try again or use the studio email directly.",
      });
    } finally {
      setNewsletterSending(false);
    }
  };

  const copyEmail = async () => {
    await navigator.clipboard?.writeText(STUDIO_EMAIL);
    toast.success("Studio email copied", { description: STUDIO_EMAIL });
  };

  return (
    <section id="contact" className="contact-section" data-testid="contact-section">
      <div className="section-shell">
        <Reveal className="contact-heading">
          <div>
            <h2>Keep a place<br /><em>for the work.</em></h2>
          </div>
        </Reveal>
        <div className="contact-grid">
          <Reveal className="contact-form-panel">
            <div className="panel-header">
              <span>Collector dispatch</span>
            </div>
            <form className="contact-form" onSubmit={submitInquiry} data-testid="contact-inquiry-form">
              <label>
                Your name
                <input name="name" type="text" placeholder="Full name" required data-testid="contact-name-input" />
              </label>
              <label>
                Email address
                <input name="email" type="email" placeholder="you@example.com" required data-testid="contact-email-input" />
              </label>
              <label>
                I am writing as
                <select name="role" defaultValue="" required data-testid="contact-role-select">
                  <option value="" disabled>Select one</option>
                  <option>Collector</option>
                  <option>Gallery / institution</option>
                  <option>Press / editorial</option>
                  <option>Studio visit</option>
                  <option>Other</option>
                </select>
              </label>
              <label>
                Your note
                <textarea name="message" rows="5" placeholder="Tell the studio what has stayed with you." required data-testid="contact-message-input" />
              </label>
              <button
                className="button button-primary form-submit"
                type="submit"
                disabled={inquirySending}
                aria-busy={inquirySending}
                data-testid="contact-submit-button"
              >
                {inquirySending ? "Sending dispatch…" : "Send studio dispatch"} <ArrowUpRight size={16} strokeWidth={1.4} />
              </button>
              <p className="form-note">Live dispatch / replies return to the studio inbox.</p>
            </form>
          </Reveal>
          <Reveal className="contact-aside">
            <p className="newsletter-section-heading" data-testid="newsletter-heading">Newsletter sign up</p>
            <div className="newsletter-panel">
              <div className="panel-header">
                <Mail size={15} strokeWidth={1.3} />
              </div>
              <h3>With,<br /><em>Love</em></h3>
              <p>Occasional letters from the studio.</p>
              <form className="newsletter-form" onSubmit={subscribe} data-testid="newsletter-subscribe-form">
                <label className="sr-only" htmlFor="newsletter-email">Email address</label>
                <input id="newsletter-email" name="email" type="email" placeholder="Your email address" required data-testid="newsletter-email-input" />
                <button
                  className="circle-submit"
                  type="submit"
                  aria-label={newsletterSending ? "Sending With, Love signup" : "Join With, Love"}
                  aria-busy={newsletterSending}
                  disabled={newsletterSending}
                  data-testid="newsletter-submit-button"
                >
                  <ArrowUpRight size={18} strokeWidth={1.4} />
                </button>
              </form>
              <span className="micro-note">No noise. No more than necessary.</span>
            </div>
            <div className="contact-details">
              <div className="contact-detail-block">
                <p className="section-kicker">With, Love / direct studio line</p>
                <a className="studio-email" href={`mailto:${STUDIO_EMAIL}`} data-testid="studio-email-link">{STUDIO_EMAIL}</a>
                <button className="copy-email" type="button" onClick={copyEmail} data-testid="copy-studio-email-button">
                  <Clipboard size={14} strokeWidth={1.3} /> Copy address
                </button>
              </div>
              <div className="contact-detail-block social-links">
                <p className="section-kicker">Follow the studio</p>
                <a className="social-link" href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="instagram-link">
                  <Instagram size={16} strokeWidth={1.3} /> Instagram / @loves_artpage <ExternalLink size={13} strokeWidth={1.3} />
                </a>
              </div>
              <div className="contact-detail-block contact-locations">
                <p className="section-kicker">Studio base / by appointment</p>
                <span>Maple Grove · Minnesota</span>
              </div>
              <button className="catalogue-link" type="button" onClick={onCatalogue} data-testid="contact-catalogue-button">
                Open the collector catalogue <Download size={15} strokeWidth={1.3} />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="footer-topline">
        <span>With, Love / Love Quetee</span>
        <span>© 2026 / All works reserved</span>
      </div>
      <div className="footer-wordmark">Love Quetee</div>
      <div className="footer-bottomline">
        <div className="footer-links">
          <NavLink to="/" end data-testid="footer-link-home">Home</NavLink>
          <NavLink to="/works" end data-testid="footer-link-works">Works</NavLink>
          <NavLink to="/about" end data-testid="footer-link-about">About</NavLink>
          <NavLink to="/exhibitions" end data-testid="footer-link-exhibitions">Exhibitions</NavLink>
          <NavLink to="/news-events" end data-testid="footer-link-news-events">News &amp; Events</NavLink>
          <NavLink to="/contact" end data-testid="footer-link-contact">Contact</NavLink>
          <Link to="/studio" data-testid="footer-studio-link">Private studio</Link>
        </div>
        <button className="back-to-top" type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="back-to-top-button">
          Back to top <ArrowUpRight size={15} strokeWidth={1.4} />
        </button>
      </div>
    </footer>
  );
}

function ModalShell({ children, onClose, testId, wide = false, drawer = false, labelledBy, surfaceClassName = "" }) {
  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousDocumentOverflow = document.documentElement.style.overflow;
    const activeLenis = pageLenis;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    activeLenis?.stop();
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousDocumentOverflow;
      activeLenis?.start();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);
  useEffect(() => {
    if (!surfaceClassName.includes("artwork-detail-modal-surface")) return undefined;
    const surface = document.querySelector(".artwork-detail-modal-surface");
    if (!surface) return undefined;
    const handleWheel = (event) => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      surface.scrollTop += event.deltaY;
    };
    surface.addEventListener("wheel", handleWheel, { passive: false });
    return () => surface.removeEventListener("wheel", handleWheel);
  }, [surfaceClassName]);


  return (
    <div className="modal-backdrop" data-testid={testId} onMouseDown={onClose}>
      <div className={`modal-surface ${wide ? "modal-wide" : ""} ${drawer ? "modal-drawer" : ""} ${surfaceClassName}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy} onMouseDown={(event) => event.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function ArtworkModal({ work, onClose, onInquire, onAddToCart, isShortlisted, onToggleShortlist, onShare, previousWork, nextWork, onPreviousWork, onNextWork }) {
  const [zoomScale, setZoomScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const artworkRef = useRef(null);
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);
  const dragRef = useRef(null);
  const zoomed = zoomScale > 1;
  const clampScale = (scale) => Math.min(4, Math.max(1, scale));
  const clampPan = useCallback((value, axis, scale) => {
    const size = artworkRef.current?.[axis === "x" ? "clientWidth" : "clientHeight"] || 0;
    const limit = size * (scale - 1) / 2;
    return Math.min(limit, Math.max(-limit, value));
  }, []);
  const updateScale = useCallback((nextScale) => {
    const scale = clampScale(nextScale);
    setZoomScale(scale);
    if (scale === 1) setPan({ x: 0, y: 0 });
    else setPan((current) => ({ x: clampPan(current.x, "x", scale), y: clampPan(current.y, "y", scale) }));
  }, [clampPan]);
  useEffect(() => {
    const viewport = artworkRef.current;
    if (!viewport) return undefined;
    const handleWheel = (event) => {
      if (!zoomed) return;
      event.preventDefault();
      updateScale(zoomScale - event.deltaY * 0.0025);
    };
    viewport.addEventListener("wheel", handleWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", handleWheel);
  }, [updateScale, zoomScale, zoomed]);
  const handlePointerDown = (event) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointersRef.current.size === 2) {
      const points = [...pointersRef.current.values()];
      pinchRef.current = { distance: Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y), scale: zoomScale };
      dragRef.current = null;
    } else if (zoomed) {
      dragRef.current = { x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y };
    }
  };
  const handlePointerMove = (event) => {
    if (!pointersRef.current.has(event.pointerId)) return;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointersRef.current.size >= 2 && pinchRef.current) {
      const points = [...pointersRef.current.values()];
      const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      updateScale(pinchRef.current.scale * distance / pinchRef.current.distance);
    } else if (dragRef.current && zoomed) {
      setPan({
        x: clampPan(dragRef.current.panX + event.clientX - dragRef.current.x, "x", zoomScale),
        y: clampPan(dragRef.current.panY + event.clientY - dragRef.current.y, "y", zoomScale),
      });
    }
  };
  const handlePointerUp = (event) => {
    pointersRef.current.delete(event.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (pointersRef.current.size === 1 && zoomed) {
      const point = [...pointersRef.current.values()][0];
      dragRef.current = { x: point.x, y: point.y, panX: pan.x, panY: pan.y };
    } else if (pointersRef.current.size === 0) {
      dragRef.current = null;
    }
  };
  const printEdition = getPrintEdition(work.id);
  useEffect(() => {
    setZoomScale(1);
    setPan({ x: 0, y: 0 });
  }, [work.id]);
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onPreviousWork();
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        onNextWork();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onNextWork, onPreviousWork]);


  return (
    <ModalShell testId="artwork-detail-modal" onClose={onClose} wide surfaceClassName="artwork-detail-modal-surface">
      <div className="modal-topline artwork-detail-topline">
        <span>Detail{work.accession ? ` / ${work.accession}` : ""}</span>
        <div className="artwork-modal-navigation" data-testid="artwork-modal-navigation">
          <button className="artwork-modal-nav-button" type="button" onClick={onPreviousWork} data-testid="artwork-modal-previous-work-button">
            <ArrowLeft size={16} strokeWidth={1.4} /> Previous
          </button>
          <button className="artwork-modal-nav-button" type="button" onClick={onNextWork} data-testid="artwork-modal-next-work-button">
            Next <ArrowRight size={16} strokeWidth={1.4} />
          </button>
        </div>
        <button className="modal-close" type="button" onClick={onClose} data-testid="artwork-modal-close-button">
          Close <X size={17} strokeWidth={1.3} />
        </button>
      </div>
      <div className="detail-modal-grid">
        <div
          ref={artworkRef}
          className={`modal-artwork ${work.tone} ${work.imageUrl ? "has-image" : ""} ${zoomed ? "is-zoomed" : ""}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          data-testid="artwork-zoom-viewport"
        >
          {work.imageUrl && (
            <img
              className="modal-artwork-image"
              src={work.imageUrl}
              alt={work.imageAlt || `${work.title} by Love Quetee`}
              draggable="false"
              style={{ transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoomScale})` }}
              data-testid={`modal-artwork-image-${work.id}`}
            />
          )}
          <div className="modal-artwork-wash" aria-hidden="true" />
          {!work.imageUrl && <span className="modal-roman">{work.roman}</span>}
          {!work.imageUrl && <span className="modal-artwork-copy">Original image<br />to be added</span>}
          {!work.imageUrl && <span className="modal-artwork-note">Archival placeholder / {work.dimensions}</span>}
          {work.imageUrl && <div className="artwork-zoom-hint" role="note" data-testid="artwork-zoom-hint">Scroll / pinch to inspect</div>}
        </div>
        <div className="modal-detail-copy">
          {work.series && <p className="section-kicker">{work.series}</p>}
          <h2 id="artwork-detail-title" data-testid="artwork-detail-title">{work.title}</h2>
          {work.description && (
            <div className="modal-description" data-testid="artwork-description">
              {work.description.split("\n\n").map((paragraph, index) => <p key={`${work.id}-description-${index}`}>{paragraph}</p>)}
            </div>
          )}
          <dl className="detail-list">
            <div><dt>Year</dt><dd>{work.year}</dd></div>
            <div><dt>Medium</dt><dd>{work.medium}</dd></div>
            <div><dt>Dimensions</dt><dd>{work.dimensions}</dd></div>
            <div><dt>Status</dt><dd>{work.status}</dd></div>
            {work.accession && <div><dt>Accession</dt><dd>{work.accession}</dd></div>}
            {work.awards?.length > 0 && <div><dt>Awards</dt><dd>{work.awards.join(" · ")}</dd></div>}
          </dl>
          <div className="artwork-modal-tools">
            <button
              className={`artwork-shortlist-toggle${isShortlisted ? " is-active" : ""}`}
              type="button"
              aria-pressed={isShortlisted}
              aria-label={`${isShortlisted ? "Remove" : "Add"} ${work.title} ${isShortlisted ? "from" : "to"} collector shortlist`}
              onClick={() => onToggleShortlist(work)}
              data-testid="artwork-modal-shortlist-toggle"
            >
              {isShortlisted ? <BookmarkCheck size={15} strokeWidth={1.4} /> : <Bookmark size={15} strokeWidth={1.4} />}
              {isShortlisted ? "Shortlisted" : "Shortlist this work"}
            </button>
            <button className="artwork-share-button" type="button" onClick={() => onShare(work)} aria-label={`Share ${work.title}`} data-testid="artwork-modal-share-button">
              Share artwork <ArrowUpRight size={15} strokeWidth={1.3} />
            </button>
          </div>
          {printEdition ? (
            <section className="modal-print-edition" data-testid="modal-print-edition-section">
              <div className="modal-print-heading">
                <span>Print</span>
                <strong data-testid="modal-print-price">{printEdition.priceLabel}</strong>
              </div>
              <p className="modal-print-dimensions" data-testid="modal-print-dimensions">{printEdition.dimensions}</p>
              <button
                className="button button-primary modal-print-cta"
                type="button"
                onClick={() => onAddToCart(work)}
                aria-label={`Add ${printEdition.dimensions} print edition of ${work.title} to cart`}
                data-testid={`modal-add-print-button-${work.id}`}
              >
                Add print to cart <ShoppingBag size={15} strokeWidth={1.3} />
              </button>
            </section>
          ) : work.series !== "Still Life" && work.id !== "work-08" ? (
            <div className="modal-print-unavailable" data-testid="modal-print-unavailable-notice">
              Prints unavailable for Still Life studies / inquire for original acquisition only.
            </div>
          ) : null}
          <button className="button button-primary" type="button" onClick={() => onInquire(work)} data-testid={`modal-inquire-button-${work.id}`}>
            Inquire for acquisition <ArrowUpRight size={16} strokeWidth={1.4} />
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function InquiryModal({ work, selectedWorks = [], onClose }) {
  const inquiryWorks = selectedWorks.length ? selectedWorks : work ? [work] : [];
  const inquiryWorkValue = inquiryWorks.map((item) => item.title).join(" · ");
  const inquiryMessage = inquiryWorks.length > 1
    ? `I’m interested in these works: ${inquiryWorkValue}.`
    : "";
  const [sending, setSending] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setSending(true);
    try {
      const response = await fetch(`${API}/api/acquisition-inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(Object.fromEntries(new FormData(form).entries())),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Acquisition inquiry delivery failed");
      if (payload.status !== "sent") {
        if (payload.confirmation_status === "sent") {
          toast.success("Your confirmation is on its way", { description: "We received your email address. The studio inbox is temporarily unavailable, but your automatic response has been sent." });
          onClose();
          return;
        }
        throw new Error(payload.message || "The studio is temporarily unable to receive inquiries.");
      }
      toast.success("Your collector inquiry was sent", {
        description: payload.confirmation_status === "sent"
          ? "The studio received your note. A confirmation is on its way to your email."
          : "The studio received your note. The confirmation email could not be delivered yet.",
      });
      onClose();
    } catch (error) {
      toast.error("Collector inquiry could not be sent", {
        description: error instanceof Error ? error.message : "Please try again shortly.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalShell testId="acquisition-inquiry-modal" onClose={onClose}>
      <div className="modal-topline">
        <span>Private collector inquiry</span>
        <button className="modal-close" type="button" onClick={onClose} data-testid="inquiry-modal-close-button">
          Close <X size={17} strokeWidth={1.3} />
        </button>
      </div>
      <div className="inquiry-header">
        <p className="section-kicker">{work ? [work.accession, work.series].filter(Boolean).join(" / ") : "With, Love studio"}</p>
        <h2>Begin a considered<br /><em>conversation.</em></h2>
        <p>We will return with availability, pricing, and next steps for bringing a work home.</p>
      </div>
      <form className="modal-form" onSubmit={submit} data-testid="acquisition-inquiry-form">
        <label>
          Name
          <input name="name" type="text" placeholder="Full name" required data-testid="inquiry-name-input" />
        </label>
        <label>
          Email
          <input name="email" type="email" placeholder="you@example.com" required data-testid="inquiry-email-input" />
        </label>
        {inquiryWorks.length > 1 ? (
          <label>
            Selected works
            <input name="work" type="text" value={inquiryWorkValue} readOnly data-testid="inquiry-works-summary-input" />
          </label>
        ) : (
          <label>
            Selected work
            <select name="work" defaultValue={work?.title || ""} required data-testid="inquiry-work-select">
              <option value="" disabled>Select a work</option>
              {archiveWorks.map((item) => <option key={item.id}>{item.title}</option>)}
            </select>
          </label>
        )}
        <label className="form-full-width">
          Message
          <textarea name="message" rows="4" placeholder="A note for the studio" defaultValue={inquiryMessage} required data-testid="inquiry-message-input" />
        </label>
        <div className="modal-form-actions">
          <button className="button button-primary" type="submit" disabled={sending} aria-busy={sending} data-testid="inquiry-submit-button">
            {sending ? "Sending inquiry…" : "Send inquiry"} <ArrowUpRight size={16} strokeWidth={1.4} />
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function GoogleAuthCallback({ sessionId }) {
  const navigate = useNavigate();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    fetch(`${API}/api/auth/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.detail || "Google sign-in could not be completed");
        await new Promise((resolve) => setTimeout(resolve, 150));
        navigate("/studio", { replace: true });
      })
      .catch((authError) => setError(authError instanceof Error ? authError.message : "Google sign-in could not be completed"));
  }, [navigate, sessionId]);

  return (
    <div className="studio-shell studio-callback" data-testid="studio-auth-callback">
      <main className="studio-callback-card">
        <p className="section-kicker">Private studio / secure handoff</p>
        <h1>{error ? "The studio door stayed closed." : "Opening the studio."}</h1>
        <p data-testid="studio-auth-status">{error || "Completing your Google sign-in securely."}</p>
        {error && <a className="button button-primary" href="/studio" data-testid="studio-auth-retry">Try again <LogIn size={15} strokeWidth={1.4} /></a>}
      </main>
    </div>
  );
}

function formatLedgerDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function isFollowUpOverdue(value, status) {
  if (!value || status === "completed") return false;
  const today = new Date();
  const localToday = [today.getFullYear(), String(today.getMonth() + 1).padStart(2, "0"), String(today.getDate()).padStart(2, "0")].join("-");
  return value < localToday;
}

function BuyerEmailPreviewModal({ shipment, template, subject, html, loading, error, onTemplateChange, onClose }) {
  return (
    <ModalShell onClose={onClose} testId="studio-email-preview-modal" wide labelledBy="studio-email-preview-title" surfaceClassName="studio-email-preview-modal-surface">
      <div className="studio-email-preview-header">
        <div>
          <p className="section-kicker">Buyer email / read-only preview</p>
          <h2 id="studio-email-preview-title" data-testid="studio-email-preview-title">A note before<br /><em>it arrives.</em></h2>
          <p className="studio-email-preview-recipient" data-testid="studio-email-preview-recipient">Preview for {shipment.buyer_email} / {shipment.order_reference}</p>
        </div>
        <button className="studio-email-preview-close" type="button" onClick={onClose} aria-label="Close buyer email preview" data-testid="studio-email-preview-close"><X size={18} strokeWidth={1.3} /></button>
      </div>
      <div className="studio-email-preview-toolbar">
        <label>
          <span className="studio-ledger-label">Email template</span>
          <select value={template} onChange={(event) => onTemplateChange(event.target.value)} disabled={loading} data-testid="studio-email-preview-template-select">
            {BUYER_EMAIL_PREVIEW_TEMPLATES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        {subject && <span className="studio-email-preview-subject" data-testid="studio-email-preview-subject">Subject / {subject}</span>}
      </div>
      {loading && <div className="studio-email-preview-loading" data-testid="studio-email-preview-loading"><LoaderCircle className="checkout-spinner" size={17} /> Rendering preview…</div>}
      {error && <div className="studio-email-preview-error" role="alert" data-testid="studio-email-preview-error">{error}</div>}
      {!loading && !error && html && <iframe className="studio-email-preview-frame" title="Buyer email preview" srcDoc={html} sandbox="" data-testid="studio-email-preview-frame" />}
      <p className="studio-email-preview-note" data-testid="studio-email-preview-note">Preview only. Sending remains available from the row’s explicit Send or Resend action.</p>
    </ModalShell>
  );
}

function ShipmentLedgerRow({ shipment, index, onOutreachChange, outreachSavingKey, outreachSavedKey, outreachErrorKey, outreachError, onNoteBlur, noteSavingKey, noteSavedKey, noteErrorKey, noteError, onDeliveryStatusChange, deliverySavingKey, deliverySavedKey, deliveryErrorKey, deliveryError, onDeliveryEmail, deliveryEmailSavingKey, deliveryEmailSavedKey, deliveryEmailErrorKey, deliveryEmailError, onCopyTracking, trackingCopiedKey, onPreviewEmail, onEmailHistoryToggle, onEmailHistoryFilterChange, emailHistoryByOrder, emailHistoryFilter, emailHistoryOpenKey, emailHistoryLoadingKey, emailHistoryErrorKey, emailHistoryError }) {
  const [noteDraft, setNoteDraft] = useState(shipment.outreach_note || "");
  const rowId = `${slugify(shipment.order_reference || "shipment")}-${index + 1}`;
  const outreachKey = `${shipment.buyer_email}:${shipment.order_reference}`;
  const shippingAddress = [shipment.address_line1, shipment.address_line2, [shipment.city, shipment.state, shipment.postal_code].filter(Boolean).join(", "), shipment.country].filter(Boolean).join(" / ");
  const deliveryStatus = shipment.delivery_status || "in_transit";
  const trackingUrl = shipment.tracking_url || getCarrierTrackingUrl(shipment.carrier, shipment.tracking_number);
  const timeline = Array.isArray(shipment.delivery_timeline) && shipment.delivery_timeline.length ? shipment.delivery_timeline : [{ status: deliveryStatus, at: shipment.sent_at }];
  const emailHistoryCacheKey = `${shipment.order_reference}:${emailHistoryFilter}`;
  const emailHistory = emailHistoryByOrder[emailHistoryCacheKey] || [];
  const emailHistoryOpen = emailHistoryOpenKey === shipment.order_reference;
  const isEmailHistoryLoading = emailHistoryLoadingKey === emailHistoryCacheKey;
  const isSaving = outreachSavingKey === outreachKey;
  const isSaved = outreachSavedKey === outreachKey;
  const isNoteSaving = noteSavingKey === outreachKey;
  const isNoteSaved = noteSavedKey === outreachKey;
  const isDeliverySaving = deliverySavingKey === outreachKey;
  const isDeliverySaved = deliverySavedKey === outreachKey;
  const isDeliveryEmailSaving = deliveryEmailSavingKey === outreachKey;
  const isDeliveryEmailSaved = deliveryEmailSavedKey === outreachKey;
  useEffect(() => {
    setNoteDraft(shipment.outreach_note || "");
  }, [shipment.outreach_note]);
  return (
    <article className="studio-ledger-row" data-testid={`studio-ledger-row-${rowId}`}>
      <div className="studio-ledger-row-topline">
        <div>
          <span className="studio-ledger-label">Order reference</span>
          <strong data-testid={`studio-ledger-order-${rowId}`}>{shipment.order_reference}</strong>
        </div>
        <div>
          <span className="studio-ledger-label">Purchased</span>
          <strong data-testid={`studio-ledger-purchase-date-${rowId}`}>{shipment.purchase_date_time || "Not provided"}</strong>
          <small className="studio-ledger-shipped-date" data-testid={`studio-ledger-date-${rowId}`}>Shipped {shipment.shipped_date}</small>
        </div>
      </div>
      <div className="studio-ledger-row-details">
        <div>
          <span className="studio-ledger-label">Recipient</span>
          <strong className="studio-ledger-value" data-testid={`studio-ledger-buyer-name-${rowId}`}>{shipment.shipping_name || "Name not provided"}</strong>
          <span className="studio-ledger-value" data-testid={`studio-ledger-buyer-${rowId}`}>{shipment.buyer_email}</span>
          {shippingAddress && <small className="studio-ledger-shipping-address" data-testid={`studio-ledger-address-${rowId}`}>{shippingAddress}</small>}
          {shipment.shipping_phone && <small className="studio-ledger-shipping-phone" data-testid={`studio-ledger-phone-${rowId}`}>Phone {shipment.shipping_phone}</small>}
        </div>
        <div>
          <span className="studio-ledger-label">Carrier / tracking</span>
          <span className="studio-ledger-value" data-testid={`studio-ledger-carrier-${rowId}`}>{shipment.carrier} / {shipment.tracking_number}</span>
          <div className="studio-ledger-tracking-actions">
            {trackingUrl && <a className="studio-ledger-tracking-link" href={trackingUrl} target="_blank" rel="noreferrer" data-testid={`studio-ledger-tracking-link-${rowId}`}>Open carrier tracking <ExternalLink size={13} strokeWidth={1.4} /></a>}
            <button className="studio-ledger-copy-tracking" type="button" onClick={() => onCopyTracking(shipment)} data-testid={`studio-ledger-copy-tracking-${rowId}`}>{trackingCopiedKey === outreachKey ? "Copied" : "Copy tracking"} <Clipboard size={13} strokeWidth={1.4} /></button>
          </div>
        </div>
      </div>
      <div className="studio-ledger-delivery-panel" data-testid={`studio-ledger-delivery-panel-${rowId}`}>
        <div className="studio-ledger-delivery-heading">
          <span className="studio-ledger-label">Delivery status</span>
          <span className={`studio-ledger-delivery-status studio-ledger-delivery-status-${deliveryStatus}`} data-testid={`studio-ledger-delivery-status-${rowId}`}>{DELIVERY_STATUS_LABELS[deliveryStatus] || deliveryStatus}</span>
        </div>
        <label className="studio-ledger-delivery-select-field">
          <span className="studio-ledger-label">Set status</span>
          <select value={deliveryStatus} disabled={isDeliverySaving} onChange={(event) => onDeliveryStatusChange(shipment, event.target.value)} data-testid={`studio-ledger-delivery-select-${rowId}`}>
            {DELIVERY_STATUSES.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
          </select>
        </label>
        <button className="studio-ledger-delivery-toggle" type="button" onClick={() => onDeliveryStatusChange(shipment, deliveryStatus === "delivered" ? "in_transit" : "delivered")} disabled={isDeliverySaving} aria-busy={isDeliverySaving} data-testid={`studio-ledger-delivery-toggle-${rowId}`}>
          {isDeliverySaving ? "Saving…" : deliveryStatus === "delivered" ? "Undo delivered" : "Mark delivered"}
        </button>
        {isDeliverySaved && <span className="studio-ledger-delivery-feedback" data-testid={`studio-ledger-delivery-saved-${rowId}`}>Saved</span>}
        {deliveryErrorKey === outreachKey && <span className="studio-ledger-delivery-error" role="alert" data-testid={`studio-ledger-delivery-error-${rowId}`}>{deliveryError}</span>}
        <div className="studio-ledger-delivery-email">
          <button className="studio-ledger-preview-email-button" type="button" onClick={() => onPreviewEmail(shipment)} data-testid={`studio-ledger-preview-email-${rowId}`}><Eye size={13} strokeWidth={1.4} /> Preview buyer email</button>
          <button className="studio-ledger-delivery-email-button" type="button" onClick={() => onDeliveryEmail(shipment)} disabled={deliveryStatus !== "delivered" || isDeliveryEmailSaving} aria-busy={isDeliveryEmailSaving} data-testid={`studio-ledger-delivery-email-${rowId}`}>
            {isDeliveryEmailSaving ? "Sending delivered email…" : shipment.delivery_email_sent ? "Resend delivered email" : "Send delivered email"} <Send size={13} strokeWidth={1.4} />
          </button>
          {deliveryStatus !== "delivered" && <small data-testid={`studio-ledger-delivery-email-hint-${rowId}`}>Mark delivered first</small>}
          {isDeliveryEmailSaved && <span className="studio-ledger-delivery-feedback" data-testid={`studio-ledger-delivery-email-saved-${rowId}`}>Email sent</span>}
          {deliveryEmailErrorKey === outreachKey && <span className="studio-ledger-delivery-error" role="alert" data-testid={`studio-ledger-delivery-email-error-${rowId}`}>{deliveryEmailError}</span>}
        </div>
      </div>
      <div className="studio-ledger-timeline" data-testid={`studio-ledger-timeline-${rowId}`}>
        <span className="studio-ledger-label">Delivery timeline</span>
        <ol>
          {timeline.map((entry, timelineIndex) => <li key={`${entry.status}-${entry.at}-${timelineIndex}`} data-testid={`studio-ledger-timeline-entry-${rowId}-${timelineIndex}`}><span>{DELIVERY_STATUS_LABELS[entry.status] || entry.status}</span><small>{formatLedgerDate(entry.at)}</small></li>)}
        </ol>
      </div>
      <div className="studio-ledger-email-history" data-testid={`studio-ledger-email-history-${rowId}`}>
        <button className="studio-ledger-email-history-toggle" type="button" onClick={() => onEmailHistoryToggle(shipment)} aria-expanded={emailHistoryOpen} data-testid={`studio-ledger-email-history-toggle-${rowId}`}>{emailHistoryOpen ? "Hide email history" : "Show email history"} <Mail size={13} strokeWidth={1.4} /></button>
        {emailHistoryOpen && <div className="studio-ledger-email-history-panel"><label className="studio-ledger-email-history-filter"><span className="studio-ledger-label">Filter events</span><select value={emailHistoryFilter} onChange={(event) => onEmailHistoryFilterChange(shipment, event.target.value)} data-testid={`studio-ledger-email-history-filter-${rowId}`}>{EMAIL_HISTORY_FILTERS.map((filter) => <option key={filter.value} value={filter.value}>{filter.label}</option>)}</select></label>{isEmailHistoryLoading && <span data-testid={`studio-ledger-email-history-loading-${rowId}`}>Loading email history…</span>}{emailHistoryErrorKey === emailHistoryCacheKey && <span className="studio-ledger-delivery-error" role="alert" data-testid={`studio-ledger-email-history-error-${rowId}`}>{emailHistoryError}</span>}{!isEmailHistoryLoading && emailHistoryErrorKey !== emailHistoryCacheKey && emailHistory.length === 0 && <span data-testid={`studio-ledger-email-history-empty-${rowId}`}>No email events recorded.</span>}{!isEmailHistoryLoading && emailHistory.length > 0 && <ol>{emailHistory.map((event, historyIndex) => <li key={`${event.email_type}-${event.sent_at}-${historyIndex}`} data-testid={`studio-ledger-email-history-event-${rowId}-${historyIndex}`}><strong>{event.label}</strong><span>{event.recipient}</span><small>{formatLedgerDate(event.sent_at)} / {event.delivery_status}</small></li>)}</ol>}</div>}
      </div>
      <div className="studio-ledger-note-row">
        <label className="studio-ledger-note-field">
          <span className="studio-ledger-label">Outreach note</span>
          <input type="text" maxLength={240} value={noteDraft} onChange={(event) => setNoteDraft(event.target.value)} onBlur={() => onNoteBlur(shipment, noteDraft)} placeholder="Add a quiet note for follow-up" aria-label={`Outreach note for ${shipment.order_reference}`} data-testid={`studio-ledger-note-input-${rowId}`} />
        </label>
        <select className="studio-ledger-note-phrases" value="" onChange={(event) => { if (event.target.value) setNoteDraft(event.target.value); }} aria-label={`Quick phrases for ${shipment.order_reference}`} data-testid={`studio-ledger-note-phrases-${rowId}`}>
          <option value="">Quick phrase</option>
          {OUTREACH_NOTE_PHRASES.map((phrase) => <option key={phrase} value={phrase}>{phrase}</option>)}
        </select>
        <span className="studio-ledger-note-count" data-testid={`studio-ledger-note-count-${rowId}`}>{noteDraft.length}/240</span>
        {isNoteSaving && <span className="studio-ledger-note-feedback" data-testid={`studio-ledger-note-saving-${rowId}`}>Saving…</span>}
        {isNoteSaved && <span className="studio-ledger-note-feedback" data-testid={`studio-ledger-note-saved-${rowId}`}>Saved</span>}
        {noteErrorKey === outreachKey && <span className="studio-ledger-note-error" role="alert" data-testid={`studio-ledger-note-error-${rowId}`}>{noteError}</span>}
        {shipment.outreach_note_updated_at && <span className="studio-ledger-note-updated" data-testid={`studio-ledger-note-updated-${rowId}`}>Updated {formatLedgerDate(shipment.outreach_note_updated_at)}</span>}
      </div>
      <div className="studio-ledger-row-footer">
        <div className="studio-ledger-row-statuses">
          <span className="studio-ledger-status" data-testid={`studio-ledger-status-${rowId}`}>{shipment.status}</span>
          <label className="studio-ledger-outreach-field">
            <span className="studio-ledger-label">Outreach</span>
            <select value={shipment.outreach_status || "pending"} disabled={isSaving} onChange={(event) => onOutreachChange(shipment, event.target.value)} data-testid={`studio-ledger-outreach-select-${rowId}`}>
              {OUTREACH_STATUSES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          {isSaving && <span className="studio-ledger-outreach-feedback" data-testid={`studio-ledger-outreach-saving-${rowId}`}>Saving…</span>}
          {isSaved && <span className="studio-ledger-outreach-feedback" data-testid={`studio-ledger-outreach-saved-${rowId}`}>Saved</span>}
          {outreachErrorKey === outreachKey && <span className="studio-ledger-outreach-error" role="alert" data-testid={`studio-ledger-outreach-error-${rowId}`}>{outreachError}</span>}
          {shipment.outreach_updated_at && <span className="studio-ledger-outreach-updated" data-testid={`studio-ledger-outreach-updated-${rowId}`}>Updated {formatLedgerDate(shipment.outreach_updated_at)}</span>}
        </div>
        <span className="studio-ledger-recorded" data-testid={`studio-ledger-sent-at-${rowId}`}>Recorded {formatLedgerDate(shipment.sent_at)}</span>
      </div>
    </article>
  );
}

function ShipmentLedgerInsights({ insights, loading, activeCarrier, activeOutreachStatus, onCarrierDrilldown, onOutreachDrilldown }) {
  if (loading) return <div className="studio-ledger-insights-loading" data-testid="studio-ledger-insights-loading">Refreshing dispatch insights…</div>;
  const carrierCounts = insights?.by_carrier || {};
  const outreachCounts = insights?.by_outreach_status || {};
  return (
    <section className="studio-ledger-insights" data-testid="studio-ledger-insights" aria-label="Dispatch insights">
      <div className="studio-ledger-insight-total">
        <span className="studio-ledger-label">Matching sent updates</span>
        <strong data-testid="studio-ledger-insights-total">{insights?.total || 0}</strong>
      </div>
      <div className="studio-ledger-insight-group" data-testid="studio-ledger-insights-outreach">
        <span className="studio-ledger-label">Outreach</span>
        <div className="studio-ledger-insight-values">
          {OUTREACH_STATUSES.map((status) => {
            const count = outreachCounts[status.value] || 0;
            return <button type="button" key={status.value} className={`studio-ledger-insight-button${activeOutreachStatus === status.value ? " is-active" : ""}`} onClick={() => onOutreachDrilldown(status.value)} disabled={!count} aria-pressed={activeOutreachStatus === status.value} data-testid={`studio-ledger-insights-status-${status.value}`}><b>{status.label}</b> {count}</button>;
          })}
        </div>
      </div>
      <div className="studio-ledger-insight-group" data-testid="studio-ledger-insights-carriers">
        <span className="studio-ledger-label">Carriers in search</span>
        <div className="studio-ledger-insight-values">
          {["USPS", "UPS", "FedEx", "DHL", "Other"].map((carrier) => {
            const count = carrierCounts[carrier] || 0;
            return <button type="button" key={carrier} className={`studio-ledger-insight-button${activeCarrier === carrier ? " is-active" : ""}`} onClick={() => onCarrierDrilldown(carrier)} disabled={!count} aria-pressed={activeCarrier === carrier} data-testid={`studio-ledger-insights-carrier-${carrier.toLowerCase()}`}><b>{carrier}</b> {count}</button>;
          })}
        </div>
      </div>
    </section>
  );
}

function PendingFollowupPanel({ summary, loading, error, onRetry, onReview }) {
  if (loading) return <div className="studio-pending-loading" data-testid="studio-pending-loading">Checking pending follow-up…</div>;
  if (error) {
    return (
      <div className="studio-pending-error" role="alert" data-testid="studio-pending-error">
        <span>{error}</span>
        <button type="button" onClick={onRetry} data-testid="studio-pending-retry-button">Retry pending queue</button>
      </div>
    );
  }
  return (
    <section className="studio-pending-panel" data-testid="studio-pending-panel">
      <div className="studio-pending-header">
        <div>
          <p className="section-kicker">Collector follow-up</p>
          <h3 data-testid="studio-pending-title">{summary.total} pending {summary.total === 1 ? "shipment" : "shipments"}</h3>
        </div>
        <button type="button" onClick={onReview} disabled={!summary.total} data-testid="studio-pending-review-button">Review pending <ArrowRight size={14} strokeWidth={1.4} /></button>
      </div>
      {summary.total > 0 ? (
        <div className="studio-pending-list" data-testid="studio-pending-list">
          {summary.shipments.slice(0, 3).map((shipment, index) => (
            <div className="studio-pending-row" key={`${shipment.order_reference}-${index}`} data-testid={`studio-pending-row-${index + 1}`}>
              <strong data-testid={`studio-pending-order-${index + 1}`}>{shipment.order_reference}</strong>
              <span data-testid={`studio-pending-buyer-${index + 1}`}>{shipment.buyer_email}</span>
              <span data-testid={`studio-pending-date-${index + 1}`}>{shipment.shipped_date}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="studio-pending-empty" data-testid="studio-pending-empty">Nothing is waiting for a follow-up.</p>
      )}
    </section>
  );
}

function SavedLedgerViewItem({ view, active, pendingDelete, onUse, onDeleteRequest, onDeleteConfirm, onDeleteCancel }) {
  return (
    <div className={`studio-saved-view-item${active ? " is-active" : ""}`} data-testid={`studio-saved-view-item-${view.id}`}>
      <button className="studio-saved-view-item-button" type="button" onClick={() => onUse(view)} aria-pressed={active} data-testid={`studio-saved-view-item-button-${view.id}`}>
        <span>{view.name}</span>
        <small>{view.search || "All records"} · {view.carrier || "All carriers"}</small>
      </button>
      {active && <span className="studio-saved-view-active-badge" data-testid="studio-saved-view-active-badge">Active</span>}
      {pendingDelete ? (
        <span className="studio-saved-view-delete-confirmation" data-testid={`studio-saved-view-delete-confirmation-${view.id}`}>
          <button type="button" onClick={() => onDeleteConfirm(view.id)} data-testid={`studio-saved-view-delete-confirm-${view.id}`}>Delete</button>
          <button type="button" onClick={onDeleteCancel} data-testid={`studio-saved-view-delete-cancel-${view.id}`}>Keep</button>
        </span>
      ) : (
        <button className="studio-saved-view-delete-button" type="button" onClick={() => onDeleteRequest(view.id)} aria-label={`Delete saved view ${view.name}`} data-testid={`studio-saved-view-delete-button-${view.id}`}>
          <Trash2 size={14} strokeWidth={1.3} />
        </button>
      )}
    </div>
  );
}

function SavedLedgerViewBar({ views, loading, error, onRetry, activeViewId, onUse, onReplace, onDeleteRequest, onDeleteConfirm, onDeleteCancel, pendingDeleteId, creating, onStartCreate, createName, onCreateNameChange, onCreate, onCancelCreate, saving, savedFeedback, saveError, filtersDirty }) {
  if (loading) return <div className="studio-saved-view-loading" data-testid="studio-saved-view-loading">Checking saved views…</div>;
  if (error) {
    return (
      <div className="studio-saved-view-error" role="alert" data-testid="studio-saved-view-error">
        <span>{error}</span>
        <button type="button" onClick={onRetry} data-testid="studio-saved-view-retry-button">Retry saved views</button>
      </div>
    );
  }
  const activeView = views.find((view) => view.id === activeViewId);
  return (
    <section className="studio-saved-views-container" role="region" aria-label="Saved shipment filter views" data-testid="studio-saved-views-container">
      <div className="studio-saved-views-header">
        <div>
          <p className="section-kicker">Saved views / dispatch filters</p>
          <span className="studio-saved-views-count" data-testid="studio-saved-views-count">{views.length} {views.length === 1 ? "view" : "views"}</span>
        </div>
        <button className="studio-saved-view-create-button" type="button" onClick={onStartCreate} disabled={creating} data-testid="studio-saved-view-create-button">
          <Bookmark size={14} strokeWidth={1.4} /> Save current view
        </button>
      </div>
      {views.length ? (
        <div className="studio-saved-views-list" data-testid="studio-saved-views-list">
          {views.map((view) => (
            <SavedLedgerViewItem key={view.id} view={view} active={view.id === activeViewId} pendingDelete={view.id === pendingDeleteId} onUse={onUse} onDeleteRequest={onDeleteRequest} onDeleteConfirm={onDeleteConfirm} onDeleteCancel={onDeleteCancel} />
          ))}
        </div>
      ) : (
        <p className="studio-saved-view-empty" data-testid="studio-saved-view-empty-state">No saved views yet. Save frequent search and carrier filters for one-tap recall.</p>
      )}
      {creating && (
        <form className="studio-saved-view-create-form" onSubmit={(event) => { event.preventDefault(); onCreateNameChange(createName.trim()); onCreate(createName.trim()); }} onKeyDown={(event) => { if (event.key === "Escape") onCancelCreate(); }} data-testid="studio-saved-view-create-form">
          <label htmlFor="studio-saved-view-name-input">Name this view</label>
          <input id="studio-saved-view-name-input" type="text" value={createName} onChange={(event) => onCreateNameChange(event.target.value)} placeholder="e.g. USPS pending" maxLength={40} autoFocus data-testid="studio-saved-view-name-input" />
          <button className="studio-saved-view-save-submit" type="submit" disabled={saving || !createName.trim()} data-testid="studio-saved-view-save-submit-button">
            {saving ? <><LoaderCircle className="checkout-spinner" size={14} /> Saving…</> : "Save"}
          </button>
          <button className="studio-saved-view-cancel-button" type="button" onClick={onCancelCreate} disabled={saving} data-testid="studio-saved-view-cancel-button">Cancel</button>
        </form>
      )}
      {activeView && (
        <div className="studio-saved-view-bar" data-testid="studio-saved-view-bar">
          <div className="studio-saved-view-summary">
            <span className="studio-saved-view-badge" data-testid="studio-saved-view-badge">Active view</span>
            <span data-testid="studio-saved-view-summary">{activeView.name} · {activeView.search || "All records"} · {activeView.carrier || "All carriers"}</span>
          </div>
          <div className="studio-saved-view-actions">
            <button type="button" onClick={() => onUse(activeView)} data-testid="studio-saved-view-use-button"><Bookmark size={14} strokeWidth={1.4} /> Use saved view</button>
            {filtersDirty && <button type="button" onClick={() => onReplace(activeView)} disabled={saving} data-testid="studio-saved-view-replace-button">Replace {activeView.name}</button>}
            <button type="button" onClick={() => onDeleteRequest(activeView.id)} data-testid="studio-saved-view-clear-button">Delete active</button>
          </div>
        </div>
      )}
      {savedFeedback && <span className="studio-saved-view-saved-feedback" data-testid="studio-saved-view-saved-feedback">{savedFeedback}</span>}
      {saveError && <span className="studio-saved-view-inline-error" role="alert" data-testid="studio-saved-view-inline-error">{saveError}</span>}
    </section>
  );
}

function ShipmentLedgerFilters({ searchInput, carrier, outreachStatus, shippedFromInput, shippedToInput, onSearchChange, onCarrierChange, onOutreachStatusChange, onShippedFromChange, onShippedToChange, onDatePreset, onSearch, onExport, exporting, exportError }) {
  return (
    <form className="studio-ledger-filters" onSubmit={(event) => { event.preventDefault(); onSearch(); }} data-testid="studio-ledger-filters">
      <div className="studio-ledger-date-presets" data-testid="studio-ledger-date-presets">
        <span className="studio-ledger-label">Quick window</span>
        {DATE_PRESETS.map((preset) => {
          const range = getDatePresetRange(preset.value);
          const active = shippedFromInput === range.from && shippedToInput === range.to;
          return <button type="button" key={preset.value} className={active ? "is-active" : ""} onClick={() => onDatePreset(preset.value)} aria-pressed={active} data-testid={`studio-ledger-date-preset-${preset.value}`}>{preset.label}</button>;
        })}
      </div>
      <label className="studio-ledger-filter-field studio-ledger-search-field">
        <span className="studio-ledger-label">Find sent record</span>
        <input type="search" value={searchInput} onChange={(event) => onSearchChange(event.target.value)} placeholder="Order, buyer, or tracking" aria-label="Search order references, buyer emails, or tracking numbers" data-testid="studio-ledger-search-input" />
      </label>
      <label className="studio-ledger-filter-field">
        <span className="studio-ledger-label">Carrier</span>
        <select value={carrier} onChange={(event) => onCarrierChange(event.target.value)} aria-label="Filter shipment history by carrier" data-testid="studio-ledger-carrier-filter">
          <option value="">All carriers</option>
          <option value="USPS">USPS</option>
          <option value="UPS">UPS</option>
          <option value="FedEx">FedEx</option>
          <option value="DHL">DHL</option>
          <option value="Other">Other</option>
        </select>
      </label>
      <label className="studio-ledger-filter-field">
        <span className="studio-ledger-label">Outreach</span>
        <select value={outreachStatus} onChange={(event) => onOutreachStatusChange(event.target.value)} aria-label="Filter shipment history by outreach status" data-testid="studio-ledger-outreach-filter">
          <option value="">All outreach</option>
          <option value="pending">Follow-up pending</option>
          <option value="contacted">Contacted</option>
          <option value="complete">Complete</option>
        </select>
      </label>
      <label className="studio-ledger-filter-field studio-ledger-date-field">
        <span className="studio-ledger-label">Shipped from</span>
        <input type="date" value={shippedFromInput} onChange={(event) => onShippedFromChange(event.target.value)} aria-label="Shipped from date" data-testid="studio-ledger-shipped-from" />
      </label>
      <label className="studio-ledger-filter-field studio-ledger-date-field">
        <span className="studio-ledger-label">Shipped to</span>
        <input type="date" value={shippedToInput} onChange={(event) => onShippedToChange(event.target.value)} aria-label="Shipped to date" data-testid="studio-ledger-shipped-to" />
      </label>
      <button className="studio-ledger-search" type="submit" data-testid="studio-ledger-search-button"><Search size={15} strokeWidth={1.4} /> Search ledger</button>
      <div className="studio-ledger-export-wrap">
        <button className="studio-ledger-export" type="button" onClick={onExport} disabled={exporting} aria-busy={exporting} data-testid="studio-ledger-export-button">
          {exporting ? <><LoaderCircle className="checkout-spinner" size={15} /> Preparing CSV…</> : <><Download size={15} strokeWidth={1.4} /> Download CSV</>}
        </button>
        {exportError && <span className="studio-ledger-export-error" role="alert" data-testid="studio-ledger-export-error">{exportError}</span>}
      </div>
    </form>
  );
}

function ShipmentLedger({ shipments, total, loading, loadingMore, hasMore, error, onRetry, onLoadMore, searchInput, carrier, outreachStatus, shippedFromInput, shippedToInput, onSearchChange, onCarrierChange, onOutreachStatusChange, onShippedFromChange, onShippedToChange, onDatePreset, onSearch, onExport, exporting, exportError, onOutreachChange, outreachSavingKey, outreachSavedKey, outreachErrorKey, outreachError, onNoteBlur, noteSavingKey, noteSavedKey, noteErrorKey, noteError, onDeliveryStatusChange, deliverySavingKey, deliverySavedKey, deliveryErrorKey, deliveryError, onDeliveryEmail, deliveryEmailSavingKey, deliveryEmailSavedKey, deliveryEmailErrorKey, deliveryEmailError, onCopyTracking, trackingCopiedKey, onPreviewEmail, onEmailHistoryToggle, onEmailHistoryFilterChange, emailHistoryByOrder, emailHistoryFilter, emailHistoryOpenKey, emailHistoryLoadingKey, emailHistoryErrorKey, emailHistoryError, insights, insightsLoading, activeCarrier, activeOutreachStatus, onCarrierDrilldown, onOutreachDrilldown, pendingSummary, pendingLoading, pendingError, onRetryPending, onReviewPending, savedViews, savedViewsLoading, savedViewsError, onRetrySavedViews, activeSavedViewId, onUseSavedView, onReplaceSavedView, onRequestDeleteSavedView, onConfirmDeleteSavedView, onCancelDeleteSavedView, pendingDeleteSavedViewId, creatingSavedView, onStartCreateSavedView, savedViewName, onSavedViewNameChange, onCreateSavedView, onCancelCreateSavedView, savedViewSaving, savedViewFeedback, savedViewSaveError, savedViewFiltersDirty }) {
  const hasRows = shipments.length > 0;
  const progressTotal = total || shipments.length;
  return (
    <section className="studio-ledger" data-testid="studio-shipment-ledger">
      <div className="studio-ledger-header" data-testid="studio-ledger-header">
        <div>
          <p className="section-kicker">Dispatch archive / sent record</p>
          <h2 data-testid="studio-ledger-title">Quiet ledger<br /><em>sent prints.</em></h2>
        </div>
        <span className="studio-ledger-count" data-testid="studio-ledger-count">{String(progressTotal).padStart(2, "0")} dispatches recorded</span>
      </div>
      <SavedLedgerViewBar views={savedViews} loading={savedViewsLoading} error={savedViewsError} onRetry={onRetrySavedViews} activeViewId={activeSavedViewId} onUse={onUseSavedView} onReplace={onReplaceSavedView} onDeleteRequest={onRequestDeleteSavedView} onDeleteConfirm={onConfirmDeleteSavedView} onDeleteCancel={onCancelDeleteSavedView} pendingDeleteId={pendingDeleteSavedViewId} creating={creatingSavedView} onStartCreate={onStartCreateSavedView} createName={savedViewName} onCreateNameChange={onSavedViewNameChange} onCreate={onCreateSavedView} onCancelCreate={onCancelCreateSavedView} saving={savedViewSaving} savedFeedback={savedViewFeedback} saveError={savedViewSaveError} filtersDirty={savedViewFiltersDirty} />
      <ShipmentLedgerFilters searchInput={searchInput} carrier={carrier} outreachStatus={outreachStatus} shippedFromInput={shippedFromInput} shippedToInput={shippedToInput} onSearchChange={onSearchChange} onCarrierChange={onCarrierChange} onOutreachStatusChange={onOutreachStatusChange} onShippedFromChange={onShippedFromChange} onShippedToChange={onShippedToChange} onDatePreset={onDatePreset} onSearch={onSearch} onExport={onExport} exporting={exporting} exportError={exportError} />
      <PendingFollowupPanel summary={pendingSummary} loading={pendingLoading} error={pendingError} onRetry={onRetryPending} onReview={onReviewPending} />
      <ShipmentLedgerInsights insights={insights} loading={insightsLoading} activeCarrier={activeCarrier} activeOutreachStatus={activeOutreachStatus} onCarrierDrilldown={onCarrierDrilldown} onOutreachDrilldown={onOutreachDrilldown} />
      {loading && (
        <div className="studio-ledger-loading" data-testid="studio-ledger-loading">
          <LoaderCircle className="checkout-spinner" size={17} /> Consulting studio ledger…
        </div>
      )}
      {!loading && error && (
        <div className={`studio-ledger-error${hasRows ? " studio-ledger-error-inline" : ""}`} role="alert" data-testid="studio-ledger-error">
          <span>{error}</span>
          <button className="studio-ledger-retry" type="button" onClick={onRetry} data-testid="studio-ledger-retry-button">{hasRows ? "Retry older updates" : "Retry ledger lookup"}</button>
        </div>
      )}
      {!loading && !error && !hasRows && (
        <div className="studio-ledger-empty" data-testid="studio-ledger-empty">
          <Truck size={19} strokeWidth={1.3} />
          <strong>The ledger is still waiting.</strong>
          <span>When shipments leave the desk above, their permanent studio records will appear here chronologically.</span>
        </div>
      )}
      {!loading && hasRows && (
        <>
          <div className="studio-ledger-list" data-testid="studio-ledger-list">
            {shipments.map((shipment, index) => <ShipmentLedgerRow key={`${shipment.order_reference}-${index}`} shipment={shipment} index={index} onOutreachChange={onOutreachChange} outreachSavingKey={outreachSavingKey} outreachSavedKey={outreachSavedKey} outreachErrorKey={outreachErrorKey} outreachError={outreachError} onNoteBlur={onNoteBlur} noteSavingKey={noteSavingKey} noteSavedKey={noteSavedKey} noteErrorKey={noteErrorKey} noteError={noteError} onDeliveryStatusChange={onDeliveryStatusChange} deliverySavingKey={deliverySavingKey} deliverySavedKey={deliverySavedKey} deliveryErrorKey={deliveryErrorKey} deliveryError={deliveryError} onDeliveryEmail={onDeliveryEmail} deliveryEmailSavingKey={deliveryEmailSavingKey} deliveryEmailSavedKey={deliveryEmailSavedKey} deliveryEmailErrorKey={deliveryEmailErrorKey} deliveryEmailError={deliveryEmailError} onCopyTracking={onCopyTracking} trackingCopiedKey={trackingCopiedKey} onPreviewEmail={onPreviewEmail} onEmailHistoryToggle={onEmailHistoryToggle} onEmailHistoryFilterChange={onEmailHistoryFilterChange} emailHistoryByOrder={emailHistoryByOrder} emailHistoryFilter={emailHistoryFilter} emailHistoryOpenKey={emailHistoryOpenKey} emailHistoryLoadingKey={emailHistoryLoadingKey} emailHistoryErrorKey={emailHistoryErrorKey} emailHistoryError={emailHistoryError} />)}
          </div>
          <div className="studio-ledger-pagination" data-testid="studio-ledger-pagination">
            <span className="studio-ledger-progress" aria-live="polite" data-testid="studio-ledger-progress">Showing {shipments.length} of {progressTotal} sent updates</span>
            {hasMore ? (
              <button className="studio-ledger-older" type="button" onClick={onLoadMore} disabled={loadingMore} aria-busy={loadingMore} data-testid="studio-ledger-older-updates-button">
                {loadingMore ? <><LoaderCircle className="checkout-spinner" size={15} /> Consulting older updates…</> : <>Older updates <ArrowDownRight size={15} strokeWidth={1.4} /></>}
              </button>
            ) : (
              <span className="studio-ledger-end" data-testid="studio-ledger-end">That is the whole ledger.</span>
            )}
          </div>
        </>
      )}
    </section>
  );
}



function StudioPaidOrderRow({ order, index, onPackingToggle, packingSavingKey, packingSavedKey, packingErrorKey, packingError }) {
  const rowId = `${slugify(order.order_reference || "paid-order")}-${index + 1}`;
  const address = [order.address_line1, order.address_line2, [order.city, order.state, order.postal_code].filter(Boolean).join(", "), order.country].filter(Boolean).join(" / ") || "Shipping address not provided";
  const notificationSent = order.owner_notification_status === "sent";
  const packingKey = order.session_id || order.order_reference || rowId;
  const packingReady = Boolean(order.packing_ready);
  const isPackingSaving = packingSavingKey === packingKey;
  const isPackingSaved = packingSavedKey === packingKey;
  return (
    <article className="studio-paid-order" data-testid={`studio-paid-order-row-${rowId}`}>
      <div className="studio-paid-order-topline">
        <div>
          <span className="studio-ledger-label">Order / receipt number</span>
          <strong data-testid={`studio-paid-order-reference-${rowId}`}>{order.order_reference || "Pending assignment"}</strong>
        </div>
        <div className="studio-paid-order-status-wrap">
          <span className="studio-ledger-label">Payment</span>
          <span className="studio-paid-order-status" data-testid={`studio-paid-order-status-${rowId}`}>{order.payment_status || "paid"}</span>
          <small data-testid={`studio-paid-order-paid-at-${rowId}`}>{order.purchase_date_time || formatLedgerDate(order.paid_at)}</small>
        </div>
      </div>
      <div className="studio-paid-order-grid">
        <div>
          <span className="studio-ledger-label">Collector</span>
          <strong data-testid={`studio-paid-order-buyer-name-${rowId}`}>{order.shipping_name || "Name not provided"}</strong>
          <span className="studio-paid-order-value" data-testid={`studio-paid-order-email-${rowId}`}>{order.email || "Email not provided"}</span>
          <span className="studio-paid-order-value" data-testid={`studio-paid-order-phone-${rowId}`}>{order.shipping_phone || "Phone not provided"}</span>
        </div>
        <div>
          <span className="studio-ledger-label">Ship to</span>
          <span className="studio-paid-order-value" data-testid={`studio-paid-order-address-${rowId}`}>{address}</span>
        </div>
      </div>
      <div className="studio-paid-order-bottomline">
        <div>
          <span className="studio-ledger-label">Prints</span>
          <span className="studio-paid-order-value" data-testid={`studio-paid-order-items-${rowId}`}>
            {(order.items || []).map((item) => `${item.title} × ${item.quantity}`).join(" · ") || "No item details"}
          </span>
        </div>
        <div className="studio-paid-order-total-wrap">
          <span className="studio-ledger-label">Total</span>
          <strong data-testid={`studio-paid-order-total-${rowId}`}>{formatUSD(Number(order.amount || 0))}</strong>
        </div>
      </div>
      <span className={`studio-paid-order-notification${notificationSent ? " is-sent" : ""}`} data-testid={`studio-paid-order-notification-${rowId}`}>
        {notificationSent ? "Shipment notification sent" : "Shipment notification pending"}
      </span>
      <div className="studio-paid-order-packing">
        <div className="studio-paid-order-packing-state">
          <span className="studio-ledger-label">Packing</span>
          <strong data-testid={`studio-paid-order-packing-status-${rowId}`}>{packingReady ? "Ready to pack" : "Not marked"}</strong>
        </div>
        <button
          className={`studio-paid-order-packing-toggle${packingReady ? " is-ready" : ""}`}
          type="button"
          onClick={() => onPackingToggle(order)}
          disabled={isPackingSaving}
          aria-busy={isPackingSaving}
          aria-pressed={packingReady}
          data-testid={`studio-paid-order-packing-toggle-${rowId}`}
        >
          {isPackingSaving ? "Saving…" : packingReady ? "Ready to pack" : "Mark ready to pack"}
        </button>
        {isPackingSaved && <span className="studio-paid-order-packing-feedback" data-testid={`studio-paid-order-packing-saved-${rowId}`}>Saved</span>}
        {packingErrorKey === packingKey && <span className="studio-paid-order-packing-error" role="alert" data-testid={`studio-paid-order-packing-error-${rowId}`}>{packingError}</span>}
      </div>
    </article>
  );
}

function StudioPaidOrders({ orders, total, loading, loadingMore, hasMore, error, onRetry, onLoadMore, searchInput, countryInput, onSearchChange, onCountryChange, onSearch, onClear, onExport, exporting, exportError, onPackingToggle, packingSavingKey, packingSavedKey, packingErrorKey, packingError }) {
  const hasOrders = orders.length > 0;
  return (
    <section className="studio-paid-orders" data-testid="studio-paid-orders">
      <div className="studio-paid-orders-header">
        <div>
          <p className="section-kicker">Paid orders / shipping queue</p>
          <h2 data-testid="studio-paid-orders-title">Ready for<br /><em>the journey.</em></h2>
        </div>
        <span className="studio-ledger-count" data-testid="studio-paid-orders-count">{total} paid {total === 1 ? "order" : "orders"}</span>
      </div>
      <p className="studio-paid-orders-intro">Every paid print order arrives here with the collector’s full delivery details before you send the tracking update.</p>
      <form className="studio-paid-orders-filters" onSubmit={(event) => { event.preventDefault(); onSearch(); }} data-testid="studio-paid-orders-filters">
        <label>
          <span className="studio-ledger-label">Find paid order</span>
          <input type="search" value={searchInput} onChange={(event) => onSearchChange(event.target.value)} placeholder="Name, order, email, or city" data-testid="studio-paid-orders-search-input" />
        </label>
        <label>
          <span className="studio-ledger-label">Destination country</span>
          <select value={countryInput} onChange={(event) => onCountryChange(event.target.value)} aria-label="Filter paid orders by destination country" data-testid="studio-paid-orders-country-filter">
            {PAID_ORDER_COUNTRIES.map((country) => <option key={country.value || "all"} value={country.value}>{country.label}</option>)}
          </select>
        </label>
        <button className="studio-paid-orders-search-button" type="submit" data-testid="studio-paid-orders-search-button">Search orders</button>
        <button className="studio-paid-orders-clear-button" type="button" onClick={onClear} data-testid="studio-paid-orders-clear-button">Clear</button>
        <button className="studio-paid-orders-export-button" type="button" onClick={onExport} disabled={exporting} aria-busy={exporting} data-testid="studio-paid-orders-export-button">
          {exporting ? "Preparing CSV…" : "Download packing CSV"}
        </button>
        {exportError && <span className="studio-paid-orders-export-error" role="alert" data-testid="studio-paid-orders-export-error">{exportError}</span>}
      </form>
      {loading && <div className="studio-paid-orders-loading" data-testid="studio-paid-orders-loading"><LoaderCircle className="checkout-spinner" size={17} /> Loading paid orders…</div>}
      {!loading && error && (
        <div className={`studio-paid-orders-error${hasOrders ? " studio-paid-orders-error-inline" : ""}`} role="alert" data-testid="studio-paid-orders-error">
          <span>{error}</span>
          <button type="button" onClick={() => onRetry()} data-testid="studio-paid-orders-retry-button">{hasOrders ? "Retry older paid orders" : "Retry paid orders"}</button>
        </div>
      )}
      {!loading && !error && !hasOrders && <div className="studio-paid-orders-empty" data-testid="studio-paid-orders-empty">No paid print orders yet.</div>}
      {!loading && hasOrders && (
        <div className="studio-paid-orders-list" data-testid="studio-paid-orders-list">
          {orders.map((order, index) => <StudioPaidOrderRow key={`${order.session_id || order.order_reference}-${index}`} order={order} index={index} onPackingToggle={onPackingToggle} packingSavingKey={packingSavingKey} packingSavedKey={packingSavedKey} packingErrorKey={packingErrorKey} packingError={packingError} />)}
        </div>
      )}
      {!loading && !error && hasOrders && (
        <div className="studio-paid-orders-pagination" data-testid="studio-paid-orders-pagination">
          <span className="studio-paid-orders-progress" aria-live="polite" data-testid="studio-paid-orders-progress">Showing {orders.length} of {total} paid orders</span>
          {hasMore ? (
            <button className="studio-paid-orders-older" type="button" onClick={onLoadMore} disabled={loadingMore} aria-busy={loadingMore} data-testid="studio-paid-orders-older-button">
              {loadingMore ? <><LoaderCircle className="checkout-spinner" size={15} /> Loading older paid orders…</> : <>Older paid orders <ArrowDownRight size={15} strokeWidth={1.4} /></>}
            </button>
          ) : (
            <span className="studio-paid-orders-end" data-testid="studio-paid-orders-end">All paid orders are loaded</span>
          )}
        </div>
      )}
    </section>
  );
}

function StudioCatalog() {
  return (
    <section id="studio-catalog" className="studio-catalog" data-testid="studio-catalog">
      <div className="studio-catalog-header">
        <div>
          <p className="section-kicker">Private catalog / 2026</p>
          <h2>Descendants of<br /><em>the Sun.</em></h2>
        </div>
        <span className="studio-ledger-count" data-testid="studio-catalog-count">{descendantsCatalogWorks.length} works</span>
      </div>
      <div className="studio-catalog-grid">
        {descendantsCatalogWorks.map((work) => (
          <article className="studio-catalog-card" key={work.id} data-testid={`studio-catalog-work-${work.id}`}>
            <img src={work.imageUrl} alt={work.imageAlt} loading="lazy" />
            <div>
              <p className="studio-ledger-label">{work.series}</p>
              <h3>{work.title}</h3>
              <span>{work.dimensions} / {work.medium} / {work.year}</span>
              <strong>{work.status}</strong>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function StudioDisputeReminders({ reminders, loading, error, onRetry }) {
  const today = new Date();
  const localToday = [today.getFullYear(), String(today.getMonth() + 1).padStart(2, "0"), String(today.getDate()).padStart(2, "0")].join("-");
  return (
    <section className="studio-dispute-reminders" data-testid="studio-dispute-reminders">
      <div className="studio-dispute-reminders-header"><div><p className="section-kicker">Today / collector care</p><h2>Follow-ups<br /><em>to remember.</em></h2></div><span className="studio-ledger-count" data-testid="studio-dispute-reminders-count">{reminders.length} due</span></div>
      {loading && <div className="studio-dispute-reminders-loading" data-testid="studio-dispute-reminders-loading"><LoaderCircle className="checkout-spinner" size={17} /> Checking follow-ups…</div>}
      {!loading && error && <div className="studio-dispute-reminders-error" role="alert" data-testid="studio-dispute-reminders-error"><span>{error}</span><button type="button" onClick={onRetry} data-testid="studio-dispute-reminders-retry">Retry</button></div>}
      {!loading && !error && reminders.length === 0 && <div className="studio-dispute-reminders-empty" data-testid="studio-dispute-reminders-empty">No follow-ups are due today.</div>}
      {!loading && !error && reminders.length > 0 && <div className="studio-dispute-reminders-list" data-testid="studio-dispute-reminders-list">{reminders.map((reminder) => <a className="studio-dispute-reminder" href="#studio-disputes" key={reminder.dispute_id} data-testid={`studio-dispute-reminder-${reminder.dispute_id}`}><div><strong>{reminder.order_reference}</strong><span>{reminder.name} / {reminder.email}</span></div><span className={reminder.next_contact_date < localToday ? "is-overdue" : ""}>{reminder.next_contact_date < localToday ? "Overdue" : "Due today"}<small>{reminder.next_contact_date}</small></span></a>)}</div>}
    </section>
  );
}

function StudioDisputes({ disputes, loading, error, onRetry, onStatusChange, savingKey, savedKey, errorKey, errorMessage, filterStatus, onFilterChange, searchInput, onSearchChange, onSearch, onClear, onFollowUpChange, followUpSavingKey, followUpSavedKey, followUpErrorKey, followUpError }) {
  return (
    <section className="studio-disputes" id="studio-disputes" data-testid="studio-disputes">
      <div className="studio-disputes-header">
        <div><p className="section-kicker">Collector care / private log</p><h2>Disputes<br /><em>to tend.</em></h2></div>
        <div className="studio-disputes-filter-wrap"><div className="studio-disputes-search"><label><span className="studio-ledger-label">Find dispute</span><input type="search" value={searchInput} onChange={(event) => onSearchChange(event.target.value)} placeholder="Order, collector, or email" data-testid="studio-disputes-search-input" /></label><button type="button" onClick={onSearch} data-testid="studio-disputes-search-button">Search</button><button type="button" onClick={onClear} data-testid="studio-disputes-clear-button">Clear</button></div><span className="studio-ledger-count" data-testid="studio-disputes-count">{disputes.length} dispute record{disputes.length === 1 ? "" : "s"}</span><label><span className="studio-ledger-label">Show</span><select value={filterStatus} onChange={(event) => onFilterChange(event.target.value)} data-testid="studio-disputes-filter"><option value="open">Open</option><option value="completed">Completed</option><option value="all">All</option></select></label></div>
      </div>
      {loading && <div className="studio-disputes-loading" data-testid="studio-disputes-loading"><LoaderCircle className="checkout-spinner" size={17} /> Loading disputes…</div>}
      {!loading && error && <div className="studio-disputes-error" role="alert" data-testid="studio-disputes-error"><span>{error}</span><button type="button" onClick={onRetry} data-testid="studio-disputes-retry-button">Retry disputes</button></div>}
      {!loading && !error && disputes.length === 0 && <div className="studio-disputes-empty" data-testid="studio-disputes-empty">No disputes in this view.</div>}
      {!loading && !error && disputes.length > 0 && <div className="studio-disputes-list" data-testid="studio-disputes-list">
        {disputes.map((dispute) => {
          const isCompleted = dispute.status === "completed";
          const saving = savingKey === dispute.dispute_id;
          const saved = savedKey === dispute.dispute_id;
          const followUpSaving = followUpSavingKey === dispute.dispute_id;
          const followUpSaved = followUpSavedKey === dispute.dispute_id;
          const overdue = isFollowUpOverdue(dispute.next_contact_date, dispute.status);
          return (
            <article className={`studio-dispute-row${isCompleted ? " is-completed" : ""}`} key={dispute.dispute_id} data-testid={`studio-dispute-row-${dispute.dispute_id}`}>
              <div className="studio-dispute-row-topline"><div><span className="studio-ledger-label">Order / receipt number</span><strong data-testid={`studio-dispute-order-${dispute.dispute_id}`}>{dispute.order_reference}</strong></div><span className={`studio-dispute-status studio-dispute-status-${dispute.status}`} data-testid={`studio-dispute-status-${dispute.dispute_id}`}>{isCompleted ? "Completed" : "Open"}</span></div>
              <div className="studio-dispute-grid"><div><span className="studio-ledger-label">Collector</span><strong data-testid={`studio-dispute-name-${dispute.dispute_id}`}>{dispute.name}</strong><span data-testid={`studio-dispute-email-${dispute.dispute_id}`}>{dispute.email}</span></div><div><span className="studio-ledger-label">Delivery answer</span><span data-testid={`studio-dispute-delivery-${dispute.dispute_id}`}>{dispute.delivery_status === "delivered" ? "Buyer says delivered" : "Buyer says not delivered"}</span><small data-testid={`studio-dispute-submitted-${dispute.dispute_id}`}>Submitted {formatLedgerDate(dispute.submitted_at)}</small></div></div>
              <div className="studio-dispute-reason"><span className="studio-ledger-label">Reason</span><p data-testid={`studio-dispute-reason-${dispute.dispute_id}`}>{dispute.reason}</p></div>
              <div className="studio-dispute-follow-up"><label><span className="studio-ledger-label">Next contact</span><input type="date" value={dispute.next_contact_date || ""} onChange={(event) => onFollowUpChange(dispute, event.target.value)} disabled={followUpSaving} data-testid={`studio-dispute-next-contact-${dispute.dispute_id}`} /></label><button type="button" onClick={() => onFollowUpChange(dispute, "")} disabled={!dispute.next_contact_date || followUpSaving} data-testid={`studio-dispute-clear-contact-${dispute.dispute_id}`}>Clear date</button>{overdue && <span className="studio-dispute-overdue" data-testid={`studio-dispute-overdue-${dispute.dispute_id}`}>Overdue</span>}{followUpSaving && <span className="studio-dispute-feedback" data-testid={`studio-dispute-follow-up-saving-${dispute.dispute_id}`}>Saving…</span>}{followUpSaved && <span className="studio-dispute-feedback" data-testid={`studio-dispute-follow-up-saved-${dispute.dispute_id}`}>Saved</span>}{followUpErrorKey === dispute.dispute_id && <span className="studio-dispute-error" role="alert" data-testid={`studio-dispute-follow-up-error-${dispute.dispute_id}`}>{followUpError}</span>}</div>
              <div className="studio-dispute-footer"><span className="studio-dispute-email-status" data-testid={`studio-dispute-email-status-${dispute.dispute_id}`}>{dispute.buyer_ack_email_sent && dispute.owner_notification_email_sent ? "Buyer and studio emails sent" : "Email follow-up needs attention"}</span><button className="studio-dispute-complete-button" type="button" onClick={() => onStatusChange(dispute)} disabled={saving} aria-busy={saving} data-testid={`studio-dispute-complete-button-${dispute.dispute_id}`}>{saving ? "Saving…" : isCompleted ? "Reopen dispute" : "Mark dispute complete"}</button>{saved && <span className="studio-dispute-feedback" data-testid={`studio-dispute-saved-${dispute.dispute_id}`}>Saved</span>}{errorKey === dispute.dispute_id && <span className="studio-dispute-error" role="alert" data-testid={`studio-dispute-row-error-${dispute.dispute_id}`}>{errorMessage}</span>}</div>
            </article>
          );
        })}
      </div>}
    </section>
  );
}

function StudioShipmentScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const catalogOnly = location.pathname === "/studio/catalog";
  const [authState, setAuthState] = useState(null);
  const [paidOrders, setPaidOrders] = useState([]);
  const [paidOrdersTotal, setPaidOrdersTotal] = useState(0);
  const [paidOrdersPage, setPaidOrdersPage] = useState(1);
  const [paidOrdersHasMore, setPaidOrdersHasMore] = useState(false);
  const [paidOrdersLoading, setPaidOrdersLoading] = useState(true);
  const [paidOrdersLoadingMore, setPaidOrdersLoadingMore] = useState(false);
  const [paidOrdersError, setPaidOrdersError] = useState("");
  const [paidOrderPackingSavingKey, setPaidOrderPackingSavingKey] = useState("");
  const [paidOrderPackingSavedKey, setPaidOrderPackingSavedKey] = useState("");
  const [paidOrderPackingErrorKey, setPaidOrderPackingErrorKey] = useState("");
  const [paidOrderPackingError, setPaidOrderPackingError] = useState("");
  const [paidOrderSearchInput, setPaidOrderSearchInput] = useState("");
  const [paidOrderSearch, setPaidOrderSearch] = useState("");
  const [paidOrderCountryInput, setPaidOrderCountryInput] = useState("");
  const [paidOrderCountry, setPaidOrderCountry] = useState("");
  const [paidOrdersExporting, setPaidOrdersExporting] = useState(false);
  const [paidOrdersExportError, setPaidOrdersExportError] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(true);
  const [shipmentsLoadingMore, setShipmentsLoadingMore] = useState(false);
  const [shipmentsHasMore, setShipmentsHasMore] = useState(false);
  const [shipmentsPage, setShipmentsPage] = useState(1);
  const [shipmentsTotal, setShipmentsTotal] = useState(0);
  const [shipmentsError, setShipmentsError] = useState("");
  const [ledgerInsights, setLedgerInsights] = useState({ total: 0, by_carrier: {}, by_outreach_status: {} });
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [pendingSummary, setPendingSummary] = useState({ total: 0, shipments: [] });
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingError, setPendingError] = useState("");
  const [ledgerSearchInput, setLedgerSearchInput] = useState("");
  const [ledgerSearch, setLedgerSearch] = useState("");
  const [ledgerCarrier, setLedgerCarrier] = useState("");
  const [ledgerOutreachStatus, setLedgerOutreachStatus] = useState("");
  const [ledgerShippedFromInput, setLedgerShippedFromInput] = useState("");
  const [ledgerShippedToInput, setLedgerShippedToInput] = useState("");
  const [ledgerShippedFrom, setLedgerShippedFrom] = useState("");
  const [ledgerShippedTo, setLedgerShippedTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [outreachSavingKey, setOutreachSavingKey] = useState("");
  const [outreachSavedKey, setOutreachSavedKey] = useState("");
  const [outreachErrorKey, setOutreachErrorKey] = useState("");
  const [outreachError, setOutreachError] = useState("");
  const [noteSavingKey, setNoteSavingKey] = useState("");
  const [noteSavedKey, setNoteSavedKey] = useState("");
  const [noteErrorKey, setNoteErrorKey] = useState("");
  const [noteError, setNoteError] = useState("");
  const [deliverySavingKey, setDeliverySavingKey] = useState("");
  const [deliverySavedKey, setDeliverySavedKey] = useState("");
  const [deliveryErrorKey, setDeliveryErrorKey] = useState("");
  const [deliveryError, setDeliveryError] = useState("");
  const [deliveryEmailSavingKey, setDeliveryEmailSavingKey] = useState("");
  const [deliveryEmailSavedKey, setDeliveryEmailSavedKey] = useState("");
  const [deliveryEmailErrorKey, setDeliveryEmailErrorKey] = useState("");
  const [deliveryEmailError, setDeliveryEmailError] = useState("");
  const [trackingCopiedKey, setTrackingCopiedKey] = useState("");
  const [emailPreview, setEmailPreview] = useState(null);
  const [disputes, setDisputes] = useState([]);
  const [disputeReminders, setDisputeReminders] = useState([]);
  const [disputeRemindersLoading, setDisputeRemindersLoading] = useState(true);
  const [disputeRemindersError, setDisputeRemindersError] = useState("");
  const [disputeFilter, setDisputeFilter] = useState("open");
  const [disputeSearchInput, setDisputeSearchInput] = useState("");
  const [disputeSearch, setDisputeSearch] = useState("");
  const [disputesLoading, setDisputesLoading] = useState(true);
  const [disputesError, setDisputesError] = useState("");
  const [disputeSavingKey, setDisputeSavingKey] = useState("");
  const [disputeSavedKey, setDisputeSavedKey] = useState("");
  const [disputeErrorKey, setDisputeErrorKey] = useState("");
  const [disputeError, setDisputeError] = useState("");
  const [followUpSavingKey, setFollowUpSavingKey] = useState("");
  const [followUpSavedKey, setFollowUpSavedKey] = useState("");
  const [followUpErrorKey, setFollowUpErrorKey] = useState("");
  const [followUpError, setFollowUpError] = useState("");
  const [emailHistoryByOrder, setEmailHistoryByOrder] = useState({});
  const [emailHistoryFilter, setEmailHistoryFilter] = useState("all");
  const [emailHistoryOpenKey, setEmailHistoryOpenKey] = useState("");
  const [emailHistoryLoadingKey, setEmailHistoryLoadingKey] = useState("");
  const [emailHistoryErrorKey, setEmailHistoryErrorKey] = useState("");
  const [emailHistoryError, setEmailHistoryError] = useState("");
  const [orderReferenceLookup, setOrderReferenceLookup] = useState({ value: "", loading: false, message: "Enter the buyer email, then leave the field to load its receipt number.", error: "" });
  const [savedViews, setSavedViews] = useState([]);
  const [activeSavedViewId, setActiveSavedViewId] = useState("");
  const [savedViewsLoading, setSavedViewsLoading] = useState(true);
  const [savedViewsError, setSavedViewsError] = useState("");
  const [savedViewSaving, setSavedViewSaving] = useState(false);
  const [savedViewFeedback, setSavedViewFeedback] = useState("");
  const [savedViewSaveError, setSavedViewSaveError] = useState("");
  const [creatingSavedView, setCreatingSavedView] = useState(false);
  const [savedViewName, setSavedViewName] = useState("");
  const [pendingDeleteSavedViewId, setPendingDeleteSavedViewId] = useState("");
  const [fields, setFields] = useState({
    buyer_email: "",
    carrier: "",
    tracking_number: "",
    shipped_date: new Date().toISOString().slice(0, 10),
  });

  const loadPaidOrders = useCallback(async (page = 1, append = false) => {
    if (append) setPaidOrdersLoadingMore(true);
    else setPaidOrdersLoading(true);
    setPaidOrdersError("");
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "50" });
      if (paidOrderSearch) params.set("search", paidOrderSearch);
      if (paidOrderCountry) params.set("country", paidOrderCountry);
      const response = await fetch(`${API}/api/studio/orders?${params.toString()}`, { credentials: "include" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Paid orders could not be loaded");
      if (!Array.isArray(payload.orders) || !Number.isInteger(payload.page) || !Number.isInteger(payload.total) || typeof payload.has_more !== "boolean") throw new Error("Paid orders returned an invalid page");
      setPaidOrders((current) => append ? [...current, ...payload.orders] : payload.orders);
      setPaidOrdersPage(payload.page);
      setPaidOrdersTotal(payload.total);
      setPaidOrdersHasMore(payload.has_more);
    } catch (error) {
      setPaidOrdersError(error instanceof Error ? error.message : "Paid orders could not be loaded");
    } finally {
      if (append) setPaidOrdersLoadingMore(false);
      else setPaidOrdersLoading(false);
    }
  }, [paidOrderCountry, paidOrderSearch]);

  const exportPaidOrders = useCallback(async () => {
    setPaidOrdersExporting(true);
    setPaidOrdersExportError("");
    try {
      const params = new URLSearchParams();
      if (paidOrderSearch) params.set("search", paidOrderSearch);
      if (paidOrderCountry) params.set("country", paidOrderCountry);
      const response = await fetch(`${API}/api/studio/orders/export?${params.toString()}`, { credentials: "include" });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || "Packing CSV could not be downloaded");
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = "love-quetee-paid-orders-packing.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      setPaidOrdersExportError(error instanceof Error ? error.message : "Packing CSV could not be downloaded");
    } finally {
      setPaidOrdersExporting(false);
    }
  }, [paidOrderSearch, paidOrderCountry]);

  const applyPaidOrderSearch = useCallback(() => {
    const nextSearch = paidOrderSearchInput.trim();
    const nextCountry = paidOrderCountryInput;
    const unchanged = nextSearch === paidOrderSearch && nextCountry === paidOrderCountry;
    setPaidOrderSearch(nextSearch);
    setPaidOrderCountry(nextCountry);
    if (unchanged) loadPaidOrders();
  }, [loadPaidOrders, paidOrderCountry, paidOrderCountryInput, paidOrderSearch, paidOrderSearchInput]);

  const clearPaidOrderFilters = useCallback(() => {
    const unchanged = !paidOrderSearchInput && !paidOrderCountryInput && !paidOrderSearch && !paidOrderCountry;
    setPaidOrderSearchInput("");
    setPaidOrderCountryInput("");
    setPaidOrderSearch("");
    setPaidOrderCountry("");
    setPaidOrdersPage(1);
    if (unchanged) loadPaidOrders();
  }, [loadPaidOrders, paidOrderCountry, paidOrderCountryInput, paidOrderSearch, paidOrderSearchInput]);

  const loadOlderPaidOrders = useCallback(() => {
    if (!paidOrdersHasMore || paidOrdersLoadingMore) return;
    loadPaidOrders(paidOrdersPage + 1, true);
  }, [loadPaidOrders, paidOrdersHasMore, paidOrdersLoadingMore, paidOrdersPage]);

  const updatePaidOrderPacking = useCallback(async (order) => {
    const packingKey = order.session_id || order.order_reference;
    if (!packingKey) return;
    const nextReady = !order.packing_ready;
    setPaidOrderPackingSavingKey(packingKey);
    setPaidOrderPackingSavedKey("");
    setPaidOrderPackingErrorKey("");
    setPaidOrderPackingError("");
    try {
      const response = await fetch(`${API}/api/studio/orders/packing`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: order.session_id, packing_ready: nextReady }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Packing status could not be saved");
      setPaidOrders((current) => current.map((row) => (
        (row.session_id || row.order_reference) === packingKey
          ? { ...row, packing_ready: payload.packing_ready, packing_updated_at: payload.packing_updated_at }
          : row
      )));
      setPaidOrderPackingSavedKey(packingKey);
      window.setTimeout(() => setPaidOrderPackingSavedKey((current) => current === packingKey ? "" : current), 1800);
    } catch (error) {
      setPaidOrderPackingErrorKey(packingKey);
      setPaidOrderPackingError(error instanceof Error ? error.message : "Packing status could not be saved");
    } finally {
      setPaidOrderPackingSavingKey("");
    }
  }, []);

  const loadShipments = useCallback(async (page = 1, append = false) => {
    if (append) setShipmentsLoadingMore(true);
    else {
      setShipmentsLoading(true);
      setShipments([]);
      setShipmentsPage(1);
      setShipmentsTotal(0);
      setShipmentsHasMore(false);
      setLedgerInsights({ total: 0, by_carrier: {}, by_outreach_status: {} });
      setInsightsLoading(true);
    }
    setShipmentsError("");
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "20" });
      if (ledgerSearch) params.set("search", ledgerSearch);
      if (ledgerCarrier) params.set("carrier", ledgerCarrier);
      if (ledgerOutreachStatus) params.set("outreach_status", ledgerOutreachStatus);
      if (ledgerShippedFrom) params.set("shipped_from", ledgerShippedFrom);
      if (ledgerShippedTo) params.set("shipped_to", ledgerShippedTo);
      const response = await fetch(`${API}/api/studio/shipments?${params.toString()}`, { credentials: "include" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Shipment history could not be loaded");
      if (!Array.isArray(payload.shipments) || !Number.isInteger(payload.page) || !Number.isInteger(payload.total) || typeof payload.has_more !== "boolean" || !payload.insights || !Number.isInteger(payload.insights.total)) {
        throw new Error("Shipment history returned an invalid page");
      }
      setShipments((current) => append ? [...current, ...payload.shipments] : payload.shipments);
      setShipmentsPage(payload.page);
      setShipmentsTotal(payload.total);
      setShipmentsHasMore(payload.has_more);
      setLedgerInsights(payload.insights);
    } catch (error) {
      setShipmentsError(error instanceof Error ? error.message : "Shipment history could not be loaded");
    } finally {
      if (append) setShipmentsLoadingMore(false);
      else {
        setShipmentsLoading(false);
        setInsightsLoading(false);
      }
    }
  }, [ledgerSearch, ledgerCarrier, ledgerOutreachStatus, ledgerShippedFrom, ledgerShippedTo]);

  const loadPendingSummary = useCallback(async () => {
    setPendingLoading(true);
    setPendingError("");
    try {
      const response = await fetch(`${API}/api/studio/shipments?page=1&page_size=3&outreach_status=pending`, { credentials: "include" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Pending follow-up could not be loaded");
      if (!Array.isArray(payload.shipments) || !Number.isInteger(payload.total)) throw new Error("Pending follow-up returned an invalid queue");
      setPendingSummary({ total: payload.total, shipments: payload.shipments });
    } catch (error) {
      setPendingError(error instanceof Error ? error.message : "Pending follow-up could not be loaded");
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const exportShipments = useCallback(async () => {
    setExporting(true);
    setExportError("");
    try {
      const params = new URLSearchParams();
      if (ledgerSearch) params.set("search", ledgerSearch);
      if (ledgerCarrier) params.set("carrier", ledgerCarrier);
      if (ledgerOutreachStatus) params.set("outreach_status", ledgerOutreachStatus);
      if (ledgerShippedFrom) params.set("shipped_from", ledgerShippedFrom);
      if (ledgerShippedTo) params.set("shipped_to", ledgerShippedTo);
      const response = await fetch(`${API}/api/studio/shipments/export?${params.toString()}`, { credentials: "include" });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || "The dispatch record could not be exported");
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = "love-quetee-dispatch-history.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "The dispatch record could not be exported");
    } finally {
      setExporting(false);
    }
  }, [ledgerSearch, ledgerCarrier, ledgerOutreachStatus, ledgerShippedFrom, ledgerShippedTo]);

  const updateOutreachStatus = useCallback(async (shipment, nextStatus) => {
    const rowKey = `${shipment.buyer_email}:${shipment.order_reference}`;
    setOutreachSavingKey(rowKey);
    setOutreachSavedKey("");
    setOutreachErrorKey("");
    setOutreachError("");
    try {
      const response = await fetch(`${API}/api/studio/shipments/outreach`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          buyer_email: shipment.buyer_email,
          order_reference: shipment.order_reference,
          outreach_status: nextStatus,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "The outreach status could not be saved");
      setShipments((current) => current.map((row) => (
        row.buyer_email === shipment.buyer_email && row.order_reference === shipment.order_reference
          ? { ...row, outreach_status: payload.outreach_status, outreach_updated_at: payload.outreach_updated_at }
          : row
      )));
      setOutreachSavedKey(rowKey);
      window.setTimeout(() => setOutreachSavedKey((current) => current === rowKey ? "" : current), 1800);
      loadPendingSummary();
    } catch (error) {
      setOutreachErrorKey(rowKey);
      setOutreachError(error instanceof Error ? error.message : "The outreach status could not be saved");
    } finally {
      setOutreachSavingKey("");
    }
  }, [loadPendingSummary]);

  const updateDeliveryStatus = useCallback(async (shipment, nextStatus) => {
    const rowKey = `${shipment.buyer_email}:${shipment.order_reference}`;
    setDeliverySavingKey(rowKey);
    setDeliverySavedKey("");
    setDeliveryErrorKey("");
    setDeliveryError("");
    try {
      const response = await fetch(`${API}/api/studio/shipments/delivery-status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ buyer_email: shipment.buyer_email, order_reference: shipment.order_reference, delivery_status: nextStatus }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "The delivery status could not be saved");
      setShipments((current) => current.map((row) => (
        row.buyer_email === shipment.buyer_email && row.order_reference === shipment.order_reference
          ? { ...row, delivery_status: payload.delivery_status, delivered_at: payload.delivered_at, delivery_timeline: payload.delivery_timeline }
          : row
      )));
      setDeliverySavedKey(rowKey);
      window.setTimeout(() => setDeliverySavedKey((current) => current === rowKey ? "" : current), 1800);
    } catch (error) {
      setDeliveryErrorKey(rowKey);
      setDeliveryError(error instanceof Error ? error.message : "The delivery status could not be saved");
    } finally {
      setDeliverySavingKey("");
    }
  }, []);

  const sendDeliveryEmail = useCallback(async (shipment) => {
    const rowKey = `${shipment.buyer_email}:${shipment.order_reference}`;
    setDeliveryEmailSavingKey(rowKey);
    setDeliveryEmailSavedKey("");
    setDeliveryEmailErrorKey("");
    setDeliveryEmailError("");
    try {
      const response = await fetch(`${API}/api/studio/shipments/delivery-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ buyer_email: shipment.buyer_email, order_reference: shipment.order_reference, resend: Boolean(shipment.delivery_email_sent) }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "The delivered email could not be sent");
      if (payload.status === "already_sent") {
        setShipments((current) => current.map((row) => row.buyer_email === shipment.buyer_email && row.order_reference === shipment.order_reference ? { ...row, delivery_email_sent: true, delivery_email_id: payload.email_id } : row));
      } else {
        setShipments((current) => current.map((row) => row.buyer_email === shipment.buyer_email && row.order_reference === shipment.order_reference ? { ...row, delivery_email_sent: true, delivery_email_id: payload.email_id, delivery_email_sent_at: payload.delivery_email_sent_at } : row));
      }
      setDeliveryEmailSavedKey(rowKey);
      window.setTimeout(() => setDeliveryEmailSavedKey((current) => current === rowKey ? "" : current), 1800);
    } catch (error) {
      setDeliveryEmailErrorKey(rowKey);
      setDeliveryEmailError(error instanceof Error ? error.message : "The delivered email could not be sent");
    } finally {
      setDeliveryEmailSavingKey("");
    }
  }, []);

  const copyTracking = useCallback(async (shipment) => {
    const rowKey = `${shipment.buyer_email}:${shipment.order_reference}`;
    try {
      await copyToClipboard(shipment.tracking_number);
      setTrackingCopiedKey(rowKey);
      window.setTimeout(() => setTrackingCopiedKey((current) => current === rowKey ? "" : current), 1800);
    } catch {
      setDeliveryErrorKey(rowKey);
      setDeliveryError("Tracking number could not be copied");
    }
  }, []);

  const loadBuyerEmailPreview = useCallback(async (shipment, template) => {
    setEmailPreview({ shipment, template, subject: "", html: "", loading: true, error: "" });
    try {
      const response = await fetch(`${API}/api/studio/shipments/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ buyer_email: shipment.buyer_email, order_reference: shipment.order_reference, template }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Buyer email preview could not be rendered");
      setEmailPreview((current) => current && current.shipment.order_reference === shipment.order_reference ? { ...current, subject: payload.subject || "", html: payload.html || "", loading: false, error: "" } : current);
    } catch (error) {
      setEmailPreview((current) => current && current.shipment.order_reference === shipment.order_reference ? { ...current, loading: false, error: error instanceof Error ? error.message : "Buyer email preview could not be rendered" } : current);
    }
  }, []);

  const openBuyerEmailPreview = useCallback((shipment) => {
    loadBuyerEmailPreview(shipment, "tracking_update");
  }, [loadBuyerEmailPreview]);

  const changeBuyerEmailPreviewTemplate = useCallback((template) => {
    if (!emailPreview?.shipment) return;
    loadBuyerEmailPreview(emailPreview.shipment, template);
  }, [emailPreview, loadBuyerEmailPreview]);

  const updateOutreachNote = useCallback(async (shipment, noteDraft) => {
    const rowKey = `${shipment.buyer_email}:${shipment.order_reference}`;
    const note = noteDraft.trim();
    if (note === (shipment.outreach_note || "")) return;
    setNoteSavingKey(rowKey);
    setNoteSavedKey("");
    setNoteErrorKey("");
    setNoteError("");
    try {
      const response = await fetch(`${API}/api/studio/shipments/note`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          buyer_email: shipment.buyer_email,
          order_reference: shipment.order_reference,
          outreach_note: note,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "The outreach note could not be saved");
      setShipments((current) => current.map((row) => (
        row.buyer_email === shipment.buyer_email && row.order_reference === shipment.order_reference
          ? { ...row, outreach_note: payload.outreach_note, outreach_note_updated_at: payload.outreach_note_updated_at }
          : row
      )));
      setNoteSavedKey(rowKey);
      window.setTimeout(() => setNoteSavedKey((current) => current === rowKey ? "" : current), 1800);
    } catch (error) {
      setNoteErrorKey(rowKey);
      setNoteError(error instanceof Error ? error.message : "The outreach note could not be saved");
    } finally {
      setNoteSavingKey("");
    }
  }, []);

  const loadDisputes = useCallback(async () => {
    setDisputesLoading(true);
    setDisputesError("");
    try {
      const params = new URLSearchParams({ page: "1", page_size: "50", status: disputeFilter, search: disputeSearch });
      const response = await fetch(`${API}/api/studio/disputes?${params.toString()}`, { credentials: "include" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Disputes could not be loaded");
      if (!Array.isArray(payload.disputes)) throw new Error("Disputes returned an invalid list");
      setDisputes(payload.disputes);
    } catch (error) {
      setDisputesError(error instanceof Error ? error.message : "Disputes could not be loaded");
    } finally {
      setDisputesLoading(false);
    }
  }, [disputeFilter, disputeSearch]);

  const applyDisputeSearch = useCallback(() => {
    setDisputeSearch(disputeSearchInput.trim());
  }, [disputeSearchInput]);

  const clearDisputeSearch = useCallback(() => {
    setDisputeSearchInput("");
    setDisputeSearch("");
  }, []);

  const loadDisputeReminders = useCallback(async () => {
    setDisputeRemindersLoading(true);
    setDisputeRemindersError("");
    try {
      const response = await fetch(`${API}/api/studio/disputes/reminders`, { credentials: "include" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Follow-ups could not be loaded");
      setDisputeReminders(Array.isArray(payload.reminders) ? payload.reminders : []);
    } catch (error) {
      setDisputeRemindersError(error instanceof Error ? error.message : "Follow-ups could not be loaded");
    } finally {
      setDisputeRemindersLoading(false);
    }
  }, []);

  const updateDisputeStatus = useCallback(async (dispute) => {
    const nextStatus = dispute.status === "completed" ? "open" : "completed";
    setDisputeSavingKey(dispute.dispute_id);
    setDisputeSavedKey("");
    setDisputeErrorKey("");
    setDisputeError("");
    try {
      const response = await fetch(`${API}/api/studio/disputes/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ dispute_id: dispute.dispute_id, status: nextStatus }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Dispute status could not be saved");
      setDisputes((current) => current.map((row) => row.dispute_id === dispute.dispute_id ? payload.dispute : row));
      setDisputeSavedKey(dispute.dispute_id);
      window.setTimeout(() => setDisputeSavedKey((current) => current === dispute.dispute_id ? "" : current), 1800);
    } catch (error) {
      setDisputeErrorKey(dispute.dispute_id);
      setDisputeError(error instanceof Error ? error.message : "Dispute status could not be saved");
    } finally {
      setDisputeSavingKey("");
    }
  }, []);

  const updateDisputeFollowUp = useCallback(async (dispute, nextContactDate) => {
    setFollowUpSavingKey(dispute.dispute_id);
    setFollowUpSavedKey("");
    setFollowUpErrorKey("");
    setFollowUpError("");
    try {
      const response = await fetch(`${API}/api/studio/disputes/follow-up`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ dispute_id: dispute.dispute_id, next_contact_date: nextContactDate || null }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Next contact date could not be saved");
      setDisputes((current) => current.map((row) => row.dispute_id === dispute.dispute_id ? payload.dispute : row));
      loadDisputeReminders();
      setFollowUpSavedKey(dispute.dispute_id);
      window.setTimeout(() => setFollowUpSavedKey((current) => current === dispute.dispute_id ? "" : current), 1800);
    } catch (error) {
      setFollowUpErrorKey(dispute.dispute_id);
      setFollowUpError(error instanceof Error ? error.message : "Next contact date could not be saved");
    } finally {
      setFollowUpSavingKey("");
    }
  }, [loadDisputeReminders]);

  const loadEmailHistory = useCallback(async (shipment, filter) => {
    const orderReference = shipment.order_reference;
    const cacheKey = `${orderReference}:${filter}`;
    if (emailHistoryByOrder[cacheKey]) return;
    setEmailHistoryLoadingKey(cacheKey);
    setEmailHistoryErrorKey("");
    setEmailHistoryError("");
    try {
      const params = new URLSearchParams({ order_reference: orderReference, email_type: filter });
      const response = await fetch(`${API}/api/studio/email-history?${params.toString()}`, { credentials: "include" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Email history could not be loaded");
      setEmailHistoryByOrder((current) => ({ ...current, [cacheKey]: payload.history || [] }));
    } catch (error) {
      setEmailHistoryErrorKey(cacheKey);
      setEmailHistoryError(error instanceof Error ? error.message : "Email history could not be loaded");
    } finally {
      setEmailHistoryLoadingKey("");
    }
  }, [emailHistoryByOrder]);

  const toggleEmailHistory = useCallback((shipment) => {
    const orderReference = shipment.order_reference;
    if (emailHistoryOpenKey === orderReference) {
      setEmailHistoryOpenKey("");
      return;
    }
    setEmailHistoryOpenKey(orderReference);
    loadEmailHistory(shipment, emailHistoryFilter);
  }, [emailHistoryFilter, emailHistoryOpenKey, loadEmailHistory]);

  const changeEmailHistoryFilter = useCallback((shipment, filter) => {
    setEmailHistoryFilter(filter);
    if (emailHistoryOpenKey === shipment.order_reference) loadEmailHistory(shipment, filter);
  }, [emailHistoryOpenKey, loadEmailHistory]);

  const loadSavedViews = useCallback(async () => {
    setSavedViewsLoading(true);
    setSavedViewsError("");
    try {
      const response = await fetch(`${API}/api/studio/saved-views`, { credentials: "include" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Saved views could not be loaded");
      if (!Array.isArray(payload.saved_views)) throw new Error("Saved views returned an invalid list");
      setSavedViews(payload.saved_views);
    } catch (error) {
      setSavedViewsError(error instanceof Error ? error.message : "Saved views could not be loaded");
    } finally {
      setSavedViewsLoading(false);
    }
  }, []);

  const createSavedView = useCallback(async (name = savedViewName) => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setSavedViewSaveError("Give this view a name first");
      return;
    }
    setSavedViewSaving(true);
    setSavedViewSaveError("");
    try {
      const response = await fetch(`${API}/api/studio/saved-views`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: trimmedName, search: ledgerSearchInput.trim(), carrier: ledgerCarrier }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Saved view could not be created");
      if (!Array.isArray(payload.saved_views)) throw new Error("Saved views returned an invalid list");
      setSavedViews(payload.saved_views);
      setActiveSavedViewId(payload.saved_view?.id || "");
      setCreatingSavedView(false);
      setSavedViewName("");
      setSavedViewFeedback(`Saved “${payload.saved_view?.name || trimmedName}”`);
      window.setTimeout(() => setSavedViewFeedback(""), 1800);
    } catch (error) {
      setSavedViewSaveError(error instanceof Error ? error.message : "Saved view could not be created");
    } finally {
      setSavedViewSaving(false);
    }
  }, [ledgerCarrier, ledgerSearchInput, savedViewName]);

  const useSavedView = useCallback((view) => {
    if (!view) return;
    setActiveSavedViewId(view.id);
    const nextSearch = view.search || "";
    const nextCarrier = view.carrier || "";
    const unchanged = nextSearch === ledgerSearch && nextCarrier === ledgerCarrier;
    setLedgerSearchInput(nextSearch);
    setLedgerSearch(nextSearch);
    setLedgerCarrier(nextCarrier);
    if (unchanged) loadShipments(1, false);
  }, [ledgerCarrier, ledgerSearch, loadShipments]);

  const replaceSavedView = useCallback(async (view) => {
    if (!view) return;
    setSavedViewSaving(true);
    setSavedViewSaveError("");
    try {
      const response = await fetch(`${API}/api/studio/saved-views/${encodeURIComponent(view.id)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ search: ledgerSearchInput.trim(), carrier: ledgerCarrier }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Saved view could not be replaced");
      if (!Array.isArray(payload.saved_views)) throw new Error("Saved views returned an invalid list");
      setSavedViews(payload.saved_views);
      setActiveSavedViewId(payload.saved_view?.id || view.id);
      setSavedViewFeedback(`Replaced “${payload.saved_view?.name || view.name}”`);
      window.setTimeout(() => setSavedViewFeedback(""), 1800);
    } catch (error) {
      setSavedViewSaveError(error instanceof Error ? error.message : "Saved view could not be replaced");
    } finally {
      setSavedViewSaving(false);
    }
  }, [ledgerCarrier, ledgerSearchInput]);

  const requestDeleteSavedView = useCallback((viewId) => {
    setSavedViewSaveError("");
    setPendingDeleteSavedViewId(viewId);
  }, []);

  const cancelDeleteSavedView = useCallback(() => setPendingDeleteSavedViewId(""), []);

  const confirmDeleteSavedView = useCallback(async (viewId) => {
    setSavedViewSaving(true);
    setSavedViewSaveError("");
    try {
      const response = await fetch(`${API}/api/studio/saved-views/${encodeURIComponent(viewId)}`, { method: "DELETE", credentials: "include" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Saved view could not be deleted");
      if (!Array.isArray(payload.saved_views)) throw new Error("Saved views returned an invalid list");
      setSavedViews(payload.saved_views);
      if (viewId === activeSavedViewId) setActiveSavedViewId("");
      setPendingDeleteSavedViewId("");
      setSavedViewFeedback("Saved view deleted");
      window.setTimeout(() => setSavedViewFeedback(""), 1800);
    } catch (error) {
      setSavedViewSaveError(error instanceof Error ? error.message : "Saved view could not be deleted");
    } finally {
      setSavedViewSaving(false);
    }
  }, [activeSavedViewId]);

  const activeSavedView = savedViews.find((view) => view.id === activeSavedViewId);
  const savedViewFiltersDirty = Boolean(activeSavedView) && (activeSavedView.search !== ledgerSearchInput.trim() || activeSavedView.carrier !== ledgerCarrier);

  useEffect(() => {
    if (activeSavedViewId || !savedViews.length) return;
    const matchingView = savedViews.find((view) => view.search === ledgerSearchInput.trim() && view.carrier === ledgerCarrier);
    if (matchingView) setActiveSavedViewId(matchingView.id);
  }, [activeSavedViewId, ledgerCarrier, ledgerSearchInput, savedViews]);

  const isOwner = authState?.user?.is_owner;

  const applyLedgerSearch = useCallback(() => {
    const nextSearch = ledgerSearchInput.trim();
    const nextFrom = ledgerShippedFromInput;
    const nextTo = ledgerShippedToInput;
    const unchanged = nextSearch === ledgerSearch && nextFrom === ledgerShippedFrom && nextTo === ledgerShippedTo;
    setLedgerSearch(nextSearch);
    setLedgerShippedFrom(nextFrom);
    setLedgerShippedTo(nextTo);
    if (unchanged) loadShipments(1, false);
  }, [ledgerSearchInput, ledgerSearch, ledgerShippedFromInput, ledgerShippedToInput, ledgerShippedFrom, ledgerShippedTo, loadShipments]);

  const applyCarrierDrilldown = useCallback((carrier) => {
    setLedgerCarrier((current) => current === carrier ? "" : carrier);
  }, []);

  const applyOutreachDrilldown = useCallback((status) => {
    setLedgerOutreachStatus((current) => current === status ? "" : status);
  }, []);

  const applyDatePreset = useCallback((preset) => {
    const range = getDatePresetRange(preset);
    setLedgerShippedFromInput(range.from);
    setLedgerShippedToInput(range.to);
    setLedgerShippedFrom(range.from);
    setLedgerShippedTo(range.to);
  }, []);

  const reviewPending = useCallback(() => {
    setLedgerSearchInput("");
    setLedgerSearch("");
    setLedgerCarrier("");
    setLedgerOutreachStatus("pending");
    setLedgerShippedFromInput("");
    setLedgerShippedToInput("");
    setLedgerShippedFrom("");
    setLedgerShippedTo("");
  }, []);

  useEffect(() => {
    if (!isOwner) return undefined;
    const timer = window.setTimeout(() => {
      const nextSearch = ledgerSearchInput.trim();
      if (nextSearch !== ledgerSearch) setLedgerSearch(nextSearch);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [isOwner, ledgerSearchInput, ledgerSearch]);

  const loadOlderShipments = useCallback(() => {
    if (!shipmentsHasMore || shipmentsLoadingMore) return;
    loadShipments(shipmentsPage + 1, true);
  }, [loadShipments, shipmentsHasMore, shipmentsLoadingMore, shipmentsPage]);

  const retryShipments = useCallback(() => {
    const append = shipments.length > 0;
    loadShipments(append ? shipmentsPage + 1 : 1, append);
  }, [loadShipments, shipments.length, shipmentsPage]);

  useEffect(() => {
    let active = true;
    fetch(`${API}/api/auth/me`, { credentials: "include" })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (response.status === 401) return { user: null };
          throw new Error(payload.detail || "Studio access could not be checked");
        }
        return { user: payload };
      })
      .then((state) => {
        if (active) setAuthState(state);
      })
      .catch((error) => {
        if (active) setAuthState({ error: error instanceof Error ? error.message : "Studio access could not be checked" });
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isOwner) return undefined;
    loadPaidOrders();
    loadShipments();
    loadDisputes();
    loadDisputeReminders();
    loadSavedViews();
    loadPendingSummary();
    return undefined;
  }, [isOwner, loadDisputeReminders, loadDisputes, loadPaidOrders, loadShipments, loadSavedViews, loadPendingSummary]);

  const startGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/studio";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    setAuthState({ user: null });
    setResult(null);
    navigate("/studio", { replace: true });
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    setFields((current) => ({ ...current, [name]: value }));
    if (name === "buyer_email") {
      setOrderReferenceLookup({ value: "", loading: false, message: "Enter the buyer email, then leave the field to load its receipt number.", error: "" });
    }
  };

  const lookupOrderReference = async () => {
    const email = fields.buyer_email.trim();
    if (!email || !email.includes("@")) {
      setOrderReferenceLookup({ value: "", loading: false, message: "Enter a valid buyer email to load its receipt number.", error: "" });
      return;
    }
    setOrderReferenceLookup((current) => ({ ...current, loading: true, error: "", message: "Looking for the connected purchase…" }));
    try {
      const response = await fetch(`${API}/api/studio/order-reference?buyer_email=${encodeURIComponent(email)}`, { credentials: "include" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "The automatic receipt number could not be loaded");
      setOrderReferenceLookup({ value: payload.order_reference || "", loading: false, message: payload.message || "A receipt number will be generated when tracking is sent.", error: "" });
    } catch (error) {
      setOrderReferenceLookup((current) => ({ ...current, loading: false, error: error instanceof Error ? error.message : "The automatic receipt number could not be loaded", message: "The receipt number will still be generated when tracking is sent." }));
    }
  };

  const submitShipment = async (event) => {
    event.preventDefault();
    setSending(true);
    setResult(null);
    try {
      const response = await fetch(`${API}/api/studio/shipments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(fields),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "The tracking update could not be sent");
      setResult(payload);
      if (payload.order_reference) {
        setOrderReferenceLookup({ value: payload.order_reference, loading: false, message: "Connected to the customer's purchase receipt.", error: "" });
      }
      if (payload.status === "sent") {
        setFields((current) => ({ ...current, tracking_number: "" }));
      }
      if (payload.status === "sent" || payload.status === "already_sent") loadShipments();
    } catch (error) {
      setResult({ status: "error", message: error instanceof Error ? error.message : "The tracking update could not be sent" });
    } finally {
      setSending(false);
    }
  };

  if (authState === null) {
    return (
      <div className="studio-shell studio-loading" data-testid="studio-screen">
        <main className="studio-loading-card"><LoaderCircle className="checkout-spinner" size={22} /><span>Checking the private studio.</span></main>
      </div>
    );
  }

  if (authState.error) {
    return (
      <div className="studio-shell studio-access" data-testid="studio-screen">
        <main className="studio-access-card">
          <p className="section-kicker">Private studio / unavailable</p>
          <h1>The studio door<br /><em>is quiet.</em></h1>
          <p>{authState.error}</p>
          <a className="button button-text" href="/#home" data-testid="studio-return-home">Return to the public site <ArrowRight size={15} strokeWidth={1.4} /></a>
        </main>
      </div>
    );
  }

  if (!authState.user) {
    return (
      <div className="studio-shell studio-access" data-testid="studio-screen">
        <main className="studio-access-card">
          <p className="section-kicker">Private studio / owner access</p>
          <h1>Studio dispatch<br /><em>desk.</em></h1>
          <p>Sign in with the approved studio Google account to send a buyer their shipping update.</p>
          <button className="button button-primary" type="button" onClick={startGoogleAuth} data-testid="studio-google-login-button">
            Continue with Google <LogIn size={16} strokeWidth={1.4} />
          </button>
          <a className="button button-text" href="/#home" data-testid="studio-return-home">Return to the public site <ArrowRight size={15} strokeWidth={1.4} /></a>
        </main>
      </div>
    );
  }

  if (!authState.user.is_owner) {
    return (
      <div className="studio-shell studio-access" data-testid="studio-screen">
        <main className="studio-access-card">
          <p className="section-kicker">Private studio / owner access</p>
          <h1>This room is<br /><em>studio-only.</em></h1>
          <p data-testid="studio-owner-error">This Google account (<strong data-testid="studio-signed-in-email">{authState.user.email}</strong>) is signed in, but it is not on the Love Quetee studio-owner allowlist.</p>
          <button className="button button-text" type="button" onClick={logout} data-testid="studio-logout-button">Sign out <LogOut size={15} strokeWidth={1.4} /></button>
        </main>
      </div>
    );
  }

  if (catalogOnly) {
    return (
      <div className="studio-shell studio-dashboard" data-testid="studio-catalog-page">
        <header className="studio-header">
          <a className="brand-lockup" href="/#home" data-testid="studio-catalog-brand-link"><span className="brand-mark">LQ</span><span className="brand-name">Love Quetee</span></a>
          <div className="studio-header-actions">
            <Link className="studio-catalog-link" to="/studio" data-testid="studio-catalog-back-link">Dispatch desk <ArrowRight size={14} strokeWidth={1.4} /></Link>
            <span className="studio-owner-email" data-testid="studio-catalog-owner-email">{authState.user.email}</span>
            <button className="studio-logout" type="button" onClick={logout} data-testid="studio-catalog-logout-button">Sign out <LogOut size={14} strokeWidth={1.4} /></button>
          </div>
        </header>
        <main className="studio-main studio-catalog-page-main">
          <StudioCatalog />
        </main>
      </div>
    );
  }

  return (
    <div className="studio-shell studio-dashboard" data-testid="studio-screen">
      <header className="studio-header">
        <a className="brand-lockup" href="/#home" data-testid="studio-brand-link"><span className="brand-mark">LQ</span><span className="brand-name">Love Quetee</span></a>
        <div className="studio-header-actions">
          <span className="studio-owner-email" data-testid="studio-owner-email">{authState.user.email}</span>
          <a className="studio-catalog-link" href="/studio/catalog" data-testid="studio-catalog-link">Private catalog <ArrowRight size={14} strokeWidth={1.4} /></a>
          <button className="studio-logout" type="button" onClick={logout} data-testid="studio-logout-button">Sign out <LogOut size={14} strokeWidth={1.4} /></button>
        </div>
      </header>
      <main className="studio-main">
        <section className="studio-intro">
          <p className="section-kicker">Private studio / dispatch desk</p>
          <h1>Send the work<br /><em>on its way.</em></h1>
          <p>After a package leaves the studio, enter its carrier and tracking number once. The buyer receives a quiet, considered update in the same visual language as the rest of the studio.</p>
        </section>
        <StudioPaidOrders
          orders={paidOrders}
          total={paidOrdersTotal}
          loading={paidOrdersLoading}
          error={paidOrdersError}
          onRetry={() => loadPaidOrders()}
          searchInput={paidOrderSearchInput}
          countryInput={paidOrderCountryInput}
          onSearchChange={setPaidOrderSearchInput}
          onCountryChange={setPaidOrderCountryInput}
          onSearch={applyPaidOrderSearch}
          onClear={clearPaidOrderFilters}
          onExport={exportPaidOrders}
          exporting={paidOrdersExporting}
          exportError={paidOrdersExportError}
          hasMore={paidOrdersHasMore}
          loadingMore={paidOrdersLoadingMore}
          onLoadMore={loadOlderPaidOrders}
          onPackingToggle={updatePaidOrderPacking}
          packingSavingKey={paidOrderPackingSavingKey}
          packingSavedKey={paidOrderPackingSavedKey}
          packingErrorKey={paidOrderPackingErrorKey}
          packingError={paidOrderPackingError}
        />
        <section className="studio-shipment-panel" data-testid="studio-shipment-panel">
          <div className="studio-panel-heading"><div><p className="section-kicker">Shipment update / manual send</p><h2>Tracking details</h2></div><Truck size={25} strokeWidth={1.2} /></div>
          <form className="studio-shipment-form" onSubmit={submitShipment} data-testid="shipment-notification-form">
            <label>Buyer email<input name="buyer_email" type="email" value={fields.buyer_email} onChange={updateField} onBlur={lookupOrderReference} placeholder="buyer@example.com" required data-testid="shipment-buyer-email-input" /></label>
            <div className="studio-order-reference-readonly" data-testid="shipment-order-reference-display">
              <span className="studio-ledger-label">Automatic order / receipt number</span>
              <strong data-testid="shipment-order-reference-value">{orderReferenceLookup.loading ? "Looking up…" : orderReferenceLookup.value || "Generated automatically"}</strong>
              {orderReferenceLookup.error ? <small className="studio-order-reference-error" role="alert" data-testid="shipment-order-reference-error">{orderReferenceLookup.error}</small> : <small data-testid="shipment-order-reference-message">{orderReferenceLookup.message}</small>}
            </div>
            <label>Carrier<select name="carrier" value={fields.carrier} onChange={updateField} required data-testid="shipment-carrier-select"><option value="">Select carrier</option><option>USPS</option><option>UPS</option><option>FedEx</option><option>DHL</option><option>Other</option></select></label>
            <label>Tracking number<input name="tracking_number" type="text" value={fields.tracking_number} onChange={updateField} placeholder="Tracking number" required data-testid="shipment-tracking-number-input" /></label>
            <label>Shipped date<input name="shipped_date" type="date" value={fields.shipped_date} onChange={updateField} required data-testid="shipment-shipped-date-input" /><CalendarDays size={15} strokeWidth={1.3} /></label>
            <button className="button button-primary studio-submit" type="submit" disabled={sending} aria-busy={sending} data-testid="shipment-send-button">{sending ? "Sending tracking note…" : "Send tracking update"} <Send size={15} strokeWidth={1.4} /></button>
          </form>
          {result?.status === "sent" && <div className="studio-result studio-result-success" data-testid="shipment-success-message"><CheckCircle2 size={18} /><span>Tracking update sent to {fields.buyer_email || "the buyer"}.<small>Order / receipt number / {result.order_reference}</small><small>Email reference / {result.email_id}</small></span></div>}
          {result?.status === "already_sent" && <div className="studio-result" data-testid="shipment-duplicate-message"><CheckCircle2 size={18} /><span>This tracking update was already sent for order / receipt {result.order_reference}.</span></div>}
          {result?.status === "error" && <div className="studio-result studio-result-error" role="alert" data-testid="shipment-error-message">{result.message}</div>}
          <ShipmentLedger shipments={shipments} total={shipmentsTotal} loading={shipmentsLoading} loadingMore={shipmentsLoadingMore} hasMore={shipmentsHasMore} error={shipmentsError} onRetry={retryShipments} onLoadMore={loadOlderShipments} searchInput={ledgerSearchInput} carrier={ledgerCarrier} outreachStatus={ledgerOutreachStatus} shippedFromInput={ledgerShippedFromInput} shippedToInput={ledgerShippedToInput} onSearchChange={setLedgerSearchInput} onCarrierChange={setLedgerCarrier} onOutreachStatusChange={setLedgerOutreachStatus} onShippedFromChange={setLedgerShippedFromInput} onShippedToChange={setLedgerShippedToInput} onDatePreset={applyDatePreset} onSearch={applyLedgerSearch} onExport={exportShipments} exporting={exporting} exportError={exportError} onOutreachChange={updateOutreachStatus} outreachSavingKey={outreachSavingKey} outreachSavedKey={outreachSavedKey} outreachErrorKey={outreachErrorKey} outreachError={outreachError} onNoteBlur={updateOutreachNote} noteSavingKey={noteSavingKey} noteSavedKey={noteSavedKey} noteErrorKey={noteErrorKey} noteError={noteError} onDeliveryStatusChange={updateDeliveryStatus} deliverySavingKey={deliverySavingKey} deliverySavedKey={deliverySavedKey} deliveryErrorKey={deliveryErrorKey} deliveryError={deliveryError} onDeliveryEmail={sendDeliveryEmail} deliveryEmailSavingKey={deliveryEmailSavingKey} deliveryEmailSavedKey={deliveryEmailSavedKey} deliveryEmailErrorKey={deliveryEmailErrorKey} deliveryEmailError={deliveryEmailError} onCopyTracking={copyTracking} trackingCopiedKey={trackingCopiedKey} onPreviewEmail={openBuyerEmailPreview} onEmailHistoryToggle={toggleEmailHistory} onEmailHistoryFilterChange={changeEmailHistoryFilter} emailHistoryByOrder={emailHistoryByOrder} emailHistoryFilter={emailHistoryFilter} emailHistoryOpenKey={emailHistoryOpenKey} emailHistoryLoadingKey={emailHistoryLoadingKey} emailHistoryErrorKey={emailHistoryErrorKey} emailHistoryError={emailHistoryError} insights={ledgerInsights} insightsLoading={insightsLoading} activeCarrier={ledgerCarrier} activeOutreachStatus={ledgerOutreachStatus} onCarrierDrilldown={applyCarrierDrilldown} onOutreachDrilldown={applyOutreachDrilldown} pendingSummary={pendingSummary} pendingLoading={pendingLoading} pendingError={pendingError} onRetryPending={loadPendingSummary} onReviewPending={reviewPending} savedViews={savedViews} savedViewsLoading={savedViewsLoading} savedViewsError={savedViewsError} onRetrySavedViews={loadSavedViews} activeSavedViewId={activeSavedViewId} onUseSavedView={useSavedView} onReplaceSavedView={replaceSavedView} onRequestDeleteSavedView={requestDeleteSavedView} onConfirmDeleteSavedView={confirmDeleteSavedView} onCancelDeleteSavedView={cancelDeleteSavedView} pendingDeleteSavedViewId={pendingDeleteSavedViewId} creatingSavedView={creatingSavedView} onStartCreateSavedView={() => { setCreatingSavedView(true); setSavedViewName(""); setSavedViewSaveError(""); }} savedViewName={savedViewName} onSavedViewNameChange={setSavedViewName} onCreateSavedView={createSavedView} onCancelCreateSavedView={() => { setCreatingSavedView(false); setSavedViewName(""); setSavedViewSaveError(""); }} savedViewSaving={savedViewSaving} savedViewFeedback={savedViewFeedback} savedViewSaveError={savedViewSaveError} savedViewFiltersDirty={savedViewFiltersDirty} />
        </section>
        <StudioDisputeReminders reminders={disputeReminders} loading={disputeRemindersLoading} error={disputeRemindersError} onRetry={loadDisputeReminders} />
        <StudioDisputes disputes={disputes} loading={disputesLoading} error={disputesError} onRetry={loadDisputes} onStatusChange={updateDisputeStatus} savingKey={disputeSavingKey} savedKey={disputeSavedKey} errorKey={disputeErrorKey} errorMessage={disputeError} filterStatus={disputeFilter} onFilterChange={setDisputeFilter} searchInput={disputeSearchInput} onSearchChange={setDisputeSearchInput} onSearch={applyDisputeSearch} onClear={clearDisputeSearch} onFollowUpChange={updateDisputeFollowUp} followUpSavingKey={followUpSavingKey} followUpSavedKey={followUpSavedKey} followUpErrorKey={followUpErrorKey} followUpError={followUpError} />
        <StudioContactLeads api={API} />
      </main>
      {emailPreview && <BuyerEmailPreviewModal shipment={emailPreview.shipment} template={emailPreview.template} subject={emailPreview.subject} html={emailPreview.html} loading={emailPreview.loading} error={emailPreview.error} onTemplateChange={changeBuyerEmailPreviewTemplate} onClose={() => setEmailPreview(null)} />}
    </div>
  );
}

function DisputePage() {
  const initialOrderReference = new URLSearchParams(window.location.search).get("order") || "";
  const [fields, setFields] = useState({ order_reference: initialOrderReference, name: "", email: "", reason: "", delivery_status: "delivered" });
  const [state, setState] = useState({ submitting: false, error: "", success: false, pending: false });
  const updateField = (event) => setFields((current) => ({ ...current, [event.target.name]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    setState({ submitting: true, error: "", success: false, pending: false });
    try {
      const response = await fetch(`${API}/api/disputes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Your dispute could not be started");
      const buyerEmailSent = payload.dispute?.buyer_ack_email_sent === true && payload.status !== "email_pending";
      setState({ submitting: false, error: "", success: buyerEmailSent, pending: !buyerEmailSent });
    } catch (error) {
      setState({ submitting: false, error: error instanceof Error ? error.message : "Your dispute could not be started", success: false, pending: false });
    }
  };
  return (
    <div className="dispute-page" data-testid="dispute-page">
      <header className="tracking-page-header"><a className="brand-lockup" href="/" data-testid="dispute-page-brand-link"><span className="brand-mark">LQ</span><span className="brand-name">Love Quetee</span></a></header>
      <main className="dispute-page-main">
        <h1 data-testid="dispute-page-title">Let’s make it<br /><em>right.</em></h1>
        <p className="dispute-page-intro">Tell me what happened with your order. Your dispute will go straight to the studio, and I will reach out soon.</p>
        {state.success ? (
          <div className="dispute-success" role="status" data-testid="dispute-success-message"><CheckCircle2 size={20} /><div><strong>Your dispute has started.</strong><p>I received your details and will reach out about it soon.</p></div></div>
        ) : (
          <>
            {state.pending && <div className="dispute-pending" role="status" data-testid="dispute-pending-message"><strong>Your dispute was received.</strong><p>The buyer acknowledgement email is still pending. Try again to send it.</p><button type="button" onClick={() => setState((current) => ({ ...current, pending: false, error: "" }))} data-testid="dispute-retry-email-button">Try email again</button></div>}
            <form className="dispute-form" onSubmit={submit} data-testid="dispute-form">
            <label>Order / receipt number<input name="order_reference" value={fields.order_reference} onChange={updateField} placeholder="LQ-2026-0001" required data-testid="dispute-order-input" /></label>
            <label>Your name<input name="name" value={fields.name} onChange={updateField} placeholder="Your name" required data-testid="dispute-name-input" /></label>
            <label>Your email<input name="email" type="email" value={fields.email} onChange={updateField} placeholder="you@example.com" required data-testid="dispute-email-input" /></label>
            <label>Was the order delivered?<select name="delivery_status" value={fields.delivery_status} onChange={updateField} required data-testid="dispute-delivery-status-select"><option value="delivered">Yes, it was delivered</option><option value="not_delivered">No, it was not delivered</option></select></label>
            <label className="dispute-form-wide">What happened?<textarea name="reason" value={fields.reason} onChange={updateField} placeholder="Share the details so I can help." rows={6} required data-testid="dispute-reason-input" /></label>
            <button className="button button-primary dispute-submit" type="submit" disabled={state.submitting} aria-busy={state.submitting} data-testid="dispute-submit-button">{state.submitting ? "Starting dispute…" : "Start dispute"} <Send size={15} strokeWidth={1.4} /></button>
            {state.error && <div className="dispute-form-error" role="alert" data-testid="dispute-form-error">{state.error}</div>}
            </form>
          </>
        )}
      </main>
    </div>
  );
}

function TrackPackagePage() {
  const params = new URLSearchParams(window.location.search);
  const carrier = params.get("carrier") || "Carrier";
  const trackingNumber = params.get("tracking") || "";
  const orderReference = params.get("order") || "Order reference unavailable";
  const trackingUrl = getCarrierTrackingUrl(carrier, trackingNumber);
  return (
    <div className="tracking-page" data-testid="tracking-page">
      <header className="tracking-page-header"><a className="brand-lockup" href="/" data-testid="tracking-page-brand-link"><span className="brand-mark">LQ</span><span className="brand-name">Love Quetee</span></a></header>
      <main className="tracking-page-main">
        <p className="section-kicker">Package tracking / Love Quetee</p>
        <h1 data-testid="tracking-page-title">A quiet arrival<br /><em>is on its way.</em></h1>
        <p className="tracking-page-intro">Your delivery details are ready below. Carrier updates continue on the carrier’s own tracking page.</p>
        <section className="tracking-page-card" data-testid="tracking-page-card">
          <span className="studio-ledger-label">Order / receipt number</span>
          <strong data-testid="tracking-page-order">{orderReference}</strong>
          <span className="studio-ledger-label">Carrier</span>
          <strong data-testid="tracking-page-carrier">{carrier}</strong>
          <span className="studio-ledger-label">Tracking number</span>
          <strong data-testid="tracking-page-number">{trackingNumber || "Not provided"}</strong>
          {trackingUrl ? <a className="button button-primary" href={trackingUrl} target="_blank" rel="noreferrer" data-testid="tracking-page-open-carrier">Open carrier tracking <ExternalLink size={15} strokeWidth={1.4} /></a> : <span className="tracking-page-unavailable" data-testid="tracking-page-link-unavailable">A carrier tracking link is not available for this carrier.</span>}
        </section>
        <a className="button button-text" href="/" data-testid="tracking-page-return-home">Return to Love Quetee <ArrowRight size={15} strokeWidth={1.4} /></a>
      </main>
    </div>
  );
}

function PaymentStatusPage({ success }) {
  const [paymentStatus, setPaymentStatus] = useState(success ? "checking" : "cancelled");
  const [orderReference, setOrderReference] = useState("");
  const [copyFeedback, setCopyFeedback] = useState("");
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  useEffect(() => {
    if (!success || !sessionId) {
      if (success) setPaymentStatus("missing");
      return undefined;
    }
    let cancelled = false;
    let timerId;
    const poll = async () => {
      try {
        const response = await fetch(`${API}/api/payments/status/${encodeURIComponent(sessionId)}`, {
          credentials: "include",
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.detail || "Unable to confirm payment");
        if (cancelled) return false;
        setPaymentStatus(payload.payment_status);
        setOrderReference(payload.order_reference || "");
        if (payload.payment_status === "paid") window.localStorage.removeItem(CART_STORAGE_KEY);
        const terminal = ["paid", "failed", "expired", "cancelled"].includes(payload.payment_status);
        if (terminal) window.clearInterval(timerId);
        return terminal;
      } catch {
        if (!cancelled) setPaymentStatus("unavailable");
        return false;
      }
    };
    const startPolling = async () => {
      const terminal = await poll();
      if (!cancelled && !terminal) timerId = window.setInterval(poll, 2500);
    };
    startPolling();
    return () => {
      cancelled = true;
      window.clearInterval(timerId);
    };
  }, [success, sessionId]);

  const copyOrderReference = async () => {
    try {
      await copyToClipboard(orderReference);
      setCopyFeedback("copied");
      window.setTimeout(() => setCopyFeedback(""), 1800);
    } catch {
      setCopyFeedback("unavailable");
    }
  };

  const paid = paymentStatus === "paid";
  const title = !success ? "Checkout cancelled." : paid ? "Print edition confirmed." : "Confirming your print dispatch.";
  const message = !success
    ? "No payment was taken. Your studio archive is still here whenever you are ready."
    : paid
      ? "Your payment is complete. The studio will prepare your archival print and follow up with the dispatch details."
      : "Your secure checkout returned to the studio. We are checking the payment status now.";

  return (
    <div className="site-shell payment-result-shell">
      <header className="site-header">
        <div className="site-header-inner">
          <a className="brand-lockup" href="/" data-testid="payment-brand-link">
            <span className="brand-mark">LQ</span>
            <span className="brand-name">Love Quetee</span>
          </a>
          <Link className="button button-text payment-back-link" to="/works" data-testid="payment-back-to-works">Back to works <ArrowRight size={15} strokeWidth={1.3} /></Link>
        </div>
      </header>
      <main>
        <section className="payment-result" data-testid="payment-result-page">
          <p className="section-kicker">Print acquisition dispatch / {success ? "return" : "pause"}</p>
          <div className="payment-result-mark" aria-hidden="true">{paid ? <CheckCircle2 size={38} strokeWidth={1} /> : <ShoppingBag size={38} strokeWidth={1} />}</div>
          <h1>{title}</h1>
          <p data-testid="payment-result-status">{message}</p>
          {success && <span className="payment-status-code">Status / {paymentStatus}</span>}
          {paid && orderReference && <div className="payment-order-reference" data-testid="payment-order-reference"><span>Order / receipt number</span><div className="payment-order-reference-row"><strong data-testid="payment-order-reference-value">{orderReference}</strong><button className="payment-order-reference-copy" type="button" onClick={copyOrderReference} aria-label="Copy order number" data-testid="payment-order-reference-copy-button"><Clipboard size={14} strokeWidth={1.3} /> {copyFeedback === "copied" ? "Copied" : "Copy order number"}</button></div><small data-testid="payment-order-reference-message">Keep this number with your purchase receipt and future tracking updates.</small>{copyFeedback === "unavailable" && <small className="payment-order-reference-copy-error" role="alert" data-testid="payment-order-reference-copy-error">Copy is unavailable in this browser. Select the number to copy it manually.</small>}</div>}
          <Link className="button button-primary" to="/works" data-testid="payment-result-works-link">Return to the print archive <ArrowRight size={16} strokeWidth={1.4} /></Link>
        </section>
      </main>
    </div>
  );
}

function CatalogueModal({ onClose }) {
  const [ownerView, setOwnerView] = useState(false);

  useEffect(() => {
    let active = true;
    fetch(`${API}/api/auth/me`, { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) return null;
        return response.json();
      })
      .then((user) => {
        if (active) setOwnerView(user?.is_owner === true && user?.email === STUDIO_EMAIL);
      })
      .catch(() => {
        if (active) setOwnerView(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <ModalShell testId="catalogue-download-modal" onClose={onClose} wide surfaceClassName="catalogue-modal-surface">
      <div className="modal-topline">
        <span>Descendants of the Sun / 2026</span>
        <button className="modal-close" type="button" onClick={onClose} data-testid="catalogue-modal-close-button">
          Close <X size={17} strokeWidth={1.3} />
        </button>
      </div>
      <div className="catalogue-grid catalogue-descendants-grid">
        <div className="catalogue-cover">
          <span className="catalogue-cover-small">Love Quetee</span>
          <span className="catalogue-cover-title">Descendants<br /><em>of the Sun</em></span>
          <span className="catalogue-cover-meta">Catalogue / 2026</span>
        </div>
        <div className="catalogue-copy">
          <p className="section-kicker">Private catalog / 2026</p>
          <h2>Descendants of<br /><em>the Sun.</em></h2>
          {!ownerView && <p className="catalogue-availability-note" data-testid="catalogue-unavailable-note">Catalog currently unavailable.</p>}
          <div className="catalogue-list" data-testid="catalogue-title-list">
            {descendantsCatalogWorks.map((work, index) => (
              <span key={work.id} data-testid={`catalogue-title-${work.id}`}>
                <b>{String(index + 1).padStart(2, "0")}</b>
                <strong>{work.title}</strong>
                {ownerView && <small>{work.dimensions} / {work.medium} / {work.year} / {work.status}</small>}
              </span>
            ))}
          </div>
          {ownerView && (
            <div className="catalogue-owner-view" data-testid="catalogue-owner-view">
              <p>Owner view / full catalog details</p>
              <div className="catalogue-owner-work-grid">
                {descendantsCatalogWorks.map((work) => (
                  <article key={work.id} data-testid={`catalogue-owner-work-${work.id}`}>
                    <img src={work.imageUrl} alt={work.imageAlt} loading="lazy" />
                    <span>{work.title}</span>
                  </article>
                ))}
              </div>
              <Link className="button button-text" to="/studio" onClick={onClose} data-testid="catalogue-owner-studio-link">
                Open Private Studio <ArrowRight size={15} strokeWidth={1.3} />
              </Link>
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [detailWork, setDetailWork] = useState(null);
  const [inquiryWork, setInquiryWork] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartCheckoutLoading, setCartCheckoutLoading] = useState(false);
  const [catalogueOpen, setCatalogueOpen] = useState(false);
  const [shortlistOpen, setShortlistOpen] = useState(false);
  const [inquiryWorks, setInquiryWorks] = useState([]);
  const { shortlistIds, toggleItem, clearAll: clearShortlist, isShortlisted } = useCollectorShortlist();
  const { cartItems, addItem, updateQuantity, removeItem, clearCart, quantityFor } = usePrintCart();
  const cartLines = useMemo(
    () => cartItems.map((item) => {
      const work = works.find((entry) => entry.id === item.workId);
      const edition = work ? getPrintEdition(work.id) : null;
      return work && edition ? { ...item, work, edition, lineTotal: edition.price * item.quantity } : null;
    }).filter(Boolean),
    [cartItems],
  );
  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);
  const cartSubtotal = cartLines.reduce((total, line) => total + line.lineTotal, 0);
  const shortlistWorks = useMemo(
    () => shortlistIds.map((id) => archiveWorks.find((work) => work.id === id)).filter(Boolean),
    [shortlistIds],
  );
  const detailWorkIndex = detailWork ? artworkNavigation.findIndex((work) => work.id === detailWork.id) : -1;
  const previousDetailWork = detailWorkIndex >= 0 ? artworkNavigation[(detailWorkIndex - 1 + artworkNavigation.length) % artworkNavigation.length] : null;
  const nextDetailWork = detailWorkIndex >= 0 ? artworkNavigation[(detailWorkIndex + 1) % artworkNavigation.length] : null;


  useEffect(() => {
    document.body.classList.add("is-ready");
    const lenis = window.matchMedia("(min-width: 641px)").matches
      ? new Lenis({ duration: 1.15, smoothWheel: true })
      : null;
    pageLenis = lenis;
    let frameId = 0;
    if (lenis) {
      const raf = (time) => {
        lenis.raf(time);
        frameId = window.requestAnimationFrame(raf);
      };
      frameId = window.requestAnimationFrame(raf);
    }
    return () => {
      document.body.classList.remove("is-ready");
      if (frameId) window.cancelAnimationFrame(frameId);
      if (pageLenis === lenis) pageLenis = null;
      lenis?.destroy();
    };
  }, []);

  useEffect(() => {
    if (location.pathname !== "/works") return;
    const artworkId = new URLSearchParams(location.search).get("artwork");
    if (!artworkId) return;
    const sharedWork = archiveWorks.find((work) => work.id === artworkId);
    if (sharedWork) setDetailWork(sharedWork);
  }, [location.pathname, location.search]);

  const openInquiry = (work = null, selectedWorks = []) => {
    setDetailWork(null);
    setInquiryWork(work || selectedWorks[0] || null);
    setInquiryWorks(selectedWorks.length ? selectedWorks : work ? [work] : []);
  };

  const openCart = () => setCartOpen(true);

  const closeInquiry = () => {
    setInquiryWork(null);
    setInquiryWorks([]);
  };

  const toggleCollectorShortlist = (work) => {
    const wasShortlisted = isShortlisted(work.id);
    toggleItem(work.id);
    toast.success(wasShortlisted ? "Removed from shortlist" : "Added to shortlist", {
      description: wasShortlisted ? work.title : `${work.title} is saved for another looking.`,
    });
  };

  const removeShortlistWork = (work) => {
    if (work) toggleCollectorShortlist(work);
    else clearShortlist();
  };

  const clearCollectorShortlist = () => {
    clearShortlist();
    toast.success("Shortlist cleared", { description: "Saved works have been removed from this browser." });
  };

  const openShortlistInquiry = (selectedWorks) => {
    if (!selectedWorks.length) return;
    setShortlistOpen(false);
    openInquiry(selectedWorks[0], selectedWorks);
  };

  const shareExhibition = async (exhibition) => {
    const title = `${exhibition.title}${exhibition.subtitle ? ` ${exhibition.subtitle}` : ""}`;
    const url = getExhibitionShareUrl(exhibition.slug);
    const shareDetails = {
      title: `${title} — Love Quetee`,
      text: `Explore ${title} at ${exhibition.venue}.`,
      url,
    };
    if (typeof navigator.share === "function") {
      try {
        await navigator.share(shareDetails);
        toast.success("Exhibition shared", { description: title });
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }
    try {
      await copyToClipboard(url);
      toast.success("Exhibition link copied", { description: "A direct exhibition page is ready to share." });
    } catch {
      toast.error("Exhibition link could not be copied", { description: "Please copy the page address from your browser." });
    }
  };

  const shareArtwork = async (work) => {
    const url = getArtworkShareUrl(work.id);
    const shareDetails = {
      title: `${work.title} — Love Quetee`,
      text: `Explore ${work.title} by Love Quetee.`,
      url,
    };
    if (typeof navigator.share === "function") {
      try {
        await navigator.share(shareDetails);
        toast.success("Artwork shared", { description: work.title });
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }
    try {
      await copyToClipboard(url);
      toast.success("Artwork link copied", { description: "A direct detail link is ready to share." });
    } catch {
      toast.error("Artwork link could not be copied", { description: "Please copy the page address from your browser." });
    }
  };

  const addPrintToCart = (work) => {
    const edition = getPrintEdition(work.id);
    if (!edition) return;
    addItem(work.id);
    toast.success("Added to cart", {
      description: `${work.title} / ${edition.dimensions} / ${edition.priceLabel}`,
    });
  };

  const startCartCheckout = async () => {
    if (!cartLines.length) return;
    setCartCheckoutLoading(true);
    try {
      const response = await fetch(`${API}/api/checkout/cart`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          items: cartLines.map((line) => ({ lookup_key: line.edition.lookupKey, quantity: line.quantity })),
          origin_url: window.location.origin,
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.detail || "Unable to start checkout");
      if (!payload.checkout_url) throw new Error("Stripe did not return a checkout URL");
      window.location.assign(payload.checkout_url);
    } catch (error) {
      setCartCheckoutLoading(false);
      toast.error("Checkout could not be opened", {
        description: error instanceof Error ? error.message : "Please try again shortly.",
      });
    }
  };

  const cart = {
    lines: cartLines,
    count: cartCount,
    subtotal: cartSubtotal,
    quantityFor,
    addItem: addPrintToCart,
    updateQuantity,
    removeItem,
    clearCart,
    onCartOpen: openCart,
    onBrowsePrints: () => navigate("/works"),
    onCheckout: startCartCheckout,
    checkoutLoading: cartCheckoutLoading,
  };

  const paymentPath = location.pathname;
  if (paymentPath === "/studio" || paymentPath === "/studio/catalog") {
    return <StudioShipmentScreen />;
  }
  if (paymentPath === "/track") {
    return <TrackPackagePage />;
  }
  if (paymentPath === "/dispute") {
    return <DisputePage />;
  }
  const isPaymentResult = paymentPath === "/payment/success" || paymentPath === "/payment/cancel";

  if (isPaymentResult) {
    return <PaymentStatusPage success={paymentPath === "/payment/success"} />;
  }

  const exhibitionSlug = paymentPath.startsWith("/exhibitions/") ? paymentPath.slice("/exhibitions/".length) : "";
  const exhibitionDetail = EXHIBITIONS.find((exhibition) => exhibition.slug === exhibitionSlug);
  const page = paymentPath === "/"
    ? <HomePage onCatalogue={() => setCatalogueOpen(true)} />
    : paymentPath === "/search"
      ? <SearchResultsPage searchItems={globalSearchItems} />
      : paymentPath === "/works"
      ? <Works onOpen={setDetailWork} onInquire={openInquiry} cart={cart} onAddToCart={addPrintToCart} shortlistWorks={shortlistWorks} onOpenShortlist={() => setShortlistOpen(true)} onToggleShortlist={toggleCollectorShortlist} onShare={shareArtwork} isShortlisted={isShortlisted} />
      : paymentPath === "/about"
        ? <About />
          : paymentPath === "/news-events"
            ? <NewsEventsPage />
            : paymentPath === "/exhibitions"
              ? <ExhibitionsNews />
              : exhibitionDetail
              ? <ExhibitionDetailPage exhibition={exhibitionDetail} onShare={shareExhibition} />
              : paymentPath === "/contact"
                ? <Contact onCatalogue={() => setCatalogueOpen(true)} />
                : null;

  if (!page) return <Navigate to="/" replace />;

  return (
    <div className="site-shell">
      <Header mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} onCatalogue={() => setCatalogueOpen(true)} cartCount={cart.count} onCartOpen={openCart} shortlistCount={shortlistWorks.length} onShortlistOpen={() => setShortlistOpen(true)} searchItems={globalSearchItems} />
      <main>{page}</main>
      <Footer />
      <Toaster position="bottom-right" theme="light" toastOptions={{ className: "salon-toast" }} />
      {detailWork && <ArtworkModal work={detailWork} previousWork={previousDetailWork} nextWork={nextDetailWork} onPreviousWork={() => setDetailWork(previousDetailWork)} onNextWork={() => setDetailWork(nextDetailWork)} onClose={() => setDetailWork(null)} onInquire={openInquiry} onAddToCart={addPrintToCart} isShortlisted={isShortlisted(detailWork.id)} onToggleShortlist={toggleCollectorShortlist} onShare={shareArtwork} />}
      {inquiryWork && <InquiryModal work={inquiryWork} selectedWorks={inquiryWorks} onClose={closeInquiry} />}
      {cartOpen && <CartDrawer cart={cart} onClose={() => setCartOpen(false)} />}
      {shortlistOpen && <CollectorShortlistDrawer shortlistWorks={shortlistWorks} onClose={() => setShortlistOpen(false)} onOpen={setDetailWork} onRemove={removeShortlistWork} onClear={clearCollectorShortlist} onInquire={openShortlistInquiry} />}
      {catalogueOpen && <CatalogueModal onClose={() => setCatalogueOpen(false)} />}
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  const sessionId = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
  return (
    <>
      <SiteMetadata />
      <ScrollToTop />
      {sessionId ? <GoogleAuthCallback sessionId={sessionId} /> : <App />}
    </>
  );
}

export default AppRouter;
